import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import { useBonsai } from "../contexts/BonsaiContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { ComposePanel, SignatureSettings } from "./MailCompose";
import { SharePointBrowser } from "../components/SharePointBrowser";
import {
  Mail, Inbox, Send, FileText, Trash2, Archive, Star,
  ChevronRight, ChevronDown, Paperclip, Search,
  FolderOpen, Folder, RefreshCw, Loader2,
  FolderUp, Sparkles, AlertCircle, ChevronLeft,
  Plus, Reply, ReplyAll, Forward,
  Image as ImageIcon, PanelRight, PanelBottom,
  LayoutList, Columns3, Sun, Moon, Pin, X,
  Flag, MailOpen, MessageSquare, Users, ArrowLeft,
  CheckCircle2, ExternalLink, Clock, Repeat, Timer,
  ListTodo, CalendarPlus, AlertTriangle,
  Eye, EyeOff, FolderInput, MessageCircle,
  StickyNote, BookOpen, Zap, Info, CornerDownLeft
} from "lucide-react";

import { API } from "../config/api";
import SaveMatterNoteModal from "../components/SaveMatterNoteModal";

// ── Theme helpers ────────────────────────────────────────────────
function getStoredTheme() { return localStorage.getItem("bonsai-mail-theme") || "dark"; }
function setStoredTheme(t) { localStorage.setItem("bonsai-mail-theme", t); }

const DARK = {
  bg: "bg-navy", bgCard: "bg-navy", bgPanel: "bg-navy", bgList: "bg-navy",
  bgPreview: "bg-navy", bgInput: "bg-navy", bgHover: "hover:bg-navy-hover",
  bgActive: "bg-teal-dim/25", bgFiling: "bg-navy",
  border: "border-navy-border", borderSub: "border-navy-border",
  text: "text-white", textSec: "text-slate-300", textMuted: "text-slate-400",
  textDim: "text-slate-500", textAccent: "text-teal", textAccentDim: "text-teal",
  ribbon: "bg-navy", ribbonHover: "hover:bg-navy-hover",
  unreadDot: "bg-teal", unreadBg: "bg-navy",
  msgSelected: "bg-teal-dim/15", dropTarget: "bg-teal-dim/30 ring-2 ring-teal/50",
  previewBodyBg: "#0d1424", previewBodyText: "#d1d5db",
  badgeBg: "bg-teal-dim/50", badgeText: "text-teal",
};
const LIGHT = {
  bg: "bg-[#F5F5F5]", bgCard: "bg-white", bgPanel: "bg-[#FAFAFA]", bgList: "bg-white",
  bgPreview: "bg-white", bgInput: "bg-[#F0F0F0]", bgHover: "hover:bg-[#E8E8E8]",
  bgActive: "bg-blue-50", bgFiling: "bg-[#FAFAFA]",
  border: "border-[#E0E0E0]", borderSub: "border-[#EEEEEE]",
  text: "text-gray-900", textSec: "text-gray-700", textMuted: "text-gray-500",
  textDim: "text-gray-400", textAccent: "text-blue-600", textAccentDim: "text-blue-500",
  ribbon: "bg-white", ribbonHover: "hover:bg-gray-100",
  unreadDot: "bg-blue-500", unreadBg: "bg-blue-50/50",
  msgSelected: "bg-blue-50", dropTarget: "bg-blue-100 ring-2 ring-blue-400/50",
  previewBodyBg: "#ffffff", previewBodyText: "#1a1a1a",
  badgeBg: "bg-blue-100", badgeText: "text-blue-600",
};

// ── General helpers ──────────────────────────────────────────────
const FOLDER_ICONS = { inbox: Inbox, "sent items": Send, drafts: FileText, "deleted items": Trash2, archive: Archive, junk: AlertCircle };
function getFolderIcon(name) { const I = FOLDER_ICONS[name.toLowerCase()] || FolderOpen; return <I className="w-4 h-4 flex-shrink-0" />; }
function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso), now = new Date(), diff = now - d;
  if (diff < 86400000 && d.getDate() === now.getDate()) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diff < 604800000) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
}
function fmtSize(b) { return b < 1024 ? b + " B" : b < 1048576 ? (b / 1024).toFixed(0) + " KB" : (b / 1048576).toFixed(1) + " MB"; }
function getPinnedFolders() { try { return JSON.parse(localStorage.getItem("bonsai-pinned-folders") || "[]"); } catch { return []; } }
function setPinnedFolders(arr) { localStorage.setItem("bonsai-pinned-folders", JSON.stringify(arr)); }

// ── Inbox intelligence: status badge computation ─────────────────
const MATTER_REF_RE = /BL\.\d{2}\.\d{3}\.\d{4}/;
const USER_EMAIL = "vanessa@bonsai.law";

function computeEmailStatus(m, filedEmailIds) {
  const badges = [];
  // Filed ✓
  if (m.filed_to_sp || filedEmailIds?.has(m.id)) badges.push("filed");
  // Matter # — check subject
  if (m.subject && MATTER_REF_RE.test(m.subject)) badges.push("matter");
  // Info only — vanessa@ is CC only, not in To
  const toAddrs = (m.to_recipients || m.toRecipients || []).map(r => (r.emailAddress?.address || r.address || r.email || r).toLowerCase());
  const isInTo = toAddrs.some(a => a.includes(USER_EMAIL));
  const ccAddrs = (m.cc_recipients || m.ccRecipients || []).map(r => (r.emailAddress?.address || r.address || r.email || r).toLowerCase());
  const isInCc = ccAddrs.some(a => a.includes(USER_EMAIL));
  if (!isInTo && isInCc) {
    badges.push("info_only");
  } else {
    // Reply needed — vanessa is in To and last message not from her
    const fromEmail = (m.from_email || m.from?.emailAddress?.address || "").toLowerCase();
    if (isInTo && fromEmail && !fromEmail.includes(USER_EMAIL)) {
      badges.push("reply_needed");
      // Overdue — >5 days and reply needed
      if (m.received) {
        const daysSince = (Date.now() - new Date(m.received).getTime()) / 86400000;
        if (daysSince > 5) badges.push("overdue");
      }
    }
  }
  return badges;
}

function StatusBadges({ badges, compact, t }) {
  if (!badges || badges.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {badges.includes("filed") && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-teal-dim/40 text-teal text-[9px] font-medium" title="Filed to SharePoint">
          <CheckCircle2 className="w-2.5 h-2.5" />
          {!compact && <span>Filed</span>}
        </span>
      )}
      {badges.includes("matter") && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-blue-900/40 text-blue-400 text-[9px] font-medium" title="Has matter reference">
          <span className="text-[10px] font-bold">#</span>
          {!compact && <span>Matter</span>}
        </span>
      )}
      {badges.includes("reply_needed") && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-status-amber-dim/40 text-status-amber text-[9px] font-medium" title="Reply needed">
          <CornerDownLeft className="w-2.5 h-2.5" />
          {!compact && <span>Reply</span>}
        </span>
      )}
      {badges.includes("overdue") && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-status-red-dim/40 text-status-red text-[9px] font-medium" title="Overdue — no reply in 5+ days">
          <Clock className="w-2.5 h-2.5" />
          {!compact && <span>Overdue</span>}
        </span>
      )}
      {badges.includes("info_only") && (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-gray-700/40 text-gray-400 text-[9px] font-medium" title="Info only — CC'd">
          <Info className="w-2.5 h-2.5" />
          {!compact && <span>Info</span>}
        </span>
      )}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════
// RIBBON
// ═══════════════════════════════════════════════════════════════════
function RibbonBtn({ icon: Icon, label, onClick, disabled, primary, accent, active, danger, testId, t }) {
  const base = "flex flex-col items-center gap-0.5 px-2.5 py-1.5 rounded text-[10px] font-medium transition-all min-w-[52px]";
  const variant = disabled ? `${t.textDim} cursor-not-allowed opacity-50`
    : active ? `${t.bgActive} ${t.textAccent} ring-1 ring-teal/60`
    : primary ? "bg-teal text-white hover:bg-teal shadow-sm"
    : accent ? "text-status-amber hover:bg-status-amber-dim/20"
    : danger ? "text-status-red hover:bg-status-red-dim/20"
    : `${t.textSec} ${t.ribbonHover}`;
  return (
    <button onClick={onClick} disabled={disabled} className={`${base} ${variant}`} data-testid={testId}>
      <Icon className="w-4 h-4" />
      <span className="leading-tight">{label}</span>
    </button>
  );
}
function RibbonGroup({ label, children, t }) {
  return (
    <div className="flex flex-col">
      <div className="flex items-end gap-0.5 px-1">{children}</div>
      <span className={`text-[9px] ${t.textDim} text-center mt-0.5 tracking-wide`}>{label}</span>
    </div>
  );
}
function RibbonSep({ t }) { return <div className={`w-px h-10 ${t.border} mx-1.5 self-center`} />; }

function RibbonBar({
  activeTab, onTabChange, onNewEmail, onReply, onReplyAll, onForward,
  onDelete, onArchive, onSignature, readingPane, onReadingPaneChange,
  hasSelection, hasEmail, showFiling, onToggleFiling, theme, onToggleTheme, t,
  searchQuery, onSearchChange, onSearchSubmit, searching, onCreateTask,
  onAiAssist, aiAssistLoading, onAIRules, onAiChat, aiChatActive
}) {
  const [localSearch, setLocalSearch] = useState(searchQuery || "");
  const inputRef = useRef(null);

  const handleKey = (e) => {
    if (e.key === "Enter" && localSearch.trim()) { onSearchSubmit(localSearch.trim()); }
    if (e.key === "Escape") { setLocalSearch(""); onSearchChange(""); }
  };
  const clearSearch = () => { setLocalSearch(""); onSearchChange(""); inputRef.current?.focus(); };

  return (
    <div className={`border-b ${t.border} ${t.ribbon} flex-shrink-0`} data-testid="ribbon-bar">
      <div className={`flex items-center border-b ${t.border}`}>
        <a href="/dashboard"
          className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold ${t.textAccentDim} ${t.ribbonHover} border-r ${t.border} transition-colors`}
          data-testid="caseminder-hub-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> CaseMinder Hub
        </a>
        <div className="flex">
          {["Home", "View", "Assist"].map(tab => (
            <button key={tab} onClick={() => onTabChange(tab)}
              className={`px-5 py-1.5 text-xs font-semibold tracking-wide transition-colors relative
                ${activeTab === tab
                  ? `${tab === "Assist" ? "text-purple-400" : t.textAccent} after:absolute after:bottom-0 after:left-2 after:right-2 after:h-[2px] after:bg-current after:rounded-full`
                  : `${t.textMuted} ${t.ribbonHover}`}`}
              data-testid={`ribbon-tab-${tab.toLowerCase()}`}>
              {tab}
            </button>
          ))}
        </div>
        {/* Global search bar */}
        <div className="flex-1 flex justify-center px-4">
          <div className="relative w-full max-w-md">
            <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${searching ? "animate-pulse text-status-amber" : t.textDim}`} />
            <input ref={inputRef} value={localSearch}
              onChange={e => { setLocalSearch(e.target.value); if (!e.target.value) onSearchChange(""); }}
              onKeyDown={handleKey}
              placeholder="Search all emails... (Enter to search)"
              className={`w-full pl-8 pr-8 py-1 text-xs rounded ${t.bgInput} ${t.border} border ${t.textSec} placeholder:${t.textDim} focus:outline-none focus:ring-1 focus:ring-teal/50`}
              data-testid="global-search-input" />
            {localSearch && (
              <button onClick={clearSearch} className={`absolute right-2 top-1/2 -translate-y-1/2 ${t.textDim} hover:${t.text}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        <div className="pr-3 flex items-center gap-1">
          <button onClick={onToggleTheme}
            className={`flex items-center gap-1.5 px-2 py-1 text-[10px] ${t.textMuted} rounded transition-colors ${t.ribbonHover}`}
            data-testid="theme-toggle-btn" title={theme === "dark" ? "Switch to light" : "Switch to dark"}>
            {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </button>
          <button onClick={onSignature}
            className={`flex items-center gap-1.5 px-3 py-1 text-[10px] ${t.textMuted} rounded transition-colors ${t.ribbonHover}`}
            data-testid="manage-signature-btn">
            <ImageIcon className="w-3 h-3" /> Signature
          </button>
        </div>
      </div>
      <div className="px-2 py-1">
        {activeTab === "Home" && (
          <div className="flex items-end gap-0.5">
            <RibbonGroup label="New" t={t}><RibbonBtn icon={Plus} label="New Email" primary onClick={onNewEmail} testId="new-email-btn" t={t} /></RibbonGroup>
            <RibbonSep t={t} />
            <RibbonGroup label="Respond" t={t}>
              <RibbonBtn icon={Reply} label="Reply" onClick={onReply} disabled={!hasEmail} testId="reply-btn" t={t} />
              <RibbonBtn icon={ReplyAll} label="Reply All" onClick={onReplyAll} disabled={!hasEmail} testId="reply-all-btn" t={t} />
              <RibbonBtn icon={Forward} label="Forward" onClick={onForward} disabled={!hasEmail} testId="forward-btn" t={t} />
            </RibbonGroup>
            <RibbonSep t={t} />
            <RibbonGroup label="Actions" t={t}>
              <RibbonBtn icon={Trash2} label="Delete" onClick={onDelete} disabled={!hasSelection} danger testId="delete-btn" t={t} />
              <RibbonBtn icon={Archive} label="Archive" onClick={onArchive} disabled={!hasSelection} testId="archive-btn" t={t} />
              <RibbonBtn icon={ListTodo} label="To Do" onClick={onCreateTask} disabled={!hasEmail} testId="create-todo-btn" t={t} />
            </RibbonGroup>
            <RibbonSep t={t} />
            <RibbonGroup label="File" t={t}>
              <RibbonBtn icon={FolderUp} label="File to SP" onClick={onToggleFiling} active={showFiling} testId="toggle-filing-btn" t={t} />
              <RibbonBtn icon={Sparkles} label="AI Rules" onClick={onAIRules} accent testId="ai-rules-btn" t={t} />
            </RibbonGroup>
          </div>
        )}
        {activeTab === "View" && (
          <div className="flex items-end gap-0.5">
            <RibbonGroup label="Layout" t={t}>
              <RibbonBtn icon={Columns3} label="Compact" onClick={() => onReadingPaneChange("right")} active={readingPane === "right"} testId="pane-right-btn" t={t} />
              <RibbonBtn icon={PanelBottom} label="Wide" onClick={() => onReadingPaneChange("bottom")} active={readingPane === "bottom"} testId="pane-bottom-btn" t={t} />
              <RibbonBtn icon={LayoutList} label="List Only" onClick={() => onReadingPaneChange("off")} active={readingPane === "off"} testId="pane-off-btn" t={t} />
            </RibbonGroup>
            <RibbonSep t={t} />
            <RibbonGroup label="Panels" t={t}>
              <RibbonBtn icon={PanelRight} label="Filing" onClick={onToggleFiling} active={showFiling} testId="toggle-filing-view-btn" t={t} />
            </RibbonGroup>
          </div>
        )}
        {activeTab === "Assist" && (
          <div className="flex items-end gap-0.5">
            <RibbonGroup label="Email Analysis" t={t}>
              <RibbonBtn icon={Sparkles} label="Summarise & Extract" onClick={onAiAssist} disabled={!hasEmail || aiAssistLoading} accent testId="ai-assist-btn" t={t} />
            </RibbonGroup>
            <RibbonSep t={t} />
            <RibbonGroup label="CaseMinder AI" t={t}>
              <RibbonBtn icon={MessageSquare} label="AI Chat" onClick={onAiChat} active={aiChatActive} accent testId="bonsai-chat-btn" t={t} />
            </RibbonGroup>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// AI ASSIST PANEL — email summarisation & task/deadline extraction
// ═══════════════════════════════════════════════════════════════════
function AiAssistPanel({ data, loading, error, onRun, hasEmail, emailSubject, onCreateTodo, onCreateCalendarEvent, onFileConversation, conversationId, checkedCount, onFileChecked, filingInProgress, t, getHeaders, checkedIds, onOpenFileDialog, selectedEmail, selectedMatter }) {
  const [actionStatus, setActionStatus] = useState({});
  // Contract review state
  const [reviewLoading, setReviewLoading] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState(new Set());
  const [reviewResult, setReviewResult] = useState(null);
  // Save as matter note modal
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteModalData, setNoteModalData] = useState(null);
  // Matter notes for current matter
  const [matterNotes, setMatterNotes] = useState([]);
  const [matterNotesLoading, setMatterNotesLoading] = useState(false);

  // Load matter notes when matter changes
  const currentMatterRef = selectedMatter?.reference || "";
  useEffect(() => {
    if (!currentMatterRef) { setMatterNotes([]); return; }
    let cancelled = false;
    const fetchNotes = async () => {
      setMatterNotesLoading(true);
      try {
        const res = await axios.get(`${API}/hub/matter-notes`, { params: { matter_reference: currentMatterRef }, headers: getHeaders() });
        if (!cancelled) setMatterNotes(res.data?.notes || []);
      } catch { if (!cancelled) setMatterNotes([]); }
      finally { if (!cancelled) setMatterNotesLoading(false); }
    };
    fetchNotes();
    return () => { cancelled = true; };
  }, [currentMatterRef, getHeaders]);

  const handleAction = async (type, item, key) => {
    if (type === "todo") {
      // Opens the modal dialog — no loading state needed
      onCreateTodo(item.description, item.due_date || item.date, item.priority || "normal");
      return;
    }
    setActionStatus(prev => ({ ...prev, [key]: "loading" }));
    try {
      await onCreateCalendarEvent(item.description, item.due_date || item.date, item.time);
      setActionStatus(prev => ({ ...prev, [key]: "done" }));
    } catch {
      setActionStatus(prev => ({ ...prev, [key]: "error" }));
    }
  };

  if (!hasEmail) {
    return (
      <div className={`p-6 text-center ${t.textMuted} text-sm`} data-testid="ai-assist-panel-empty">
        <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-30" />
        <p>Select an email to see AI analysis.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`p-6 text-center ${t.textMuted} text-sm`} data-testid="ai-assist-panel-loading">
        <Loader2 className="w-6 h-6 mx-auto mb-3 animate-spin text-purple-400" />
        <p>Analysing email...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 m-4 rounded-lg bg-status-red/10 border border-red-500/20" data-testid="ai-assist-panel-error">
        <p className="text-sm text-status-red font-medium mb-1">AI service temporarily unavailable</p>
        <p className="text-xs text-status-red/70">{error}</p>
        <button onClick={onRun} className="mt-3 text-xs text-purple-400 hover:text-purple-300 underline" data-testid="ai-assist-retry-btn">
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`p-6 text-center ${t.textMuted} text-sm`} data-testid="ai-assist-panel-ready">
        {checkedCount > 0 && (
          <div className="p-3 rounded-lg bg-teal-dim/20 border border-teal/40 text-left mb-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-teal" />
              <span className="text-xs font-medium text-teal">{checkedCount} email{checkedCount !== 1 ? 's' : ''} selected</span>
            </div>
            <button onClick={onFileChecked} disabled={filingInProgress}
              className="w-full text-xs py-2 bg-teal hover:bg-teal disabled:bg-teal-dim/50 disabled:text-teal/50 text-white rounded font-medium flex items-center justify-center gap-1.5 transition-colors"
              data-testid="assist-file-checked-ready-btn">
              {filingInProgress ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderUp className="w-3.5 h-3.5" />}
              File {checkedCount} email{checkedCount !== 1 ? 's' : ''} to matter
            </button>
          </div>
        )}
        <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-40 text-purple-400" />
        <p className="mb-3">Click <strong>Summarise & Extract</strong> to analyse this email.</p>
        <button onClick={onRun}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-xs font-semibold rounded-md transition-colors"
          data-testid="ai-assist-run-btn">
          <Sparkles className="w-3.5 h-3.5 inline mr-1.5" /> Summarise & Extract
        </button>
      </div>
    );
  }

  const actionLabels = { reply_needed: "Reply needed", forward_to_team: "Forward to team", archive: "Archive", file_to_sharepoint: "File to SharePoint", delete: "Delete", none: "No action" };
  const tasks = data.tasks || [];
  const deadlines = data.deadlines || [];

  const ActionButtons = ({ item, keyPrefix }) => {
    const todoKey = `${keyPrefix}-todo`;
    const calKey = `${keyPrefix}-cal`;
    return (
      <div className="flex items-center gap-1 mt-1.5">
        <button onClick={() => handleAction("todo", item, todoKey)}
          disabled={actionStatus[todoKey] === "loading" || actionStatus[todoKey] === "done"}
          className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-colors ${
            actionStatus[todoKey] === "done" ? "bg-status-green/15 text-status-green" :
            actionStatus[todoKey] === "error" ? "bg-status-red/15 text-status-red" :
            "bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"}`}
          data-testid={`${keyPrefix}-add-todo`}>
          {actionStatus[todoKey] === "loading" ? <Loader2 className="w-3 h-3 animate-spin" /> :
           actionStatus[todoKey] === "done" ? <CheckCircle2 className="w-3 h-3" /> :
           <ListTodo className="w-3 h-3" />}
          To Do
        </button>
        <button onClick={() => handleAction("calendar", item, calKey)}
          disabled={actionStatus[calKey] === "loading" || actionStatus[calKey] === "done"}
          className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-colors ${
            actionStatus[calKey] === "done" ? "bg-status-green/15 text-status-green" :
            actionStatus[calKey] === "error" ? "bg-status-red/15 text-status-red" :
            "bg-purple-500/10 text-purple-400 hover:bg-purple-500/20"}`}
          data-testid={`${keyPrefix}-add-calendar`}>
          {actionStatus[calKey] === "loading" ? <Loader2 className="w-3 h-3 animate-spin" /> :
           actionStatus[calKey] === "done" ? <CheckCircle2 className="w-3 h-3" /> :
           <CalendarPlus className="w-3 h-3" />}
          Calendar
        </button>
      </div>
    );
  };

  return (
    <div className="p-4 space-y-4 overflow-y-auto max-h-full" data-testid="ai-assist-panel-results">
      {/* Batch actions for checked emails */}
      {checkedCount > 0 && (
        <div className="p-3 rounded-lg bg-teal-dim/20 border border-teal/40">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-teal" />
            <span className="text-xs font-medium text-teal">{checkedCount} email{checkedCount !== 1 ? 's' : ''} selected</span>
          </div>
          <button
            onClick={onFileChecked}
            disabled={filingInProgress}
            className="w-full text-xs py-2 bg-teal hover:bg-teal disabled:bg-teal-dim/50 disabled:text-teal/50 text-white rounded font-medium flex items-center justify-center gap-1.5 transition-colors"
            data-testid="assist-file-checked-btn"
          >
            {filingInProgress ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderUp className="w-3.5 h-3.5" />}
            File {checkedCount} email{checkedCount !== 1 ? 's' : ''} to matter
          </button>
        </div>
      )}

      {/* AI Availability Warning */}
      {!data.ai_available && (
        <div className="p-3 rounded-lg bg-status-amber/10 border border-amber-500/20" data-testid="ai-assist-unavailable-banner">
          <p className="text-xs text-status-amber font-medium">AI service unavailable — showing fallback analysis</p>
        </div>
      )}

      {/* Summary */}
      <div className={`p-3 rounded-lg ${t.bgPanel} border ${t.border}`} data-testid="ai-assist-summary">
        <h4 className="text-[10px] font-bold uppercase tracking-widest text-purple-400 mb-2">Summary</h4>
        <p className={`text-sm ${t.textSec}`}>{data.summary}</p>
      </div>

      {/* Category & Suggested Action */}
      <div className="flex gap-2">
        <div className={`flex-1 p-3 rounded-lg ${t.bgPanel} border ${t.border}`} data-testid="ai-assist-category">
          <h4 className="text-[10px] font-bold uppercase tracking-widest text-purple-400 mb-1">Category</h4>
          <span className={`text-sm font-medium ${t.text}`}>{data.category}</span>
        </div>
        <div className={`flex-1 p-3 rounded-lg ${t.bgPanel} border ${t.border}`} data-testid="ai-assist-action">
          <h4 className="text-[10px] font-bold uppercase tracking-widest text-purple-400 mb-1">Suggested Action</h4>
          <span className={`text-sm font-medium ${t.text}`}>{actionLabels[data.suggested_action] || data.suggested_action}</span>
        </div>
      </div>

      {/* Tasks */}
      {tasks.length > 0 && (
        <div className={`p-3 rounded-lg ${t.bgPanel} border ${t.border}`} data-testid="ai-assist-tasks">
          <h4 className="text-[10px] font-bold uppercase tracking-widest text-purple-400 mb-2">Extracted Tasks ({tasks.length})</h4>
          <div className="space-y-2">
            {tasks.map((task, i) => (
              <div key={i} className={`p-2 rounded border ${t.borderSub} ${t.bgCard}`} data-testid={`ai-assist-task-${i}`}>
                <p className={`text-sm ${t.text}`}>{task.description}</p>
                <div className="flex items-center gap-3 mt-1">
                  {task.due_date && <span className="text-xs text-status-amber flex items-center gap-1"><Clock className="w-3 h-3" />{task.due_date}</span>}
                  {task.priority && <span className={`text-xs px-1.5 py-0.5 rounded ${task.priority === 'high' ? 'bg-status-red/15 text-status-red' : task.priority === 'medium' ? 'bg-status-amber/15 text-status-amber' : 'bg-slate-500/15 text-slate-400'}`}>{task.priority}</span>}
                </div>
                <ActionButtons item={task} keyPrefix={`task-${i}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Deadlines */}
      {deadlines.length > 0 && (
        <div className={`p-3 rounded-lg ${t.bgPanel} border ${t.border}`} data-testid="ai-assist-deadlines">
          <h4 className="text-[10px] font-bold uppercase tracking-widest text-status-red mb-2">Detected Deadlines ({deadlines.length})</h4>
          <div className="space-y-2">
            {deadlines.map((dl, i) => (
              <div key={i} className={`p-2 rounded border border-red-500/20 bg-status-red/5`} data-testid={`ai-assist-deadline-${i}`}>
                <p className={`text-sm ${t.text}`}>{dl.description}</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs text-status-red flex items-center gap-1"><Clock className="w-3 h-3" />{dl.date}{dl.time ? ` ${dl.time}` : ''}</span>
                  {dl.confidence && <span className="text-xs text-slate-400">({Math.round(dl.confidence * 100)}% confidence)</span>}
                </div>
                {dl.source_text && <p className={`text-xs ${t.textDim} mt-1 italic`}>"{dl.source_text}"</p>}
                <ActionButtons item={dl} keyPrefix={`deadline-${i}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {tasks.length === 0 && deadlines.length === 0 && data.ai_available && (
        <div className={`p-3 rounded-lg ${t.bgPanel} border ${t.border} text-center`}>
          <p className={`text-xs ${t.textMuted}`}>No tasks or deadlines detected in this email.</p>
        </div>
      )}

      {/* File entire conversation */}
      {conversationId && onFileConversation && (
        <div className={`p-3 rounded-lg ${t.bgPanel} border ${t.border}`}>
          <p className={`text-xs ${t.textMuted} mb-2`}>Would you like to file the entire conversation?</p>
          <button
            onClick={onFileConversation}
            disabled={filingInProgress}
            className="w-full text-xs py-2 bg-status-green hover:bg-status-green disabled:bg-status-green-dim/50 disabled:text-status-green/50 text-white rounded font-medium flex items-center justify-center gap-1.5 transition-colors"
            data-testid="assist-file-conversation-btn"
          >
            {filingInProgress ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderOpen className="w-3.5 h-3.5" />}
            File entire conversation
          </button>
        </div>
      )}

      {/* Confidence */}
      {data.confidence > 0 && (
        <p className={`text-[10px] ${t.textDim} text-center`}>AI confidence: {Math.round(data.confidence * 100)}%</p>
      )}

      {/* Save as Matter Note */}
      {selectedMatter?.reference && data.summary && (
        <div className={`p-3 rounded-lg ${t.bgPanel} border ${t.border}`}>
          <button
            onClick={() => {
              setNoteModalData({
                noteType: "general",
                analysis: data.summary + (tasks.length > 0 ? "\n\n## Tasks\n" + tasks.map(tk => `- ${tk.description}${tk.due_date ? ` (due: ${tk.due_date})` : ""}`).join("\n") : "") + (deadlines.length > 0 ? "\n\n## Deadlines\n" + deadlines.map(dl => `- ${dl.description}${dl.date ? ` (${dl.date})` : ""}`).join("\n") : ""),
                emailId: selectedEmail?.id || "",
                attachments: [],
              });
              setNoteModalOpen(true);
            }}
            className="w-full text-xs py-2 bg-purple-700 hover:bg-purple-600 text-white rounded font-medium flex items-center justify-center gap-1.5 transition-colors"
            data-testid="assist-save-matter-note-btn"
          >
            <StickyNote className="w-3.5 h-3.5" /> Save as Matter Note
          </button>
        </div>
      )}

      {/* Matter Notes for current matter */}
      {currentMatterRef && (
        <div className={`p-3 rounded-lg ${t.bgPanel} border ${t.border}`} data-testid="assist-matter-notes-section">
          <h4 className="text-[10px] font-bold uppercase tracking-widest text-teal mb-2 flex items-center gap-1.5">
            <BookOpen className="w-3.5 h-3.5" /> Matter Notes
          </h4>
          {matterNotesLoading ? (
            <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> Loading...</div>
          ) : matterNotes.length === 0 ? (
            <p className={`text-xs ${t.textDim}`}>No notes yet for {currentMatterRef}</p>
          ) : (
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
              {matterNotes.map(note => (
                <div key={note.id} className={`p-2 rounded border ${t.borderSub} ${t.bgCard} text-xs`} data-testid={`matter-note-${note.id}`}>
                  <div className="flex items-start justify-between gap-1">
                    <p className={`font-medium ${t.text} truncate flex-1`}>{note.title}</p>
                    {note.requires_review && <span className="text-[9px] px-1 py-0.5 rounded bg-status-amber/15 text-status-amber flex-shrink-0">Review</span>}
                  </div>
                  <p className={`${t.textDim} text-[10px] mt-0.5`}>
                    {note.created_by_display_name || "AI"} · {new Date(note.created_at).toLocaleDateString([], { day: "numeric", month: "short" })}
                  </p>
                  {note.sharepoint_path && (
                    <a href={note.sharepoint_path} target="_blank" rel="noopener noreferrer"
                      className="text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-0.5 mt-1">
                      <ExternalLink className="w-2.5 h-2.5" /> Open in SharePoint
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Contract Review Quick Actions ── */}
      {selectedEmail?.attachments?.length > 0 && selectedMatter?.reference && (
        <ContractReviewSection
          email={selectedEmail}
          matterReference={selectedMatter.reference}
          selectedAttachments={selectedAttachments}
          setSelectedAttachments={setSelectedAttachments}
          reviewLoading={reviewLoading}
          setReviewLoading={setReviewLoading}
          reviewResult={reviewResult}
          setReviewResult={setReviewResult}
          onSaveAsNote={(noteType, analysis) => {
            setNoteModalData({ noteType, analysis, emailId: selectedEmail?.id, attachments: Array.from(selectedAttachments).map(id => {
              const att = selectedEmail.attachments.find(a => a.id === id);
              return att ? { sharepoint_path: "", label: "" } : {};
            })});
            setNoteModalOpen(true);
          }}
          getHeaders={getHeaders}
          t={t}
        />
      )}

      {/* Save as matter note modal */}
      {noteModalOpen && noteModalData && (
        <SaveMatterNoteModal
          open={noteModalOpen}
          onOpenChange={setNoteModalOpen}
          matterReference={selectedMatter?.reference || ""}
          noteType={noteModalData.noteType}
          bodyMarkdown={noteModalData.analysis}
          sourceContext={{
            chat_session_id: "",
            chat_message_id: "",
            email_id: noteModalData.emailId || "",
            attachments: noteModalData.attachments || [],
          }}
          userName={""} 
          userId={""}
          getHeaders={getHeaders}
          t={t}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CONTRACT REVIEW SECTION (inside AI Assist panel)
// ═══════════════════════════════════════════════════════════════════
function ContractReviewSection({ email, matterReference, selectedAttachments, setSelectedAttachments, reviewLoading, setReviewLoading, reviewResult, setReviewResult, onSaveAsNote, getHeaders, t }) {
  const attachments = email?.attachments || [];

  const toggleAttachment = (id) => {
    setSelectedAttachments(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const runReview = async (noteType) => {
    if (selectedAttachments.size === 0) {
      toast.error("Select at least one attachment to review");
      return;
    }
    setReviewLoading(true);
    setReviewResult(null);
    try {
      const attList = Array.from(selectedAttachments).map((id, idx) => {
        const att = attachments.find(a => a.id === id);
        let label = "";
        if (noteType === "contract_review_other_side_amends" && selectedAttachments.size >= 2) {
          label = idx === 0 ? "baseline" : "amendments";
        }
        return { id, name: att?.name || id, label };
      });
      const res = await axios.post(`${API}/bonsai/contract-review/analyse`, {
        matter_reference: matterReference,
        note_type: noteType,
        email_id: email.id,
        attachments: attList,
      }, { headers: getHeaders() });
      setReviewResult({ noteType, analysis: res.data.analysis, attachments: res.data.attachments_processed });
    } catch (err) {
      toast.error(errorMessage(err, "Contract review failed"));
    } finally {
      setReviewLoading(false);
    }
  };

  return (
    <div className={`mt-3 pt-3 border-t ${t.border}`} data-testid="contract-review-section">
      <h4 className="text-[10px] font-bold uppercase tracking-widest text-teal mb-2 px-1">Contract Review</h4>
      <p className={`text-[10px] ${t.textDim} mb-2 px-1`}>Matter: <span className="font-mono text-teal">{matterReference}</span></p>

      {/* Attachment checkboxes */}
      <div className="space-y-1 mb-3">
        <p className={`text-[10px] ${t.textMuted} px-1`}>Select attachment(s) to review:</p>
        {attachments.map(att => (
          <label
            key={att.id}
            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-colors ${selectedAttachments.has(att.id) ? "bg-teal-dim/30 border border-teal/40" : `${t.bgPanel} border border-transparent hover:border-teal/20`}`}
            data-testid={`review-att-${att.id}`}
          >
            <Checkbox checked={selectedAttachments.has(att.id)} onCheckedChange={() => toggleAttachment(att.id)} className="scale-75" />
            <Paperclip className="w-3 h-3 text-teal flex-shrink-0" />
            <span className={`${t.text} truncate`}>{att.name}</span>
          </label>
        ))}
      </div>

      {/* Quick action buttons */}
      <div className="space-y-1.5 px-1">
        <button
          onClick={() => runReview("contract_review_client_perspective")}
          disabled={reviewLoading || selectedAttachments.size === 0}
          className="w-full text-xs py-2 bg-teal hover:bg-teal disabled:bg-teal-dim/40 disabled:text-teal/40 text-white rounded font-medium flex items-center justify-center gap-1.5 transition-colors"
          data-testid="review-client-perspective-btn"
        >
          {reviewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
          Review this contract (client perspective)
        </button>
        <button
          onClick={() => runReview("contract_review_other_side_amends")}
          disabled={reviewLoading || selectedAttachments.size === 0}
          className="w-full text-xs py-2 bg-status-amber hover:bg-status-amber disabled:bg-status-amber-dim/40 disabled:text-status-amber/40 text-white rounded font-medium flex items-center justify-center gap-1.5 transition-colors"
          data-testid="review-other-side-btn"
        >
          {reviewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
          Review the other side's amendments
        </button>
      </div>

      {/* Review result */}
      {reviewResult && (
        <div className={`mt-3 p-3 rounded-lg ${t.bgPanel} border ${t.border}`} data-testid="contract-review-result">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[10px] font-bold uppercase tracking-widest text-teal">Analysis</h4>
            <button
              onClick={() => onSaveAsNote(reviewResult.noteType, reviewResult.analysis)}
              className="text-[10px] px-2 py-1 bg-purple-600 hover:bg-purple-500 text-white rounded font-medium flex items-center gap-1 transition-colors"
              data-testid="save-as-note-btn"
            >
              <FileText className="w-3 h-3" />
              Save as matter note
            </button>
          </div>
          <div className={`text-xs ${t.text} whitespace-pre-wrap max-h-[400px] overflow-y-auto leading-relaxed`} data-testid="contract-review-analysis">
            {reviewResult.analysis}
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// AI CHAT PANEL — broader assistant (search Outlook + SharePoint)
// ═══════════════════════════════════════════════════════════════════
function AiChatPanel({ t, getHeaders, checkedIds, onOpenFileDialog, checkedCount, setCheckedIds,
  messages: allMessages, filedEmailIds, folders, onFileEmails, onArchiveEmails, onFlagEmails, triageChatRef }) {
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [chatSearching, setChatSearching] = useState(false);
  const [chatSearchIds, setChatSearchIds] = useState(new Set());
  const [pendingAction, setPendingAction] = useState(null); // { type, params, label, msgIndex }
  const chatEndRef = useRef(null);

  const toggleChatSearchId = (id) => {
    setChatSearchIds(prev => {
      const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
    });
    if (setCheckedIds) {
      setCheckedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
    }
  };

  const handleConfirmAction = async () => {
    if (!pendingAction) return;
    const { type, params, msgIndex } = pendingAction;
    setPendingAction(null);
    setChatMessages(prev => prev.map((m, i) => i === msgIndex
      ? { ...m, actions_taken: m.actions_taken?.map(a => a.type === "requires_confirmation" ? { ...a, confirmed: true, confirming: true } : a) }
      : m
    ));
    try {
      const res = await axios.post(`${API}/bonsai/chat/confirm`, { action_type: type, params }, { headers: getHeaders() });
      setChatMessages(prev => {
        const updated = [...prev];
        if (updated[msgIndex]) {
          updated[msgIndex] = {
            ...updated[msgIndex],
            actions_taken: updated[msgIndex].actions_taken?.map(a =>
              a.type === "requires_confirmation" ? { ...a, confirmed: true, confirming: false, result: res.data } : a
            )
          };
        }
        updated.push({ role: "assistant", content: res.data.response || "Action completed.", actions_taken: res.data.actions_taken, ts: Date.now() });
        return updated;
      });
    } catch {
      toast.error("Action failed");
      setChatMessages(prev => prev.map((m, i) => i === msgIndex
        ? { ...m, actions_taken: m.actions_taken?.map(a => a.type === "requires_confirmation" ? { ...a, confirmed: false, confirming: false } : a) }
        : m
      ));
    }
  };

  const handleCancelAction = () => {
    if (!pendingAction) return;
    const { msgIndex } = pendingAction;
    setPendingAction(null);
    setChatMessages(prev => {
      const updated = [...prev];
      if (updated[msgIndex]) {
        updated[msgIndex] = {
          ...updated[msgIndex],
          actions_taken: updated[msgIndex].actions_taken?.map(a =>
            a.type === "requires_confirmation" ? { ...a, cancelled: true } : a
          )
        };
      }
      updated.push({ role: "assistant", content: "Action cancelled.", ts: Date.now() });
      return updated;
    });
  };

  // Run a full-mailbox search via the dedicated ai-search endpoint
  // If the Graph $filter fails (400 — e.g. toRecipients/from queries), retry without
  // the filter and apply it client-side on the returned messages.
  const runFullMailboxSearch = async (query, filter, folder, top = 200) => {
    setChatSearching(true);
    try {
      const body = {};
      if (query) body.query = query;
      if (filter) body.filter = filter;
      if (folder) body.folder = folder;
      body.top = top;
      try {
        const res = await axios.post(`${API}/bonsai/mail/ai-search`, body, { headers: getHeaders() });
        return res.data;
      } catch (err) {
        // Graph API returns 400 for unsupported $filter (e.g. toRecipients, from/emailAddress)
        if (err?.response?.status === 400 && filter) {
          console.warn("Graph $filter failed (400), retrying without filter for client-side filtering:", filter);
          const retryBody = { top };
          if (query) retryBody.query = query;
          if (folder) retryBody.folder = folder;
          const retryRes = await axios.post(`${API}/bonsai/mail/ai-search`, retryBody, { headers: getHeaders() });
          const data = retryRes.data;
          // Apply basic client-side filtering on the results
          if (data.messages && filter) {
            const fl = filter.toLowerCase();
            data.messages = data.messages.filter(m => {
              // "only me in To" — check toRecipients length
              if (fl.includes('torecipients') || fl.includes('to/emailaddress')) {
                const toList = m.to || m.toRecipients || [];
                if (typeof toList === 'string') return true; // can't parse, keep it
                return toList.length === 1;
              }
              // from/emailAddress filter — match on from fields
              if (fl.includes('from/emailaddress') || fl.includes('from/email')) {
                const addrMatch = filter.match(/['"]([\w.@+-]+)['"]/);
                if (addrMatch) {
                  const addr = addrMatch[1].toLowerCase();
                  return (m.from_email || m.from || '').toLowerCase().includes(addr);
                }
              }
              return true;
            });
            data.total = data.messages.length;
          }
          return data;
        }
        throw err;
      }
    } finally {
      setChatSearching(false);
    }
  };

  // ── "Clear my inbox" command — runs locally using loaded emails ──
  const [triageRunning, setTriageRunning] = useState(false);
  const [triageResults, setTriageResults] = useState(null);

  const runClearMyInbox = useCallback(async () => {
    if (!allMessages || allMessages.length === 0) {
      setChatMessages(prev => [...prev, { role: "assistant", content: "No emails loaded. Open your inbox first.", ts: Date.now() }]);
      return;
    }
    setTriageRunning(true);
    setChatMessages(prev => [...prev, { role: "user", content: "clear my inbox", ts: Date.now() }]);
    const results = { filed: [], archived: [], flagged: [], overdue: [] };

    // Classify all loaded inbox emails
    for (const m of allMessages) {
      const badges = computeEmailStatus(m, filedEmailIds);
      if (badges.includes("matter") && !badges.includes("filed")) results.filed.push(m);
      if (badges.includes("info_only") && m.received && (Date.now() - new Date(m.received).getTime()) / 86400000 > 3) results.archived.push(m);
      if (badges.includes("reply_needed") && !badges.includes("overdue")) results.flagged.push(m);
      if (badges.includes("overdue")) results.overdue.push(m);
    }

    const actions = [];

    // PR #466 — phantom auto-file path REMOVED.
    //
    // The "clear my inbox" triage previously hit POST /filing/batch
    // with `auto_match_matters: true` and no drive_id/folder_id/matter
    // context. There is no BE handler that interprets `auto_match_matters`
    // (grep "auto_match_matters" backend/ → empty), so the call would
    // either queue items that the inner _file_email_inner could never
    // route OR — pre-PR-466 — silently file based on whatever guesswork
    // the BE applied. Either way the user has no per-email confirmation
    // of WHERE each email landed.
    //
    // Surfacing it instead as a list the user can act on manually
    // matches the standing rule (no auto-file path exists; every filing
    // must carry an explicit gesture from the FE).
    if (results.filed.length > 0) {
      actions.push({
        type: "triage_filed",
        count: 0,
        manual_list: results.filed.slice(0, 50),
        message: (
          "Found " + results.filed.length + " emails with matter refs. " +
          "Open each one and click File to confirm the destination — " +
          "the auto-file path was removed (PR #466) to prevent " +
          "silent mis-filings."
        ),
      });
    }

    // Step 2: Archive old info-only emails
    if (results.archived.length > 0) {
      const archiveFolder = folders?.find(f => f.name.toLowerCase() === "archive");
      if (archiveFolder) {
        try {
          await axios.post(`${API}/bonsai/mail/messages/move`, { message_ids: results.archived.map(m => m.id), destination_folder_id: archiveFolder.id }, { headers: getHeaders() });
          actions.push({ type: "triage_archived", count: results.archived.length });
        } catch {
          actions.push({ type: "triage_archived", count: 0, error: "Archive failed" });
        }
      }
    }

    // Step 3: Flag reply-needed emails
    if (results.flagged.length > 0) {
      for (const m of results.flagged) {
        try {
          await axios.patch(`${API}/bonsai/mail/messages/${encodeURIComponent(m.id)}`, { flag: { flagStatus: "flagged" } }, { headers: getHeaders() });
        } catch { /* best-effort */ }
      }
      actions.push({ type: "triage_flagged", count: results.flagged.length, emails: results.flagged.slice(0, 10) });
    }

    // Step 4: Surface overdue
    if (results.overdue.length > 0) {
      actions.push({ type: "triage_overdue", count: results.overdue.length, emails: results.overdue.slice(0, 10) });
    }

    // Build summary
    const lines = [];
    if (results.filed.length > 0) lines.push(`✅ Filed ${results.filed.length} emails to SharePoint`);
    if (results.archived.length > 0) lines.push(`📦 Archived ${results.archived.length} info-only emails`);
    if (results.flagged.length > 0) lines.push(`🚩 Flagged ${results.flagged.length} emails needing replies`);
    if (results.overdue.length > 0) lines.push(`⚠️  ${results.overdue.length} overdue emails need urgent attention`);
    if (lines.length === 0) lines.push("Your inbox is already clear — nothing to triage!");

    setChatMessages(prev => [...prev, {
      role: "assistant", content: lines.join("\n"), actions_taken: actions,
      triage_results: results, ts: Date.now()
    }]);
    setTriageResults(results);
    setTriageRunning(false);
  }, [allMessages, filedEmailIds, folders, getHeaders]);

  // Auto-trigger from "Triage now" button
  useEffect(() => {
    if (triageChatRef?.current) {
      triageChatRef.current = false;
      runClearMyInbox();
    }
  }, [triageChatRef?.current]);

  const sendChatMessage = async (e) => {
    e?.preventDefault();
    const msg = chatInput.trim();
    if (!msg) return;
    setChatInput("");

    // Intercept "clear my inbox" command
    if (/^clear\s+(my\s+)?inbox$/i.test(msg)) {
      runClearMyInbox();
      return;
    }

    setChatSending(true);
    setChatMessages(prev => [...prev, { role: "user", content: msg, ts: Date.now() }]);
    try {
      const payload = { message: msg };
      const allSelected = new Set([...chatSearchIds, ...(checkedIds || [])]);
      if (allSelected.size > 0) {
        payload.context = { selected_message_ids: Array.from(allSelected), selected_count: allSelected.size };
      }
      const res = await axios.post(`${API}/bonsai/chat`, payload, { headers: getHeaders() });

      // Re-run the search via the dedicated ai-search endpoint to get full mailbox results (up to 200)
      const searchAction = res.data.actions_taken?.find(a => a.type === "search_executed");
      let fullSearchResults = res.data.search_results;
      if (searchAction && (searchAction.query || searchAction.filter)) {
        try {
          setChatSearching(true);
          const fullRes = await runFullMailboxSearch(
            searchAction.filter ? null : searchAction.query,
            searchAction.filter || null,
            searchAction.folder === "inbox" ? "inbox" : null,
            200
          );
          if (fullRes.messages?.length > 0) {
            // Convert to the shape expected by search_results display
            fullSearchResults = {
              emails: fullRes.messages.map(m => ({
                id: m.id,
                subject: m.subject,
                from: `${m.from_name} <${m.from_email}>`,
                date: (m.received || "").slice(0, 10),
                preview: m.preview,
                to: m.to,
                cc: m.cc,
              })),
              query: searchAction.query,
              total: fullRes.total,
            };
            // Update the search_executed action count
            if (searchAction) searchAction.result_count = fullRes.total;
          }
        } catch (searchErr) {
          // Fallback: keep the original limited results
          console.warn("Full mailbox search failed, using AI results:", searchErr);
        }
      }

      const newMsg = {
        role: "assistant", content: res.data.response,
        actions_taken: res.data.actions_taken, search_results: fullSearchResults, ts: Date.now()
      };
      setChatMessages(prev => {
        const updated = [...prev, newMsg];
        // If there's a requires_confirmation action, store it as pendingAction
        const confirmAction = res.data.actions_taken?.find(a => a.type === "requires_confirmation");
        if (confirmAction) {
          setPendingAction({
            type: confirmAction.action_type,
            params: confirmAction.params || {},
            label: confirmAction.action_type?.replace(/_/g, " ") || "action",
            msgIndex: updated.length - 1
          });
        }
        return updated;
      });
      if (res.data.rules_created?.length > 0) toast.success(`Created ${res.data.rules_created.length} rule(s)`);
    } catch { toast.error("Failed to send message"); }
    finally { setChatSending(false); setChatSearching(false); }
  };

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);

  return (
    <div className="p-4 flex flex-col h-full" data-testid="ai-chat-panel">
      <div className={`text-xs ${t.textMuted} mb-3 p-2 rounded ${t.bgPanel} border ${t.border}`}>
        <p className="font-medium text-blue-400 mb-1">CaseMinder AI Chat</p>
        <p>Search across <strong>Outlook emails</strong> and <strong>SharePoint</strong>. Find emails, file conversations, create rules, and more.</p>
      </div>

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto space-y-2 mb-2 min-h-0">
        {chatMessages.length === 0 && (
          <div className={`text-center py-6 ${t.textDim} text-xs`}>
            <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-40" />
            <p>Try: &quot;find all emails from Smith &amp; Co&quot;</p>
            <p className="mt-1">&quot;search SharePoint for completion statement&quot;</p>
            <p className="mt-1">&quot;file all emails in this thread&quot;</p>
            <p className="mt-1 text-purple-400">&quot;clear my inbox&quot;</p>
          </div>
        )}
        {chatMessages.map((m, i) => (
          <div key={i} className={`text-xs p-2 rounded ${m.role === "user" ? "bg-blue-900/20 text-blue-200 ml-4" : `${t.bgPanel} ${t.text} mr-2`}`}>
            <p className="whitespace-pre-wrap">{m.content}</p>
            {m.actions_taken?.length > 0 && (
              <div className="mt-1.5 space-y-0.5">
                {m.actions_taken.map((a, j) => (
                  <div key={j} className="flex items-center gap-1 text-[10px]">
                    {a.type === "search_executed" && <><Search className="w-3 h-3 text-blue-400" /><span className="text-blue-300">Found {a.result_count} email{a.result_count !== 1 ? "s" : ""}</span></>}
                    {a.type === "emails_filed" && <><FolderOpen className={`w-3 h-3 ${a.filed_count > 0 ? "text-status-green" : "text-status-red"}`} /><span className={a.filed_count > 0 ? "text-status-green" : "text-status-red"}>{a.filed_count > 0 ? `${a.filed_count} email${a.filed_count !== 1 ? "s" : ""} filed${a.matter_reference ? ` \u2192 ${a.matter_reference}` : ""}` : `Filing failed${a.error ? `: ${a.error}` : ""}`}</span></>}
                    {a.type === "emails_deleted" && <><Trash2 className="w-3 h-3 text-status-red" /><span className="text-status-red">{a.deleted_count || 0} deleted</span></>}
                    {a.type === "emails_moved" && <><FolderUp className="w-3 h-3 text-status-amber" /><span className="text-status-amber">{a.moved_count || 0} moved{a.destination ? ` \u2192 ${a.destination}` : ""}</span></>}
                    {a.type === "emails_categorized" && <><CheckCircle2 className="w-3 h-3 text-purple-400" /><span className="text-purple-300">{a.categorized_count || 0} categorized as &quot;{a.category}&quot;</span></>}
                    {a.type === "rule_created" && <><CheckCircle2 className="w-3 h-3 text-status-green" /><span className="text-status-green">Rule: {a.rule_name}</span></>}
                    {a.type === "requires_confirmation" && (
                      a.cancelled ? (
                        <><X className="w-3 h-3 text-slate-400" /><span className="text-slate-400">{a.action_type?.replace(/_/g, " ")} — cancelled</span></>
                      ) : a.confirmed ? (
                        <>{a.confirming ? <Loader2 className="w-3 h-3 text-status-green animate-spin" /> : <CheckCircle2 className="w-3 h-3 text-status-green" />}<span className="text-status-green">{a.action_type?.replace(/_/g, " ")} — {a.confirming ? "executing…" : "confirmed"}</span></>
                      ) : (
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-3 h-3 text-yellow-400" />
                          <span className="text-yellow-300">{a.action_type?.replace(/_/g, " ")}</span>
                          <button onClick={handleConfirmAction} className="px-2 py-0.5 bg-status-green hover:bg-status-green text-white rounded text-[10px] font-medium transition-colors" data-testid="chat-confirm-action">Confirm</button>
                          <button onClick={handleCancelAction} className="px-2 py-0.5 bg-slate-600 hover:bg-slate-500 text-white rounded text-[10px] font-medium transition-colors" data-testid="chat-cancel-action">Cancel</button>
                        </div>
                      )
                    )}
                    {a.type === "search_failed" && <><AlertTriangle className="w-3 h-3 text-status-red" /><span className="text-status-red">Search failed</span></>}
                    {a.type === "matter_folders_found" && <><Search className="w-3 h-3 text-teal" /><span className="text-teal">Found {a.count || 0} matter folder{(a.count || 0) !== 1 ? "s" : ""}</span></>}
                    {a.type === "triage_filed" && <><CheckCircle2 className="w-3 h-3 text-status-green" /><span className="text-status-green">{a.count > 0 ? `${a.count} email${a.count !== 1 ? "s" : ""} filed` : a.error || "Filing skipped"}</span></>}
                    {a.type === "triage_archived" && <><Archive className="w-3 h-3 text-blue-400" /><span className="text-blue-300">{a.count > 0 ? `${a.count} info-only email${a.count !== 1 ? "s" : ""} archived` : a.error || "Archive skipped"}</span></>}
                    {a.type === "triage_flagged" && <><Flag className="w-3 h-3 text-status-amber" /><span className="text-status-amber">{a.count} email{a.count !== 1 ? "s" : ""} flagged for reply</span></>}
                    {a.type === "triage_overdue" && <><AlertTriangle className="w-3 h-3 text-status-red" /><span className="text-status-red">{a.count} overdue email{a.count !== 1 ? "s" : ""} need attention</span></>}
                  </div>
                ))}
              </div>
            )}
            {/* Selectable search results */}
            {m.search_results?.emails?.length > 0 && (
              <div className="mt-2 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-txt-muted">{m.search_results.total || m.search_results.emails.length} results (full mailbox) — select to act</span>
                  <button onClick={() => {
                    const allIds = m.search_results.emails.map(e => e.id);
                    const allSelected = allIds.every(id => chatSearchIds.has(id));
                    setChatSearchIds(prev => {
                      const next = new Set(prev);
                      allIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
                      return next;
                    });
                    if (setCheckedIds) {
                      setCheckedIds(prev => {
                        const next = new Set(prev);
                        allIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
                        return next;
                      });
                    }
                  }} className="text-[10px] text-purple-400 hover:text-purple-300" data-testid="chat-select-all-results">
                    {m.search_results.emails.every(e => chatSearchIds.has(e.id)) ? "Deselect all" : "Select all"}
                  </button>
                </div>
                {m.search_results.emails.map(em => (
                  <div key={em.id} onClick={() => toggleChatSearchId(em.id)}
                    className={`flex items-center gap-1.5 p-1.5 rounded text-[10px] cursor-pointer border transition-all ${
                      chatSearchIds.has(em.id) ? "border-green-500/60 bg-status-green-dim/20 text-white" : `border-transparent hover:bg-navy-hover ${t.textMuted}`
                    }`} data-testid={`chat-search-${em.id}`}>
                    <Checkbox checked={chatSearchIds.has(em.id)} onCheckedChange={() => toggleChatSearchId(em.id)} className="scale-75" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{em.subject || "No subject"}</p>
                      <p className="truncate opacity-60">{(em.from || "").replace(/<.*>/, "").trim()}</p>
                    </div>
                  </div>
                ))}
                {chatSearchIds.size > 0 && onOpenFileDialog && (
                  <button onClick={() => onOpenFileDialog(chatSearchIds)} className="w-full text-[10px] py-1.5 bg-status-green hover:bg-status-green text-white rounded font-medium flex items-center justify-center gap-1 mt-1 transition-colors" data-testid="chat-file-search-results">
                    <FolderOpen className="w-3 h-3" /> File {chatSearchIds.size} to matter
                  </button>
                )}
              </div>
            )}
            {/* Triage results — clickable overdue/reply-needed email lists */}
            {m.triage_results?.overdue?.length > 0 && (
              <div className="mt-2 space-y-1">
                <span className="text-[10px] text-status-red font-medium">Overdue — need urgent attention:</span>
                {m.triage_results.overdue.map(em => (
                  <div key={em.id} className="flex items-center gap-1.5 p-1.5 rounded text-[10px] cursor-pointer border border-red-800/30 hover:bg-status-red-dim/20 text-status-red"
                    onClick={() => { if (em.id) toggleChatSearchId(em.id); }} data-testid={`triage-overdue-${em.id?.substring(0, 8)}`}>
                    <Clock className="w-3 h-3 text-status-red flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{em.subject || "No subject"}</p>
                      <p className="truncate opacity-60">{em.from_name || em.from_email}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {m.triage_results?.flagged?.length > 0 && (
              <div className="mt-2 space-y-1">
                <span className="text-[10px] text-status-amber font-medium">Flagged — reply needed:</span>
                {m.triage_results.flagged.map(em => (
                  <div key={em.id} className="flex items-center gap-1.5 p-1.5 rounded text-[10px] cursor-pointer border border-amber-800/30 hover:bg-status-amber-dim/20 text-status-amber"
                    onClick={() => { if (em.id) toggleChatSearchId(em.id); }} data-testid={`triage-flagged-${em.id?.substring(0, 8)}`}>
                    <Flag className="w-3 h-3 text-status-amber flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{em.subject || "No subject"}</p>
                      <p className="truncate opacity-60">{em.from_name || em.from_email}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {(chatSearching || triageRunning) && (
          <div className="flex items-center gap-1.5 text-xs text-blue-300 px-2 py-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            <span>{triageRunning ? "Triaging inbox…" : "Searching full mailbox…"}</span>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Chat input */}
      <form onSubmit={sendChatMessage} className="flex gap-1.5 flex-shrink-0">
        <input
          value={chatInput} onChange={e => setChatInput(e.target.value)}
          placeholder={chatSearchIds.size > 0 || (checkedCount || 0) > 0 ? `${chatSearchIds.size + (checkedCount || 0)} selected \u2014 file, delete, move...` : "Search emails, SharePoint, file, move..."}
          className={`flex-1 text-xs px-2.5 py-1.5 rounded ${t.bgInput} border ${t.border} ${t.text} focus:outline-none focus:ring-1 focus:ring-blue-500/50`}
          disabled={chatSending}
          data-testid="ai-chat-input"
        />
        <button type="submit" disabled={!chatInput.trim() || chatSending}
          className="px-2 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800/50 text-white rounded text-xs transition-colors"
          data-testid="ai-chat-send">
          {chatSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </button>
      </form>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// FOLDER PANEL (with drag-drop targets & pinned folders)
// ═══════════════════════════════════════════════════════════════════
function FolderPanel({ folders, activeFolderId, onSelect, loading, onDropToFolder, pinnedIds, onTogglePin, t }) {
  const order = ["inbox", "drafts", "sent items", "archive", "deleted items", "junk email"];
  const wellKnown = ["inbox", "drafts", "sent items", "deleted items", "junk email", "archive", "outbox", "conversation history", "clutter"];
  const [expanded, setExpanded] = useState({});
  const [dragOverId, setDragOverId] = useState(null);

  const childMap = {};
  const topFolders = [];
  folders.forEach(f => {
    const isChild = folders.some(p => p.id === f.parentId && wellKnown.includes(p.name.toLowerCase()));
    if (isChild) { (childMap[f.parentId] ||= []).push(f); } else { topFolders.push(f); }
  });
  topFolders.sort((a, b) => {
    const ai = order.indexOf(a.name.toLowerCase()), bi = order.indexOf(b.name.toLowerCase());
    if (ai >= 0 && bi >= 0) return ai - bi; if (ai >= 0) return -1; if (bi >= 0) return 1;
    return a.name.localeCompare(b.name);
  });

  // Separate pinned from the rest
  const pinned = topFolders.filter(f => pinnedIds.includes(f.id));
  const unpinned = topFolders.filter(f => !pinnedIds.includes(f.id));

  const handleDragOver = (e, fId) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverId(fId); };
  const handleDragLeave = () => setDragOverId(null);
  const handleDrop = (e, fId) => { e.preventDefault(); setDragOverId(null); onDropToFolder(fId); };

  const renderFolder = (f, indent = false) => {
    const children = childMap[f.id] || [];
    const hasChildren = children.length > 0;
    const isActive = f.id === activeFolderId;
    const isDragOver = f.id === dragOverId;
    const isPinned = pinnedIds.includes(f.id);
    return (
      <div key={f.id}>
        <div
          onDragOver={e => handleDragOver(e, f.id)} onDragLeave={handleDragLeave}
          onDrop={e => handleDrop(e, f.id)}
          className={`group relative ${isDragOver ? t.dropTarget + " rounded" : ""}`}>
          <button
            onClick={() => { onSelect(f.id, f.name); if (hasChildren) setExpanded(p => ({ ...p, [f.id]: !p[f.id] })); }}
            className={`w-full flex items-center gap-2 ${indent ? "pl-10 pr-3" : "px-3"} py-[7px] text-[13px] transition-colors
              ${isActive ? `${t.bgActive} ${t.textAccent} border-l-2 border-current` : `${t.textSec} ${t.bgHover} border-l-2 border-transparent`}`}
            data-testid={`folder-${f.name.toLowerCase().replace(/\s/g, "-")}`}>
            {!indent && (hasChildren
              ? (expanded[f.id] ? <ChevronDown className="w-3 h-3 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 flex-shrink-0" />)
              : <span className="w-3 flex-shrink-0" />)}
            {getFolderIcon(f.name)}
            <span className="truncate flex-1 text-left">{f.name}</span>
            {f.unread > 0 && (
              <span className={`text-[10px] font-bold ${t.badgeText} ${t.badgeBg} px-1.5 py-px rounded-full min-w-[18px] text-center`}>
                {f.unread}
              </span>
            )}
            {/* Pin button (visible on hover) */}
            <button
              onClick={e => { e.stopPropagation(); onTogglePin(f.id); }}
              className={`opacity-0 group-hover:opacity-100 p-0.5 rounded transition-opacity ${isPinned ? `${t.textAccent} opacity-100` : t.textDim}`}
              title={isPinned ? "Unpin" : "Pin to top"}
              data-testid={`pin-folder-${f.id.substring(0, 8)}`}>
              <Pin className={`w-3 h-3 ${isPinned ? "fill-current" : ""}`} />
            </button>
          </button>
        </div>
        {hasChildren && expanded[f.id] && children.map(c => renderFolder(c, true))}
      </div>
    );
  };

  return (
    <div className={`w-52 flex-shrink-0 border-r ${t.border} ${t.bgPanel} overflow-y-auto`} data-testid="folder-panel">
      {/* Pinned section */}
      {pinned.length > 0 && (
        <>
          <div className="px-3 pt-3 pb-1">
            <h3 className={`text-[10px] font-bold uppercase tracking-wider ${t.textDim} flex items-center gap-1.5`}>
              <Pin className="w-3 h-3" /> Favourites
            </h3>
          </div>
          <div className="pb-1">{pinned.map(f => renderFolder(f))}</div>
          <div className={`mx-3 h-px ${t.border}`} />
        </>
      )}
      {/* All folders */}
      <div className="px-3 pt-2 pb-1">
        <h3 className={`text-[10px] font-bold uppercase tracking-wider ${t.textDim} flex items-center gap-1.5`}>
          <Mail className={`w-3 h-3 ${t.textAccentDim}`} /> Folders
          {loading && <Loader2 className={`w-3 h-3 animate-spin ${t.textDim} ml-auto`} />}
        </h3>
      </div>
      <div className="pb-2">{unpinned.map(f => renderFolder(f))}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// AI TRIAGE BAR — daily summary banner above email list
// ═══════════════════════════════════════════════════════════════════
function TriageBar({ messages, filedEmailIds, onTriageNow, t }) {
  const [dismissed, setDismissed] = useState(false);

  const stats = useMemo(() => {
    let needReplies = 0, needFiling = 0, overdue = 0, infoOnly = 0;
    for (const m of messages) {
      const badges = computeEmailStatus(m, filedEmailIds);
      if (badges.includes("reply_needed")) needReplies++;
      if (badges.includes("overdue")) overdue++;
      if (badges.includes("info_only")) infoOnly++;
      if (badges.includes("matter") && !badges.includes("filed")) needFiling++;
    }
    return { needReplies, needFiling, overdue, infoOnly };
  }, [messages, filedEmailIds]);

  if (dismissed || messages.length === 0) return null;
  const { needReplies, needFiling, overdue, infoOnly } = stats;
  if (needReplies + needFiling + overdue + infoOnly === 0) return null;

  return (
    <div className={`px-3 py-2 border-b ${t.border} bg-gradient-to-r from-purple-900/20 via-blue-900/15 to-teal-dim/15 flex items-center gap-2 flex-wrap`} data-testid="triage-bar">
      <Zap className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
      <span className={`text-[11px] ${t.textSec} font-medium`}>Inbox:</span>
      <span className="text-[11px] text-status-amber font-medium">{needReplies} need replies</span>
      <span className={`text-[11px] ${t.textDim}`}>·</span>
      <span className="text-[11px] text-blue-400 font-medium">{needFiling} need filing</span>
      <span className={`text-[11px] ${t.textDim}`}>·</span>
      <span className="text-[11px] text-status-red font-medium">{overdue} overdue</span>
      <span className={`text-[11px] ${t.textDim}`}>·</span>
      <span className="text-[11px] text-gray-400 font-medium">{infoOnly} info only</span>
      <div className="ml-auto flex items-center gap-1.5">
        {onTriageNow && (
          <button onClick={onTriageNow}
            className="px-2 py-0.5 bg-purple-700 hover:bg-purple-600 text-white text-[10px] rounded font-medium transition-colors"
            data-testid="triage-now-btn">
            Triage now
          </button>
        )}
        <button onClick={() => setDismissed(true)}
          className={`p-0.5 rounded ${t.textDim} hover:${t.text}`}
          title="Dismiss" data-testid="triage-dismiss-btn">
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MESSAGE LIST (with drag, search, quick actions)
// ═══════════════════════════════════════════════════════════════════
function MessageList({ messages, loading, selectedId, onSelect, checkedIds, onToggleCheck, onSelectAll, onDeselectAll,
  folderName, onRefresh, onLoadMore, hasMore, layout, onDelete, onArchive, onFlag,
  onFindConversation, onFindFromSender, onFileSelected, isSearchActive, onClearSearch, t,
  onLoadFiled, filedFilter, filedLoading, deepScanLoading, onMove, onMarkRead, folders, filedEmailIds,
  onTriageNow, loadProgress, onSelectFiled, onClearFiled,
  onCleanup, cleanupRunning, cleanupProgress, cleanupConfirm, onCleanupConfirm, onCleanupCancel }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, msg }
  const [ctxMoveOpen, setCtxMoveOpen] = useState(false); // show folder picker in context menu
  const [sortBy, setSortBy] = useState("date"); // "date" | "from"
  const [sortDir, setSortDir] = useState("desc"); // "desc" | "asc"

  const filtered = searchTerm.trim()
    ? messages.filter(m =>
        (m.subject || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.from_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.from_email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.preview || "").toLowerCase().includes(searchTerm.toLowerCase()))
    : messages;

  // Sort filtered messages
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "from") {
      const nameA = (a.from_name || a.from_email || "").toLowerCase();
      const nameB = (b.from_name || b.from_email || "").toLowerCase();
      return sortDir === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    }
    // Default: sort by date
    const dateA = new Date(a.received || 0), dateB = new Date(b.received || 0);
    return sortDir === "desc" ? dateB - dateA : dateA - dateB;
  });

  const toggleSort = (field) => {
    if (sortBy === field) { setSortDir(d => d === "desc" ? "asc" : "desc"); }
    else { setSortBy(field); setSortDir(field === "from" ? "asc" : "desc"); }
  };

  // Count filed emails in current view
  const filedInView = sorted.filter(m => m.filed_to_sp || filedEmailIds?.has(m.id)).length;

  const isWide = layout === "bottom" || layout === "off";

  const handleDragStart = (e, msg) => {
    const ids = checkedIds.size > 0 ? [...checkedIds] : [msg.id];
    e.dataTransfer.setData("application/json", JSON.stringify({ type: "emails", ids }));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleAttachmentDragStart = (e, attachment, emailId) => {
    e.dataTransfer.setData("application/json", JSON.stringify({ 
      type: "attachment", 
      attachment: { id: attachment.id, name: attachment.name, contentType: attachment.contentType },
      emailId 
    }));
    e.dataTransfer.effectAllowed = "copy";
    e.stopPropagation();
  };

  const handleContextMenu = (e, msg) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, msg });
  };

  // Close context menu on outside click or Escape
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    const onKey = (e) => { if (e.key === "Escape") setCtxMenu(null); };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("mousedown", close); window.removeEventListener("keydown", onKey); };
  }, [ctxMenu]);

  return (
    <div className={`${isWide ? "flex-1" : "w-80"} flex-shrink-0 border-r ${t.border} ${t.bgList} flex flex-col`} data-testid="message-list">
      {/* Toolbar */}
      <div className={`px-3 py-2 border-b ${t.border} flex items-center gap-2`}>
        {isSearchActive && (
          <button onClick={onClearSearch} className={`${t.textAccentDim} hover:${t.text} mr-1`} title="Back to inbox" data-testid="back-to-inbox-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <span className={`text-sm font-semibold ${isSearchActive ? t.textAccent : t.text} truncate flex-1`}>
          {folderName || "Select a folder"}
          {isSearchActive && <span className={`text-[10px] ml-2 ${t.textDim} font-normal`}>{messages.length} result{messages.length !== 1 ? "s" : ""}</span>}
          {!isSearchActive && loadProgress && !loadProgress.capped && loadProgress.total && (
            <span className={`text-[10px] ml-2 ${t.textDim} font-normal`}>Loading inbox... ({loadProgress.loaded} of {loadProgress.total})</span>
          )}
          {!isSearchActive && loadProgress?.capped && (
            <span className={`text-[10px] ml-2 text-status-amber font-normal`}>Showing 500 most recent emails</span>
          )}
          {!isSearchActive && !loadProgress && messages.length > 0 && !loading && (
            <span className={`text-[10px] ml-2 ${t.textDim} font-normal`}>{messages.length} email{messages.length !== 1 ? "s" : ""}</span>
          )}
        </span>
        <div className="flex items-center gap-0.5">
          {onCleanup && (
            <Button variant="ghost" size="sm"
              onClick={cleanupRunning ? onCleanupCancel : onCleanup}
              className={`h-6 px-1.5 text-[10px] font-medium ${cleanupRunning ? "text-teal bg-teal-dim/30" : t.textDim}`}
              title={cleanupRunning ? "Cancel cleanup" : "Scan for filed emails and clean up inbox"}
              data-testid="cleanup-filed-btn">
              {cleanupRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
              <span className="ml-1">{cleanupRunning ? "Cancel" : "Clean up filed"}</span>
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setShowSearch(s => !s)}
            className={`h-6 w-6 p-0 ${showSearch ? t.textAccent : t.textDim}`} title="Search">
            <Search className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onRefresh} className={`h-6 w-6 p-0 ${t.textDim}`} title="Refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>
      {/* Search bar */}
      {showSearch && (
        <div className={`px-2 py-1.5 border-b ${t.border} flex gap-1`}>
          <div className="relative flex-1">
            <Search className={`absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 ${t.textDim}`} />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search subject, sender..."
              className={`pl-7 h-7 text-xs ${t.bgInput} ${t.border}`} autoFocus data-testid="message-search-input" />
          </div>
          {searchTerm && <button onClick={() => setSearchTerm("")} className={t.textDim}><X className="w-3.5 h-3.5" /></button>}
        </div>
      )}
      {/* Sort bar + Select All */}
      <div className={`px-3 py-1 border-b ${t.border} flex items-center gap-3`}>
        <div className="flex items-center gap-1.5" onClick={e => { e.stopPropagation(); sorted.length > 0 && (checkedIds.size === sorted.length ? onDeselectAll() : onSelectAll()); }}>
          <Checkbox
            checked={sorted.length > 0 && checkedIds.size === sorted.length}
            className={t.border}
            data-testid="select-all-checkbox"
          />
          <span className={`text-[10px] ${t.textDim} uppercase tracking-wider cursor-pointer select-none`}>
            {checkedIds.size === sorted.length && sorted.length > 0 ? "All" : "Select all"}
          </span>
        </div>
        {filedInView > 0 && onSelectFiled && (
          <>
            <div className={`h-3 w-px ${t.border}`} />
            <button onClick={e => { e.stopPropagation(); onSelectFiled(); }}
              className={`text-[10px] text-teal hover:text-teal font-medium cursor-pointer select-none`}
              data-testid="select-all-filed-checkbox">
              Select all filed ({filedInView})
            </button>
          </>
        )}
        <div className={`h-3 w-px ${t.border}`} />
        <span className={`text-[10px] ${t.textDim} uppercase tracking-wider`}>Sort:</span>
        <button onClick={() => toggleSort("date")}
          className={`text-[11px] font-medium flex items-center gap-1 ${sortBy === "date" ? t.textAccent : t.textMuted}`}
          data-testid="sort-by-date">
          Date {sortBy === "date" && <span className="text-[9px]">{sortDir === "desc" ? "newest" : "oldest"}</span>}
        </button>
        <button onClick={() => toggleSort("from")}
          className={`text-[11px] font-medium flex items-center gap-1 ${sortBy === "from" ? t.textAccent : t.textMuted}`}
          data-testid="sort-by-from">
          From {sortBy === "from" && <span className="text-[9px]">{sortDir === "asc" ? "A-Z" : "Z-A"}</span>}
        </button>
      </div>
      {/* Cleanup progress bar */}
      {cleanupProgress && cleanupRunning && !cleanupConfirm && (
        <div className="px-3 py-2 bg-navy-card border-b border-slate-700/40 space-y-1.5" data-testid="cleanup-progress">
          <div className="flex items-center gap-2">
            <Loader2 className={`w-3.5 h-3.5 animate-spin flex-shrink-0 ${cleanupProgress.phase === "deleting" ? "text-status-red" : "text-teal"}`} />
            <span className={`text-[11px] font-medium flex-1 ${cleanupProgress.phase === "deleting" ? "text-status-red" : "text-teal"}`}>
              {cleanupProgress.step}
            </span>
            <span className="text-[10px] text-slate-400">{cleanupProgress.pct}%</span>
          </div>
          <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-300 ease-out ${cleanupProgress.phase === "deleting" ? "bg-status-red" : "bg-teal"}`}
              style={{ width: `${cleanupProgress.pct}%` }} />
          </div>
        </div>
      )}
      {/* Cleanup confirmation */}
      {cleanupConfirm && (
        <div className="px-3 py-2 bg-teal-dim/20 border-b border-teal/30 space-y-2" data-testid="cleanup-confirm">
          <p className="text-[11px] text-teal">
            Found {cleanupConfirm.count} filed email{cleanupConfirm.count !== 1 ? "s" : ""} in your inbox.
            {cleanupConfirm.preserved > 0 && (
              <span className="text-status-amber ml-1">
                {cleanupConfirm.preserved} kept (latest per thread to you + flagged).
              </span>
            )}
            {" "}Delete {cleanupConfirm.deletable || cleanupConfirm.count} from inbox?
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={onCleanupConfirm}
              className="h-6 px-3 text-[10px] bg-status-red hover:bg-status-red text-white" data-testid="cleanup-confirm-btn"
              disabled={!(cleanupConfirm.deletable || cleanupConfirm.count)}>
              <Trash2 className="w-3 h-3 mr-1" /> Delete {cleanupConfirm.deletable || cleanupConfirm.count} from inbox
            </Button>
            <Button variant="ghost" size="sm" onClick={onCleanupCancel}
              className={`h-6 px-3 text-[10px] ${t.textDim}`} data-testid="cleanup-cancel-btn">
              Cancel
            </Button>
          </div>
        </div>
      )}
      {/* Filed results banner (after scan, showing results) */}
      {filedFilter && !cleanupRunning && !cleanupConfirm && sorted.length > 0 && (
        <div className="px-3 py-1.5 bg-teal-dim/30 border-b border-teal/40 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5 text-teal flex-shrink-0" />
          <span className="text-[11px] text-teal font-medium flex-1">
            {sorted.length} email{sorted.length !== 1 ? "s" : ""} filed to SharePoint
          </span>
          {checkedIds.size === 0 ? (
            <button onClick={() => onSelectAll()} className="text-[10px] text-teal hover:text-teal font-medium" data-testid="select-all-filed-btn">
              Select all → Delete
            </button>
          ) : (
            <span className="text-[10px] text-teal">{checkedIds.size} selected</span>
          )}
        </div>
      )}
      {/* AI Triage bar */}
      <TriageBar messages={sorted} filedEmailIds={filedEmailIds} onTriageNow={onTriageNow} t={t} />
      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        {loading && messages.length === 0 && (
          <div className="flex items-center justify-center py-12"><Loader2 className={`w-5 h-5 animate-spin ${t.textAccentDim}`} /></div>
        )}
        {!loading && sorted.length === 0 && (
          <div className={`text-center ${t.textDim} py-12 text-sm`}>{searchTerm ? "No matching messages" : "No messages"}</div>
        )}
        {sorted.map(m => (
          <div key={m.id}
            draggable
            onDragStart={e => handleDragStart(e, m)}
            onClick={() => onSelect(m.id)}
            onContextMenu={e => handleContextMenu(e, m)}
            className={`group flex items-start gap-2.5 px-3 py-2.5 cursor-pointer border-b ${t.borderSub} transition-colors
              ${m.id === selectedId ? `${t.msgSelected} border-l-2 border-l-current ${t.textAccent}` : `border-l-2 border-l-transparent ${t.bgHover}`}
              ${!m.is_read ? t.unreadBg : ""}`}
            data-testid={`message-item-${m.id.substring(0, 8)}`}>
            <div className="pt-0.5" onClick={e => { e.stopPropagation(); onToggleCheck(m.id); }}>
              <Checkbox checked={checkedIds.has(m.id)} className={t.border} />
            </div>
            <div className="flex flex-col items-center pt-1.5">
              {!m.is_read ? <div className={`w-2 h-2 rounded-full ${t.unreadDot} flex-shrink-0`} /> : <div className="w-2 h-2" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-1">
                <span className={`text-[13px] truncate ${!m.is_read ? `font-semibold ${t.text}` : t.textSec}`}>
                  {m.from_name || m.from_email}
                </span>
                <span className={`text-[10px] ${t.textDim} flex-shrink-0`}>{fmtDate(m.received)}</span>
              </div>
              <div className={`text-xs truncate mt-0.5 ${!m.is_read ? `${t.text} font-medium` : t.textMuted}`}>{m.subject}</div>
              <div className={`text-[11px] ${t.textDim} truncate mt-0.5`}>{m.preview}</div>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <StatusBadges badges={computeEmailStatus(m, filedEmailIds)} compact t={t} />
                {m._source === "deep_scan" && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-blue-900/30 text-blue-400 text-[9px] font-medium" title="Found via SharePoint cross-reference (no tag)">
                    untagged
                  </span>
                )}
                {m._preserved && (
                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full bg-status-amber-dim/30 text-status-amber text-[9px] font-medium" title={`${m._preserve_reason} — will not be deleted`}>
                    {m._preserve_reason || "Kept"}
                  </span>
                )}
                {m.matter_reference && filedFilter && (
                  <span className={`text-[9px] ${t.textDim} truncate max-w-[120px]`} title={m.matter_reference}>{m.matter_reference}</span>
                )}
                {m.has_attachments && <Paperclip className={`w-3 h-3 ${t.textDim}`} />}
                {m.importance === "high" && <AlertCircle className="w-3 h-3 text-status-red" />}
                {m.flagged && <Star className="w-3 h-3 text-status-amber fill-amber-400" />}
              </div>
            </div>
            {/* Quick actions on hover */}
            <div className="hidden group-hover:flex items-center gap-0.5 flex-shrink-0 pt-1" onClick={e => e.stopPropagation()}>
              <button onClick={() => onDelete(m.id)} className="p-1 rounded hover:bg-status-red-dim/20 text-status-red/60 hover:text-status-red" title="Delete"
                data-testid={`quick-delete-${m.id.substring(0, 8)}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onArchive(m.id)} className={`p-1 rounded ${t.ribbonHover} ${t.textDim}`} title="Archive"
                data-testid={`quick-archive-${m.id.substring(0, 8)}`}>
                <Archive className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => onFlag(m.id)} className="p-1 rounded hover:bg-status-amber-dim/20 text-status-amber/60 hover:text-status-amber" title="Flag"
                data-testid={`quick-flag-${m.id.substring(0, 8)}`}>
                <Flag className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
        {hasMore && !searchTerm && (
          <button onClick={onLoadMore}
            className={`w-full py-3 text-xs ${t.textAccentDim} ${t.bgHover} font-medium`}
            data-testid="load-more-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Load more..."}
          </button>
        )}
      </div>
      {checkedIds.size > 0 && (
        <div className={`px-3 py-1.5 border-t ${t.border} ${t.bgPanel} text-[11px] ${t.textAccentDim} font-medium flex items-center gap-2`}>
          <span>{checkedIds.size} of {sorted.length} selected</span>
          {checkedIds.size < sorted.length && <button onClick={onSelectAll} className={`${t.textAccent} underline`} data-testid="select-rest-btn">select all {sorted.length}</button>}
          <button onClick={onDeselectAll} className={`${t.textDim} underline`} data-testid="deselect-all-btn">clear</button>
        </div>
      )}
      {/* Right-click context menu */}
      {ctxMenu && (
        <div className={`fixed z-50 ${t.bgCard} border ${t.border} rounded-lg shadow-xl py-1 min-w-[200px]`}
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          data-testid="message-context-menu">
          <button onClick={() => { onFindConversation(ctxMenu.msg); setCtxMenu(null); }}
            className={`w-full text-left px-3 py-2 text-xs ${t.textSec} ${t.bgHover} flex items-center gap-2.5`}
            data-testid="ctx-find-conversation">
            <MessageSquare className="w-3.5 h-3.5 text-blue-400" />
            <span>Find all in conversation</span>
          </button>
          <button onClick={() => { onFindFromSender(ctxMenu.msg); setCtxMenu(null); }}
            className={`w-full text-left px-3 py-2 text-xs ${t.textSec} ${t.bgHover} flex items-center gap-2.5`}
            data-testid="ctx-find-sender">
            <Users className="w-3.5 h-3.5 text-teal" />
            <span>Find all from {ctxMenu.msg.from_name || ctxMenu.msg.from_email}</span>
          </button>
          <div className={`my-1 h-px ${t.border}`} />
          <button onClick={() => { onFileSelected(ctxMenu.msg); setCtxMenu(null); }}
            className={`w-full text-left px-3 py-2 text-xs ${t.textSec} ${t.bgHover} flex items-center gap-2.5`}
            data-testid="ctx-file-to-sp">
            <FolderUp className="w-3.5 h-3.5 text-status-amber" />
            <span>File to SharePoint</span>
          </button>
          <button onClick={() => { onDelete(ctxMenu.msg.id); setCtxMenu(null); }}
            className={`w-full text-left px-3 py-2 text-xs text-status-red ${t.bgHover} flex items-center gap-2.5`}
            data-testid="ctx-delete">
            <Trash2 className="w-3.5 h-3.5" />
            <span>Delete</span>
          </button>
          <button onClick={() => { onArchive(ctxMenu.msg.id); setCtxMenu(null); }}
            className={`w-full text-left px-3 py-2 text-xs ${t.textSec} ${t.bgHover} flex items-center gap-2.5`}
            data-testid="ctx-archive">
            <Archive className="w-3.5 h-3.5" />
            <span>Archive</span>
          </button>
          <div className={`my-1 h-px ${t.border}`} />
          {/* Enhancement 1: Move option with inline folder picker */}
          <div className="relative">
            <button onClick={(e) => { e.stopPropagation(); setCtxMoveOpen(prev => !prev); }}
              className={`w-full text-left px-3 py-2 text-xs ${t.textSec} ${t.bgHover} flex items-center gap-2.5`}
              data-testid="ctx-move">
              <FolderInput className="w-3.5 h-3.5 text-blue-400" />
              <span>Move</span>
              <ChevronRight className="w-3 h-3 ml-auto" />
            </button>
            {ctxMoveOpen && folders && (
              <div className={`absolute left-full top-0 ml-1 ${t.bgCard} border ${t.border} rounded-lg shadow-xl py-1 min-w-[180px] max-h-[200px] overflow-y-auto z-[51]`}
                onClick={e => e.stopPropagation()} data-testid="ctx-move-picker">
                {folders.filter(f => f.name.toLowerCase() !== "deleted items").slice(0, 15).map(f => (
                  <button key={f.id} onClick={() => { onMove(f.id, ctxMenu.msg.id); setCtxMenu(null); setCtxMoveOpen(false); }}
                    className={`w-full text-left px-3 py-1.5 text-xs ${t.textSec} ${t.bgHover}`}>
                    {f.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Enhancement 1: Mark as Read / Unread */}
          <button onClick={() => {
            if (onMarkRead) onMarkRead(ctxMenu.msg.id, !ctxMenu.msg.is_read);
            setCtxMenu(null);
          }}
            className={`w-full text-left px-3 py-2 text-xs ${t.textSec} ${t.bgHover} flex items-center gap-2.5`}
            data-testid="ctx-toggle-read">
            {ctxMenu.msg.is_read
              ? <><EyeOff className="w-3.5 h-3.5 text-gray-400" /><span>Mark as Unread</span></>
              : <><Eye className="w-3.5 h-3.5 text-blue-400" /><span>Mark as Read</span></>
            }
          </button>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// EMAIL PREVIEW
// ═══════════════════════════════════════════════════════════════════
function EmailPreview({ email, loading, t, theme, filingStatus, onClearFiledTag }) {
  if (loading) return <div className={`flex-1 flex items-center justify-center ${t.bgPreview}`}><Loader2 className={`w-6 h-6 animate-spin ${t.textAccentDim}`} /></div>;
  if (!email) return (
    <div className={`flex-1 flex flex-col items-center justify-center ${t.bgPreview} ${t.textDim}`} data-testid="no-email-selected">
      <Mail className="w-10 h-10 mb-3 opacity-20" />
      <p className="text-sm">Select an email to read</p>
    </div>
  );
  return (
    <div className={`flex-1 flex flex-col ${t.bgPreview} overflow-hidden`} data-testid="email-preview">
      {/* Filing status banner */}
      {filingStatus && (
        <div className="flex items-center gap-2 px-4 py-2 bg-teal-dim/30 border-b border-teal/40" data-testid="filing-status-banner">
          <CheckCircle2 className="w-4 h-4 text-teal flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-xs text-teal font-medium">Filed to {filingStatus.matter_reference || "SharePoint"}</span>
            <span className="text-xs text-teal/60 ml-2">
              by {filingStatus.filed_by || "unknown"} on {filingStatus.filed_at ? new Date(filingStatus.filed_at).toLocaleDateString() : "unknown"}
            </span>
          </div>
          {filingStatus.email_file_url && (
            <a href={filingStatus.email_file_url} target="_blank" rel="noreferrer" className="text-[10px] text-teal hover:text-teal underline flex-shrink-0">View in SP</a>
          )}
          <button onClick={onClearFiledTag} className="text-[10px] text-status-red hover:text-status-red flex-shrink-0 px-1.5 py-0.5 rounded hover:bg-status-red-dim/30 transition-colors" data-testid="clear-filed-tag-btn" title="Remove Filed tag from this email">Clear tag</button>
        </div>
      )}
      <div className={`px-5 py-4 border-b ${t.border}`}>
        <h2 className={`text-base font-semibold ${t.text} mb-2`}>{email.subject}</h2>
        <div className="flex items-center gap-2 text-sm">
          <div className={`w-8 h-8 rounded-full ${t.bgActive} flex items-center justify-center ${t.textAccent} text-xs font-bold flex-shrink-0`}>
            {(email.from_name || email.from_email || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className={`font-medium ${t.text} text-[13px]`}>{email.from_name}</span>
              <span className={`${t.textDim} text-xs`}>&lt;{email.from_email}&gt;</span>
            </div>
            {email.to?.length > 0 && (
              <div className={`text-[11px] ${t.textDim} truncate`}>To: {email.to.map(r => r.name || r.email).join(", ")}</div>
            )}
          </div>
          <span className={`${t.textDim} ml-auto text-xs flex-shrink-0`}>{new Date(email.received).toLocaleString()}</span>
        </div>
        {email.cc?.length > 0 && (
          <div className={`text-[11px] ${t.textDim} mt-1 pl-10`}>Cc: {email.cc.map(r => r.name || r.email).join(", ")}</div>
        )}
        {email.attachments?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3 pl-10">
            {email.attachments.map(a => (
              <Badge 
                key={a.id} 
                variant="outline" 
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("application/json", JSON.stringify({ 
                    type: "attachment", 
                    attachment: { id: a.id, name: a.name, contentType: a.contentType },
                    emailId: email.id 
                  }));
                  e.dataTransfer.effectAllowed = "copy";
                  e.stopPropagation();
                }}
                className={`text-[10px] ${t.border} ${t.textMuted} gap-1 ${t.bgInput} cursor-grab active:cursor-grabbing hover:border-amber-500/50 hover:bg-status-amber-dim/20 transition-colors`}
                title="Drag to a subfolder to save"
              >
                <Paperclip className="w-2.5 h-2.5" />{a.name} ({fmtSize(a.size)})
              </Badge>
            ))}
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-1">
        <iframe
          srcDoc={`<html><head><style>
            html,body{font-family:'Segoe UI',system-ui,sans-serif;font-size:13px;color:${t.previewBodyText} !important;background:${t.previewBodyBg} !important;margin:12px;word-wrap:break-word;line-height:1.5}
            body *,body *[style]{color:${t.previewBodyText} !important;background-color:transparent !important}
            a,a *{color:${theme === "dark" ? "#34d399" : "#2563eb"} !important}
            img{max-width:100%}
            blockquote{border-left:3px solid ${theme === "dark" ? "#555" : "#ccc"};margin:8px 0;padding:4px 12px}
            table,tr,td,th{background:transparent !important;border-color:${theme === "dark" ? "#444" : "#ddd"} !important}
            td,th{color:${t.previewBodyText} !important}
            p,div,span,font,b,i,u,s,strong,em,li,h1,h2,h3,h4,h5,h6,label,pre,code{color:inherit !important;background-color:transparent !important}
            hr{border-color:${theme === "dark" ? "#333" : "#ddd"} !important}
          </style></head><body>${email.body_html || ""}</body></html>`}
          className="w-full h-full border-0" sandbox="allow-same-origin" title="Email body" data-testid="email-body-frame"
        />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FILING SIDEBAR
// ═══════════════════════════════════════════════════════════════════
function FilingSidebar({ email, onFileToSharePoint, onFileConversation, onDelete, onMove, filingInProgress, filingProgressMsg, checkedCount, folders, t, draggedMsgIds, onDropFiled, selectedMatter, setSelectedMatter, onCreateTodo, onCreateCalendarEvent }) {
  const [matterSearch, setMatterSearch] = useState("");
  const [matterResults, setMatterResults] = useState([]);
  const [searchingMatters, setSearchingMatters] = useState(false);
  const [subfolders, setSubfolders] = useState([]);
  const [loadingSubfolders, setLoadingSubfolders] = useState(false);
  const [emailTargetFolder, setEmailTargetFolder] = useState(null);
  const [attachTargetFolder, setAttachTargetFolder] = useState(null);
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [dropHover, setDropHover] = useState(false);
  const [showAttachPrompt, setShowAttachPrompt] = useState(false);
  const [pendingFileOpts, setPendingFileOpts] = useState(null);
  // Attachment subfolder browser state
  const [attachBrowsePath, setAttachBrowsePath] = useState([]); // breadcrumb [{id, name}]
  const [attachBrowseChildren, setAttachBrowseChildren] = useState([]);
  const [attachBrowseLoading, setAttachBrowseLoading] = useState(false);
  const [fileLikeLast, setFileLikeLast] = useState(null);
  const [parsedFromSubject, setParsedFromSubject] = useState(false);
  const [recentDestinations, setRecentDestinations] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [pinnedMatterIds, setPinnedMatterIds] = useState(() => { try { return JSON.parse(localStorage.getItem("bonsai_pinned_matters") || "[]"); } catch { return []; } });
  const [recentMatters, setRecentMatters] = useState([]);
  const [filingConversation, setFilingConversation] = useState(false);
  const togglePinMatter = (matterId) => {
    setPinnedMatterIds(prev => {
      const next = prev.includes(matterId) ? prev.filter(id => id !== matterId) : [...prev, matterId];
      localStorage.setItem("bonsai_pinned_matters", JSON.stringify(next));
      return next;
    });
  };
  const [deleteAfterFiling, setDeleteAfterFiling] = useState(() => {
    try { return localStorage.getItem("bonsai_delete_after_filing") === "true"; } catch { return false; }
  });
  // LawBillity Time Recording state
  const [showTimePanel, setShowTimePanel] = useState(false);
  const [timeNarrative, setTimeNarrative] = useState("");
  const [timeDuration, setTimeDuration] = useState(0.1);
  const [timeDate, setTimeDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [timeMatterRef, setTimeMatterRef] = useState("");
  const [timeTemplate, setTimeTemplate] = useState("");
  const [generatingNarrative, setGeneratingNarrative] = useState(false);
  const [savingTime, setSavingTime] = useState(false);
  const [timeResult, setTimeResult] = useState(null); // { type: "success"|"error", message }
  const [aiDurationHint, setAiDurationHint] = useState(null);
  const [todayTime, setTodayTime] = useState({ total: 0, entries: [] });
  const [loadingTodayTime, setLoadingTodayTime] = useState(false);
  const userId = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id");
  const headers = useMemo(() => userId ? { "X-Bonsai-User-Id": userId } : {}, [userId]);

  // Load File Like Last, recent destinations, favorites, recent matters on mount
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("bonsai_recent_filings") || "[]");
      if (stored.length > 0) setFileLikeLast(stored[0]);
      setRecentDestinations(stored.slice(0, 5));
    } catch {}
    try {
      setRecentMatters(JSON.parse(localStorage.getItem("bonsai_recent_matters") || "[]").slice(0, 5));
    } catch {}
    // Also fetch server-backed File Like Last (cross-platform)
    if (userId) {
      axios.get(`${API}/bonsai/filing/recent-destinations`, { params: { limit: 1 }, headers }).then(r => {
        const dests = r.data?.destinations || [];
        if (dests.length > 0 && !fileLikeLast) {
          const d = dests[0];
          setFileLikeLast({ driveId: d.drive_id, itemId: d.item_id || d.folder_id, folderId: d.folder_id, folderName: d.folder_name || d.matter_reference, matterRef: d.matter_reference || d.folder_name });
        }
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/bonsai/sharepoint/favorites`, { headers }).then(r => {
      setFavorites((r.data?.items || []).slice(0, 5));
    }).catch(() => {});
  }, [userId]);

  // Save to recent matters when a matter is selected
  const saveRecentMatter = (matter) => {
    try {
      const stored = JSON.parse(localStorage.getItem("bonsai_recent_matters") || "[]");
      const filtered = stored.filter(m => (m.id || m.itemId) !== (matter.id || matter.itemId));
      filtered.unshift({ id: matter.id, itemId: matter.itemId, driveId: matter.driveId, reference: matter.reference, title: matter.title, client_name: matter.client_name });
      localStorage.setItem("bonsai_recent_matters", JSON.stringify(filtered.slice(0, 10)));
      setRecentMatters(filtered.slice(0, 5));
    } catch {}
  };

  // Save to recent filings after successful file
  const saveRecentFiling = (matter, folder) => {
    try {
      const entry = { driveId: matter.driveId, itemId: matter.itemId || matter.id, folderId: folder.id, folderName: folder.name, matterRef: matter.reference || matter.title, timestamp: Date.now() };
      const stored = JSON.parse(localStorage.getItem("bonsai_recent_filings") || "[]");
      const filtered = stored.filter(e => e.folderId !== folder.id);
      filtered.unshift(entry);
      localStorage.setItem("bonsai_recent_filings", JSON.stringify(filtered.slice(0, 10)));
      setFileLikeLast(entry);
      setRecentDestinations(filtered.slice(0, 5));
    } catch {}
  };

  const searchMatters = useCallback(async (q) => {
    if (!q || q.length < 2) { setMatterResults([]); return; }
    setSearchingMatters(true);
    try {
      const res = await axios.get(`${API}/bonsai/filing/matters`, { params: { q }, headers });
      setMatterResults(Array.isArray(res.data) ? res.data : res.data.matters || []);
    } catch { } finally { setSearchingMatters(false); }
  }, []);

  useEffect(() => { const tm = setTimeout(() => searchMatters(matterSearch), 500); return () => clearTimeout(tm); }, [matterSearch, searchMatters]);

  const loadSubfolders = useCallback(async (matter) => {
    if (!matter?.driveId || !matter?.itemId) return;
    setLoadingSubfolders(true); setSubfolders([]); setEmailTargetFolder(null); setAttachTargetFolder(null);
    setAttachBrowsePath([]); setAttachBrowseChildren([]);
    try {
      const res = await axios.get(`${API}/bonsai/mail/matters/${matter.driveId}/${matter.itemId}/subfolders`, { headers });
      const subs = res.data.subfolders || [];
      setSubfolders(subs);
      applyAttendancesDefault(subs);
    } catch { toast.error("Failed to load matter subfolders"); } finally { setLoadingSubfolders(false); }
  }, []);

  // Reusable helper: auto-select Attendances folder + docs prompt for attachments
  // Always file to Attendances — Correspondence folder is legacy, do not use
  const applyAttendancesDefault = useCallback((subs) => {
    const att = subs.find(s => s.name.toLowerCase().includes("attendan"));
    const doc = subs.find(s => s.name.toLowerCase().includes("doc"));
    if (att) setEmailTargetFolder(att); else if (subs[0]) setEmailTargetFolder(subs[0]);
    if (doc) setAttachTargetFolder(doc);
    else {
      const drf = subs.find(s => s.name.toLowerCase() === "drafts");
      if (drf) setAttachTargetFolder(drf);
    }
    return { emailFolder: att || subs[0] || null, docsFolder: doc || null };
  }, []);

  // Browse into attachment subfolder to pick deeper destinations
  const browseAttachFolder = useCallback(async (folder) => {
    if (!selectedMatter?.driveId) return;
    setAttachBrowseLoading(true);
    try {
      const res = await axios.get(`${API}/hub/file-mover/folder-children`, {
        params: { drive_id: selectedMatter.driveId, folder_id: folder.id }, headers
      });
      const folders = (res.data.items || []).filter(i => i.is_folder);
      setAttachBrowseChildren(folders);
      setAttachBrowsePath(prev => [...prev, { id: folder.id, name: folder.name }]);
      setAttachTargetFolder(folder); // default select to this folder
    } catch { toast.error("Failed to browse folder"); }
    setAttachBrowseLoading(false);
  }, [selectedMatter, headers]);

  const attachBrowseBack = useCallback(() => {
    if (attachBrowsePath.length <= 1) {
      // Back to top-level subfolders
      setAttachBrowsePath([]);
      setAttachBrowseChildren([]);
      const doc = subfolders.find(s => s.name.toLowerCase().includes("document"));
      if (doc) setAttachTargetFolder(doc);
    } else {
      setAttachBrowsePath(prev => prev.slice(0, -1));
      // Re-browse the parent
      const parent = attachBrowsePath[attachBrowsePath.length - 2];
      browseAttachFolder({ id: parent.id, name: parent.name });
      setAttachBrowsePath(prev => prev.slice(0, -1)); // undo the push from browseAttachFolder
    }
  }, [attachBrowsePath, subfolders, browseAttachFolder]);

  const fetchAiSuggest = useCallback(async () => {
    if (!email) return;
    setLoadingAi(true);
    try {
      const res = await axios.get(`${API}/bonsai/mail/messages/${encodeURIComponent(email.id)}/suggest`, { headers });
      setAiSuggestion(res.data);
      if (res.data?.ai_suggestion?.matter_reference) {
        setMatterSearch(res.data.ai_suggestion.matter_reference);
        toast.success(`AI suggests: ${res.data.ai_suggestion.matter_reference}`);
      } else if (res.data?.history_match) {
        setMatterSearch(res.data.history_match.matter_reference || "");
        toast.info("Found match from filing history");
      } else {
        toast.info("No AI suggestion available for this email");
      }
    } catch (e) {
      console.error("AI suggest error:", e);
      toast.error(errorMessage(e, "Failed to get AI suggestion"));
    } finally { setLoadingAi(false); }
  }, [email, headers]);

  useEffect(() => {
    setSelectedMatter(null); setMatterSearch(""); setAiSuggestion(null);
    setSubfolders([]); setEmailTargetFolder(null); setAttachTargetFolder(null);
    setParsedFromSubject(false);
    // Enhancement 3: Auto-parse matter reference from subject line
    if (email?.subject) {
      const match = email.subject.match(/BL\.\d{2}\.\d{3}\.\d{4}/i);
      if (match) {
        setMatterSearch(match[0]);
        setParsedFromSubject(true);
        const parseTimer = setTimeout(() => searchMatters(match[0]), 300);
        // Also trigger AI suggestion
        const aiTimer = setTimeout(() => fetchAiSuggest(), 800);
        return () => { clearTimeout(parseTimer); clearTimeout(aiTimer); };
      }
    }
    // Auto-trigger AI suggestion when a new email is selected
    if (email?.id) {
      const timer = setTimeout(() => fetchAiSuggest(), 500);
      return () => clearTimeout(timer);
    }
  }, [email?.id]);

  const [diagnosticResult, setDiagnosticResult] = useState(null);
  const [runningDiagnostic, setRunningDiagnostic] = useState(false);

  // Auto email insight (category badge + deadline banner) - feature parity with Outlook add-in
  const [emailInsight, setEmailInsight] = useState(null);
  const [loadingInsight, setLoadingInsight] = useState(false);
  const insightCacheRef = useRef({});

  // Server-backed recently filed
  const [serverRecentFilings, setServerRecentFilings] = useState([]);

  // Morning digest summary
  const [digestSummary, setDigestSummary] = useState(null);

  // Auto-fetch email insight (category + deadlines) when email changes
  useEffect(() => {
    if (!email?.id) { setEmailInsight(null); return; }
    // Check local cache first
    if (insightCacheRef.current[email.id]) {
      setEmailInsight(insightCacheRef.current[email.id]);
      return;
    }
    // Check server cache (fast, no AI call)
    const controller = new AbortController();
    (async () => {
      try {
        const res = await axios.get(`${API}/bonsai/mail/messages/${encodeURIComponent(email.id)}/quick-insight`, { headers, signal: controller.signal });
        if (res.data?.cached) {
          insightCacheRef.current[email.id] = res.data;
          setEmailInsight(res.data);
        } else {
          // No cache — trigger full AI assist in background
          setLoadingInsight(true);
          const full = await axios.post(`${API}/bonsai/mail/messages/${encodeURIComponent(email.id)}/ai-assist`, {}, { headers, signal: controller.signal });
          const insight = { cached: true, category: full.data?.category, deadlines: full.data?.deadlines || [], tasks: full.data?.tasks || [] };
          insightCacheRef.current[email.id] = insight;
          setEmailInsight(insight);
          setLoadingInsight(false);
        }
      } catch (e) {
        if (e.name !== "CanceledError") setLoadingInsight(false);
      }
    })();
    return () => controller.abort();
  }, [email?.id]);

  // Load server-backed recent filings on mount
  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/bonsai/filing/history`, { params: { limit: 5 }, headers }).then(r => {
      setServerRecentFilings((r.data?.filings || []).slice(0, 5));
    }).catch(() => {});
  }, [userId]);

  // Load morning digest summary on mount
  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/bonsai/mail/digest-summary`, { headers }).then(r => {
      if (r.data?.available) setDigestSummary(r.data);
    }).catch(() => {});
  }, [userId]);

  const toggleDeleteAfterFiling = (val) => {
    setDeleteAfterFiling(val);
    try { localStorage.setItem("bonsai_delete_after_filing", val ? "true" : "false"); } catch {}
  };

  // Update time matter ref when selectedMatter changes
  useEffect(() => {
    if (selectedMatter?.reference) setTimeMatterRef(selectedMatter.reference);
    else if (selectedMatter?.title) setTimeMatterRef(selectedMatter.title);
  }, [selectedMatter]);

  // Update time date from email
  useEffect(() => {
    if (email?.received) {
      try { setTimeDate(new Date(email.received).toISOString().split("T")[0]); } catch {}
    }
  }, [email?.id]);

  // Fetch today's time entries
  const fetchTodayTime = useCallback(async () => {
    setLoadingTodayTime(true);
    try {
      const today = new Date().toISOString().split("T")[0];
      const res = await axios.get(`${API}/bonsai/time/recent?date=${today}`, { headers });
      const entries = res.data?.entries || res.data || [];
      const total = entries.reduce((sum, e) => sum + (e.duration || e.hours || 0), 0);
      setTodayTime({ total: Math.round(total * 100) / 100, entries: entries.slice(0, 5) });
    } catch { setTodayTime({ total: 0, entries: [] }); }
    finally { setLoadingTodayTime(false); }
  }, [headers]);

  useEffect(() => { fetchTodayTime(); }, [fetchTodayTime]);

  const generateAINarrative = async (tpl) => {
    if (!email) return;
    setGeneratingNarrative(true); setTimeNarrative(""); setAiDurationHint(null);
    try {
      const res = await axios.post(`${API}/bonsai/time/ai-narrative`, {
        subject: email.subject || "",
        body: (email.body_html || "").replace(/<[^>]*>/g, "").substring(0, 2000),
        from_address: email.from_email || "",
        email_id: email.id || "",
        template: tpl || timeTemplate || "",
      }, { headers });
      if (res.data.narrative) setTimeNarrative(res.data.narrative);
      if (res.data.suggested_duration > 0) {
        setTimeDuration(res.data.suggested_duration);
        setAiDurationHint(res.data.suggested_duration);
      }
      if (res.data.suggested_matter_reference && !timeMatterRef) {
        setTimeMatterRef(res.data.suggested_matter_reference);
      }
    } catch {
      // Fallback narrative
      const fromName = (email.from_name || email.from_email || "").split("@")[0].replace(/\./g, " ");
      const subj = (email.subject || "").substring(0, 60);
      const t2 = tpl || timeTemplate;
      if (t2 === "considering") setTimeNarrative(`Reviewing and considering your correspondence re ${subj}.`);
      else if (t2 === "drafting") setTimeNarrative(`Drafting response to you re ${subj}.`);
      else if (t2 === "reviewing") setTimeNarrative(`Reviewing your documents re ${subj}.`);
      else if (t2 === "attendance") setTimeNarrative(`Telephone attendance with you re ${subj}.`);
      else setTimeNarrative(`Reviewing your correspondence re ${subj}.`);
    } finally { setGeneratingNarrative(false); }
  };

  const handleSetTemplate = (tpl) => {
    setTimeTemplate(tpl);
    generateAINarrative(tpl);
  };

  const saveTimeEntry = async () => {
    if (!timeMatterRef) { setTimeResult({ type: "error", message: "Enter a matter reference" }); return; }
    if (!timeDuration || timeDuration <= 0) { setTimeResult({ type: "error", message: "Enter a valid duration" }); return; }
    if (!timeNarrative.trim()) { setTimeResult({ type: "error", message: "Enter a narrative" }); return; }
    setSavingTime(true); setTimeResult(null);
    try {
      const res = await axios.post(`${API}/bonsai/time/lawbillity`, {
        matter_reference: timeMatterRef,
        duration: timeDuration,
        narrative: timeNarrative.trim(),
        date: timeDate,
      }, { headers });
      if (res.status === 200) {
        setTimeResult({ type: "success", message: `${timeDuration}h recorded for ${timeMatterRef}` });
        toast.success(`Time recorded: ${timeDuration}h`);
        fetchTodayTime(); // refresh widget
      }
    } catch (e) {
      setTimeResult({ type: "error", message: errorMessage(e, "Failed to save time entry") });
    } finally { setSavingTime(false); }
  };

  // Handle drop on subfolder button (for emails or attachments)
  const [dropTarget, setDropTarget] = useState(null);
  
  const handleSubfolderDrop = async (e, subfolder) => {
    e.preventDefault();
    setDropTarget(null);
    
    try {
      const data = JSON.parse(e.dataTransfer.getData("application/json"));
      
      if (data.type === "emails") {
        // File emails to this subfolder
        if (!selectedMatter?.driveId) {
          toast.error("Select a matter first");
          return;
        }
        toast.info(`Filing ${data.ids.length} email(s) to ${subfolder.name}...`);
        onFileToSharePoint({
          driveId: selectedMatter.driveId,
          folderId: subfolder.id,
          emailIds: data.ids,
          includeAttachments: false,
          deleteAfterFiling
        });
      } else if (data.type === "attachment") {
        // File single attachment to this subfolder
        if (!selectedMatter?.driveId) {
          toast.error("Select a matter first");
          return;
        }
        toast.info(`Saving ${data.attachment.name} to ${subfolder.name}...`);
        try {
          await axios.post(`${API}/bonsai/mail/messages/${encodeURIComponent(data.emailId)}/attachments/${encodeURIComponent(data.attachment.id)}/save`, {
            drive_id: selectedMatter.driveId,
            folder_id: subfolder.id,
            filename: data.attachment.name
          }, { headers });
          toast.success(`Saved ${data.attachment.name} to ${subfolder.name}`);
        } catch (err) {
          toast.error(errorMessage(err, "Failed to save attachment"));
        }
      }
    } catch (err) {
      console.error("Drop error:", err);
    }
  };

  const handleSubfolderDragOver = (e, subfolder) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setDropTarget(subfolder.id);
  };

  const handleSubfolderDragLeave = () => {
    setDropTarget(null);
  };

  const handleFile = () => {
    if (!selectedMatter || !emailTargetFolder) { toast.error("Select a matter and target folder"); return; }
    if (!selectedMatter.driveId) { toast.error("Matter is missing drive ID — try selecting it from search results again"); return; }
    if (!emailTargetFolder.id) { toast.error("Target folder is missing — try selecting a subfolder again"); return; }
    const opts = {
      driveId: selectedMatter.driveId, folderId: emailTargetFolder.id,
      includeAttachments: includeAttachments && email?.attachments?.length > 0,
      attachmentDriveId: selectedMatter.driveId, attachmentFolderId: attachTargetFolder?.id,
      deleteAfterFiling,
    };
    // Batch filing (multiple emails) — skip attachment prompt, file directly
    if (checkedCount > 1) {
      onFileToSharePoint(opts);
      saveRecentFiling(selectedMatter, emailTargetFolder);
      return;
    }
    // Single email — show attachment dialog if it has attachments and user hasn't been prompted
    if (email?.attachments?.length > 0 && !showAttachPrompt) {
      setPendingFileOpts(opts);
      setShowAttachPrompt(true);
      return;
    }
    onFileToSharePoint(opts);
    setShowAttachPrompt(false);
    setPendingFileOpts(null);
    saveRecentFiling(selectedMatter, emailTargetFolder);
  };

  const handleFileConversation = async () => {
    if (!email?.conversationId || !selectedMatter?.driveId || !emailTargetFolder?.id) {
      toast.error("Select an email and filing destination first");
      return;
    }
    setFilingConversation(true);
    try {
      const res = await axios.post(`${API}/bonsai/filing/conversation`, {
        conversation_id: email.conversationId,
        // PR #466 — "File conversation" button click in the
        // EmailPanel chrome (handleFileConversation). Click = click_file.
        user_intent: 'click_file',
        subject: email.subject || "",
        drive_id: selectedMatter.driveId,
        folder_id: emailTargetFolder.id,
        include_attachments: includeAttachments,
        attachment_drive_id: includeAttachments ? selectedMatter.driveId : undefined,
        attachment_folder_id: includeAttachments ? attachTargetFolder?.id : undefined,
      }, { headers });
      if (res.data?.batch_id) {
        toast.success(`Filing ${res.data.total || "all"} messages in conversation...`);
        saveRecentFiling(selectedMatter, emailTargetFolder);
        // Poll for progress
        onFileConversation?.(res.data);
      } else {
        toast.error("Failed to start conversation filing");
      }
    } catch (e) {
      toast.error(errorMessage(e, "Failed to file conversation"));
    } finally {
      setFilingConversation(false);
    }
  };

  // Enhancement 6: File and Reply
  const handleFileAndReply = async () => {
    if (!selectedMatter || !emailTargetFolder) { toast.error("Select a matter and target folder"); return; }
    if (!email) { toast.error("No email selected"); return; }
    // Apply attendances default
    if (subfolders.length > 0) applyAttendancesDefault(subfolders);
    const opts = {
      driveId: selectedMatter.driveId, folderId: emailTargetFolder.id,
      includeAttachments: includeAttachments && email?.attachments?.length > 0,
      attachmentDriveId: selectedMatter.driveId, attachmentFolderId: attachTargetFolder?.id,
      deleteAfterFiling,
      _onSuccess: () => {
        // Open reply compose after successful filing
        if (typeof window.__bonsaiOpenReply === "function") window.__bonsaiOpenReply(email);
      },
    };
    onFileToSharePoint(opts);
    saveRecentFiling(selectedMatter, emailTargetFolder);
  };

  const confirmFileWithAttachments = (includeAtts, attFolder) => {
    const opts = pendingFileOpts || {
      driveId: selectedMatter.driveId, folderId: emailTargetFolder.id,
    };
    opts.includeAttachments = includeAtts;
    opts.deleteAfterFiling = deleteAfterFiling;
    if (includeAtts && attFolder) {
      opts.attachmentDriveId = selectedMatter.driveId;
      opts.attachmentFolderId = attFolder.id;
    }
    setShowAttachPrompt(false);
    setPendingFileOpts(null);
    onFileToSharePoint(opts);
    saveRecentFiling(selectedMatter, emailTargetFolder);
  };

  const runFilingDiagnostic = async () => {
    if (!selectedMatter?.driveId || !emailTargetFolder?.id) { toast.error("Select matter + folder first"); return; }
    const ids = draggedMsgIds?.length > 0 ? draggedMsgIds : [];
    if (ids.length === 0 && email?.id) ids.push(email.id);
    if (ids.length === 0) { toast.error("No email to test with"); return; }
    setRunningDiagnostic(true); setDiagnosticResult(null);
    try {
      const res = await axios.post(`${API}/bonsai/mail/filing-diagnostics`, {
        message_ids: ids.slice(0, 1), drive_id: selectedMatter.driveId, folder_id: emailTargetFolder.id,
      }, { headers });
      setDiagnosticResult(res.data);
      if (res.data.overall === "PASS") toast.success("All filing checks passed");
      else toast.error(`Filing would fail at: ${res.data.fail_step}`);
    } catch (e) {
      const msg = errorMessage(e, "Filing diagnostics failed");
      setDiagnosticResult({ overall: "ERROR", error: msg });
      toast.error(`Diagnostic failed: ${msg}`);
    } finally { setRunningDiagnostic(false); }
  };

  // Drop handler — accepts dragged emails from message list
  const handleSidebarDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setDropHover(true); };
  const handleSidebarDragLeave = () => setDropHover(false);

  // Prevent browser from opening/downloading dragged files anywhere on the page
  useEffect(() => {
    const preventDefault = (e) => { e.preventDefault(); };
    document.addEventListener("dragover", preventDefault);
    document.addEventListener("drop", preventDefault);
    return () => {
      document.removeEventListener("dragover", preventDefault);
      document.removeEventListener("drop", preventDefault);
    };
  }, []);
  const handleSidebarDrop = (e) => {
    e.preventDefault();
    setDropHover(false);
    // If a matter is selected and subfolders loaded, file immediately
    if (selectedMatter && emailTargetFolder) {
      handleFile();
    } else if (selectedMatter && !emailTargetFolder) {
      toast.info("Loading subfolders — drop again once loaded");
    } else {
      // No matter selected — auto-trigger AI suggestion and focus matter search
      toast.info("Finding best matter to file to...");
      fetchAiSuggest();
      // Scroll to and focus the matter search input
      setTimeout(() => {
        const searchInput = document.querySelector('[data-testid="matter-search-input"]');
        if (searchInput) { searchInput.scrollIntoView({ behavior: "smooth" }); searchInput.focus(); }
      }, 300);
    }
    onDropFiled?.();
  };

  // Drop on a specific matter result
  const handleMatterDrop = (e, matter) => {
    e.preventDefault(); e.stopPropagation();
    setDropHover(false);
    setSelectedMatter(matter);
    setMatterSearch(matter.reference || matter.title);
    setMatterResults([]);
    loadSubfolders(matter);
    toast.info(`Selected ${matter.reference || matter.title} — loading folders...`);
    onDropFiled?.();
  };

  return (
    <div
      className={`w-72 flex-shrink-0 border-l ${t.border} ${t.bgFiling} overflow-y-auto flex flex-col transition-all ${dropHover ? "ring-2 ring-inset ring-teal/60 bg-teal-dim/10" : ""}`}
      data-testid="filing-panel"
      onDragOver={handleSidebarDragOver}
      onDragLeave={handleSidebarDragLeave}
      onDrop={handleSidebarDrop}
    >
      <div className={`px-3 py-2.5 border-b ${t.border} flex items-center gap-2`}>
        <div className={`w-6 h-6 ${t.bgActive} rounded flex items-center justify-center`}>
          <FolderUp className={`w-3.5 h-3.5 ${t.textAccentDim}`} />
        </div>
        <span className={`text-xs font-semibold ${t.text}`}>File to SharePoint</span>
        {dropHover && <span className="text-[10px] text-teal font-medium ml-auto animate-pulse">Drop to file</span>}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {/* Drop Zone — always visible for drag-and-drop parity with Outlook add-in */}
        <div
          className={`rounded-lg border-2 border-dashed p-4 text-center transition-all cursor-pointer ${
            dropHover
              ? "border-teal bg-teal/15 scale-[1.02]"
              : selectedMatter && emailTargetFolder
                ? "border-teal/60 bg-teal-dim/10 hover:border-teal"
                : "border-gray-600/40 bg-gray-800/20"
          }`}
          onDragOver={handleSidebarDragOver}
          onDragLeave={handleSidebarDragLeave}
          onDrop={handleSidebarDrop}
          onClick={() => {
            if (selectedMatter && emailTargetFolder && (email || checkedCount > 0)) handleFile();
          }}
          data-testid="drop-zone"
        >
          {dropHover ? (
            <>
              <FolderUp className="w-6 h-6 text-teal mx-auto mb-1.5 animate-bounce" />
              <p className="text-sm font-bold text-teal">Drop to file</p>
              {selectedMatter && <p className={`text-[10px] ${t.textDim} mt-0.5`}>to {selectedMatter.reference || selectedMatter.title}</p>}
            </>
          ) : selectedMatter && emailTargetFolder ? (
            <>
              <FolderUp className="w-5 h-5 text-teal mx-auto mb-1" />
              <p className="text-xs font-semibold text-teal">Drop or click to file</p>
              <p className={`text-[10px] ${t.textDim} mt-0.5`}>to {selectedMatter.reference || selectedMatter.title} → {emailTargetFolder.name}</p>
              <button onClick={(e) => { e.stopPropagation(); setSelectedMatter(null); setEmailTargetFolder(null); setAttachTargetFolder(null); setSubfolders([]); setAttachBrowsePath([]); setAttachBrowseChildren([]); }}
                className="text-[10px] text-blue-400 hover:text-blue-300 underline mt-1.5" data-testid="change-destination-link">Change destination</button>
            </>
          ) : (
            <>
              <FolderUp className="w-4 h-4 text-gray-500 mx-auto mb-1" />
              <p className={`text-[10px] ${t.textDim}`}>Select a destination below, then drop or click to file</p>
            </>
          )}
        </div>

        {email && (
          <div className={`${t.bgInput} rounded-lg p-3 border ${t.border}`}>
            <div className="flex items-start gap-2.5">
              <div className="w-7 h-7 bg-blue-600/20 rounded flex items-center justify-center flex-shrink-0">
                <Mail className="w-3.5 h-3.5 text-blue-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className={`text-[11px] font-medium ${t.text} truncate`}>{email.subject}</p>
                <p className={`text-[10px] ${t.textDim} truncate mt-0.5`}>{email.from_name || email.from_email}</p>
                {email.attachments?.length > 0 && (
                  <div className="flex items-center gap-1 mt-1">
                    <Paperclip className={`w-2.5 h-2.5 ${t.textDim}`} />
                    <span className={`text-[10px] ${t.textDim}`}>{email.attachments.length} attachment{email.attachments.length > 1 ? "s" : ""}</span>
                  </div>
                )}
              </div>
            </div>
            {/* Enhancement 6: File & Reply button */}
            {selectedMatter && emailTargetFolder && (
              <button onClick={handleFileAndReply}
                className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-700 hover:bg-blue-600 text-white text-[11px] font-semibold rounded-lg transition-colors"
                data-testid="file-and-reply-btn">
                <Reply className="w-3.5 h-3.5" />
                <span>File & Reply</span>
              </button>
            )}
            {/* Enhancement 7: File Conversation button */}
            {email.conversationId && selectedMatter && emailTargetFolder && (
              <button onClick={handleFileConversation} disabled={filingConversation}
                className="mt-1.5 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-indigo-700 hover:bg-indigo-600 text-white text-[11px] font-semibold rounded-lg transition-colors disabled:opacity-50"
                data-testid="file-conversation-btn">
                {filingConversation ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageCircle className="w-3.5 h-3.5" />}
                <span>{filingConversation ? "Filing conversation..." : "File Conversation"}</span>
              </button>
            )}
          </div>
        )}
        {/* Email Insight — auto category badge + deadline banner */}
        {email && (emailInsight || loadingInsight) && (
          <div className="space-y-2">
            {/* Category Badge */}
            {emailInsight?.category && emailInsight.category !== "Unknown" && (
              <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${t.border} ${t.bgInput}`} data-testid="email-category-badge">
                <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                  emailInsight.category === "Client Communication" ? "bg-blue-400" :
                  emailInsight.category === "Completion" ? "bg-teal" :
                  emailInsight.category === "Searches" ? "bg-status-amber" :
                  emailInsight.category === "Land Registry" ? "bg-purple-400" :
                  emailInsight.category === "Marketing" ? "bg-pink-400" :
                  emailInsight.category === "Internal" ? "bg-slate-400" :
                  "bg-cyan-400"
                }`} />
                <span className={`text-[11px] font-medium ${t.textSec}`}>{emailInsight.category}</span>
              </div>
            )}
            {loadingInsight && !emailInsight && (
              <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${t.border} ${t.bgInput}`}>
                <Loader2 className="w-3 h-3 animate-spin text-purple-400" />
                <span className={`text-[10px] ${t.textDim}`}>Analysing email...</span>
              </div>
            )}
            {/* Deadline Banner */}
            {emailInsight?.deadlines?.length > 0 && (
              <div className={`rounded-lg border border-amber-600/50 bg-status-amber-dim/20 p-2.5`} data-testid="deadline-banner">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <AlertCircle className="w-3.5 h-3.5 text-status-amber" />
                  <span className="text-[11px] font-bold text-status-amber">Deadline detected</span>
                  <span className={`text-[9px] ${t.textDim} ml-auto`} title="Bonsai highlights dates that look like hearings or deadlines.">Not legal advice</span>
                </div>
                {emailInsight.deadlines.map((d, i) => (
                  <div key={i} className="mb-1.5 last:mb-0 p-2 rounded bg-status-amber-dim/20 border border-amber-700/30" data-testid={`deadline-item-${i}`}>
                    <p className="text-[11px] text-status-amber font-medium">{d.description}</p>
                    <p className="text-[10px] text-status-amber/80 mt-0.5">{d.date}{d.time ? ` at ${d.time}` : ""} · {Math.round((d.confidence || 0) * 100)}%</p>
                    <div className="flex gap-1.5 mt-1.5">
                      <button onClick={() => onCreateTodo?.(d.description, d.date, "normal")}
                        className="px-2 py-0.5 bg-status-amber hover:bg-status-amber text-white text-[10px] rounded font-medium"
                        data-testid={`create-task-btn-${i}`}>
                        <ListTodo className="w-3 h-3 inline mr-0.5" /> Task
                      </button>
                      <button onClick={() => onCreateCalendarEvent?.(d.description, d.date, d.time)}
                        className="px-2 py-0.5 bg-blue-700 hover:bg-blue-600 text-white text-[10px] rounded font-medium"
                        data-testid={`create-calendar-btn-${i}`}>
                        <CalendarPlus className="w-3 h-3 inline mr-0.5" /> Calendar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Extracted Tasks */}
            {emailInsight?.tasks?.length > 0 && (
              <div className={`rounded-lg border border-blue-600/30 bg-blue-900/10 p-2.5`} data-testid="tasks-banner">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <ListTodo className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-[11px] font-bold text-blue-300">Tasks found</span>
                </div>
                {(emailInsight.tasks || []).slice(0, 3).map((task, i) => (
                  <div key={i} className="text-[10px] text-blue-200/80 py-0.5">
                    · {typeof task === "string" ? task : task?.description || JSON.stringify(task)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* File Like Last — one-tap re-file */}
        {fileLikeLast && email && (
          <button
            onClick={() => {
              const fll = fileLikeLast;
              onFileToSharePoint({ driveId: fll.driveId, folderId: fll.folderId, includeAttachments: false, deleteAfterFiling });
              saveRecentFiling({ driveId: fll.driveId, itemId: fll.itemId, reference: fll.matterRef }, { id: fll.folderId, name: fll.folderName });
            }}
            className={`w-full flex items-center gap-2.5 p-2.5 rounded-lg border border-teal/50 bg-teal-dim/20 hover:bg-teal-dim/40 hover:border-teal transition-all`}
            data-testid="file-like-last-btn"
          >
            <Repeat className="w-4 h-4 text-teal flex-shrink-0" />
            <div className="flex-1 min-w-0 text-left">
              <div className="text-[11px] font-semibold text-teal">File Like Last</div>
              <div className={`text-[10px] ${t.textDim} break-words`}>{fileLikeLast.matterRef} / {fileLikeLast.folderName}</div>
            </div>
          </button>
        )}
        {/* Recently Filed — merge local + server-backed history */}
        {(recentDestinations.length > 1 || serverRecentFilings.length > 0) && !selectedMatter && (
          <div>
            <label className={`text-[10px] ${t.textDim} uppercase tracking-wider font-medium flex items-center gap-1 mb-1`}>
              <Clock className="w-3 h-3" /> Recently Filed
            </label>
            <div className="space-y-0.5">
              {/* Local recent destinations first */}
              {recentDestinations.slice(0, 3).map((rd, i) => (
                <button key={`local-${i}`}
                  onClick={() => {
                    if (email) {
                      onFileToSharePoint({ driveId: rd.driveId, folderId: rd.folderId, includeAttachments: false, deleteAfterFiling });
                      saveRecentFiling({ driveId: rd.driveId, itemId: rd.itemId, reference: rd.matterRef }, { id: rd.folderId, name: rd.folderName });
                    }
                  }}
                  className={`w-full text-left px-2.5 py-1.5 rounded text-[11px] ${t.textSec} ${t.bgHover} flex items-center gap-2`}
                  data-testid={`quick-refile-${i}`}
                >
                  <FolderOpen className="w-3 h-3 text-blue-400 flex-shrink-0" />
                  <span className="break-words flex-1 truncate">{rd.matterRef}</span>
                  <span className={`text-[9px] ${t.textDim} flex-shrink-0`}>{rd.folderName}</span>
                </button>
              ))}
              {/* Server recent filings (cross-platform history) */}
              {serverRecentFilings.filter(sf => !recentDestinations.some(rd => rd.matterRef === sf.matter_reference)).slice(0, 2).map((sf, i) => {
                const ref = sf.matter_reference || sf.sharepoint_path?.split("/").pop() || "Filed";
                const date = sf.filed_at ? new Date(sf.filed_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "";
                return (
                  <div key={`server-${i}`}
                    className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-[11px] ${t.textMuted} ${t.bgInput} border ${t.borderSub}`}
                    data-testid={`server-recent-filing-${i}`}
                  >
                    <CheckCircle2 className="w-3 h-3 text-teal flex-shrink-0" />
                    <span className="break-words flex-1 truncate">{ref}</span>
                    <span className={`text-[9px] ${t.textDim} flex-shrink-0`}>{date}</span>
                    {sf.sharepoint_url && (
                      <a href={sf.sharepoint_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        className="text-blue-400 hover:text-blue-300 flex-shrink-0" title="Open in SharePoint">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Favorites */}
        {favorites.length > 0 && !selectedMatter && (
          <div>
            <label className={`text-[10px] text-yellow-400 uppercase tracking-wider font-medium flex items-center gap-1 mb-1`}>
              <Star className="w-3 h-3" fill="currentColor" /> Favorites
            </label>
            <div className="space-y-0.5">
              {favorites.map(fav => (
                <button key={fav.id || fav.name}
                  onClick={() => {
                    setSelectedMatter({ driveId: fav.driveId, itemId: fav.itemId || fav.id, reference: fav.name || fav.reference, title: fav.name });
                    setMatterSearch(fav.name || fav.reference);
                    loadSubfolders({ driveId: fav.driveId, itemId: fav.itemId || fav.id });
                  }}
                  className={`w-full text-left px-2.5 py-1.5 rounded text-[11px] ${t.textSec} ${t.bgHover} flex items-center gap-2`}
                  data-testid={`favorite-${fav.name?.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <Star className="w-3 h-3 text-yellow-500 flex-shrink-0" fill="currentColor" />
                  <span className="break-words">{fav.name || fav.reference}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Recent Matters */}
        {recentMatters.length > 0 && !selectedMatter && !matterSearch && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={`text-[10px] ${t.textDim} uppercase tracking-wider font-medium flex items-center gap-1`}>
                <Clock className="w-3 h-3" /> Recent Matters
              </label>
              <button onClick={() => { localStorage.removeItem("bonsai_recent_matters"); setRecentMatters([]); }}
                className={`text-[9px] ${t.textDim} hover:${t.textSec}`} data-testid="clear-recent-matters">clear</button>
            </div>
            <div className="space-y-0.5">
              {recentMatters.map(rm => (
                <button key={rm.id || rm.itemId}
                  onClick={() => {
                    setSelectedMatter(rm);
                    setMatterSearch(rm.reference || rm.title);
                    loadSubfolders(rm);
                  }}
                  className={`w-full text-left px-2.5 py-1.5 rounded text-[11px] ${t.textSec} ${t.bgHover} flex items-center gap-2`}
                  data-testid={`recent-matter-${rm.reference?.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <Clock className="w-3 h-3 flex-shrink-0" />
                  <span className="break-words">{rm.reference || rm.title}</span>
                  {rm.client_name && <span className={`text-[9px] ${t.textDim} flex-shrink-0`}>{rm.client_name}</span>}
                </button>
              ))}
            </div>
          </div>
        )}
        <Button variant="outline" size="sm" onClick={fetchAiSuggest} disabled={loadingAi || !email}
          className={`w-full text-xs h-8 border-teal/50 ${t.textAccentDim}`} data-testid="ai-suggest-btn">
          {loadingAi ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Sparkles className="w-3 h-3 mr-1.5" />}AI Suggest Filing
        </Button>
        {aiSuggestion?.ai_suggestion?.matter_reference && (
          <div className={`text-[11px] ${t.textAccent} ${t.bgActive} p-2.5 rounded-lg border ${t.border}`}>
            <span className="font-medium">Suggests:</span> {aiSuggestion.ai_suggestion.matter_reference}
          </div>
        )}
        <div>
          <label className={`text-[10px] ${t.textDim} uppercase tracking-wider font-medium block mb-1.5`}>Search Matter</label>
          <div className="relative">
            <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${t.textDim}`} />
            <Input placeholder="Type matter ref or name..." value={matterSearch} onChange={e => { setMatterSearch(e.target.value); setParsedFromSubject(false); }}
              className={`pl-8 h-8 text-xs ${t.bgInput} ${t.border}`} data-testid="matter-search-input" />
          </div>
          {parsedFromSubject && (
            <p className="text-[9px] text-gray-500 mt-0.5 italic" data-testid="parsed-from-subject-label">parsed from subject</p>
          )}
        </div>
        {searchingMatters && <Loader2 className={`w-3 h-3 animate-spin ${t.textDim} mx-auto`} />}
        {/* Enhancement 4: Pinned matters section */}
        {(() => {
          const pinnedResults = matterResults.filter(m => pinnedMatterIds.includes(m.id || m.itemId));
          const unpinnedResults = matterResults.filter(m => !pinnedMatterIds.includes(m.id || m.itemId));
          return matterResults.length > 0 && !selectedMatter ? (
            <div className={`max-h-44 overflow-y-auto space-y-px ${t.bgInput} rounded-lg border ${t.border} p-1`}>
              {pinnedResults.length > 0 && (
                <>
                  <div className={`text-[9px] text-status-amber uppercase tracking-wider font-bold px-2 py-1 flex items-center gap-1`}>
                    <Pin className="w-2.5 h-2.5" /> Pinned
                  </div>
                  {pinnedResults.map(m => (
                    <button key={m.id || m.itemId}
                      onClick={() => { setSelectedMatter(m); setMatterSearch(m.reference || m.title); setMatterResults([]); loadSubfolders(m); saveRecentMatter(m); }}
                      className={`w-full text-left px-2.5 py-2 rounded text-xs ${t.textSec} ${t.bgHover} border border-amber-700/30 group`}
                      data-testid={`pinned-matter-${m.id || m.itemId}`}>
                      <div className="flex items-center gap-2">
                        <Pin className="w-3 h-3 text-status-amber flex-shrink-0 fill-current" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium break-words">{m.reference || m.title}</div>
                          {m.client_name && <div className={`text-[10px] ${t.textDim} break-words`}>{m.client_name}{m.counterparty ? ` v ${m.counterparty}` : ""}</div>}
                        </div>
                        <button onClick={e => { e.stopPropagation(); togglePinMatter(m.id || m.itemId); }}
                          className="p-1 text-status-amber hover:text-status-amber" title="Unpin">
                          <Pin className="w-3 h-3 fill-current" />
                        </button>
                      </div>
                    </button>
                  ))}
                  <div className={`mx-2 h-px ${t.border} my-1`} />
                </>
              )}
              {unpinnedResults.slice(0, 10).map(m => (
                <button key={m.id || m.itemId}
                  onClick={() => { setSelectedMatter(m); setMatterSearch(m.reference || m.title); setMatterResults([]); loadSubfolders(m); saveRecentMatter(m); }}
                  onDragOver={e => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy"; }}
                  onDrop={e => handleMatterDrop(e, m)}
                  className={`w-full text-left px-2.5 py-2 rounded text-xs ${t.textSec} ${t.bgHover} border border-transparent hover:border-teal/40 group`}
                  data-testid={`matter-option-${m.id || m.itemId}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium break-words">{m.reference || m.title}</div>
                      {m.client_name && <div className={`text-[10px] ${t.textDim} break-words`}>{m.client_name}{m.counterparty ? ` v ${m.counterparty}` : ""}</div>}
                    </div>
                    <button onClick={e => { e.stopPropagation(); togglePinMatter(m.id || m.itemId); }}
                      className={`opacity-0 group-hover:opacity-100 p-1 ${t.textDim} hover:text-status-amber transition-opacity`} title="Pin matter">
                      <Pin className="w-3 h-3" />
                    </button>
                    <span
                      onClick={e => { e.stopPropagation(); setSelectedMatter(m); setMatterSearch(m.reference || m.title); setMatterResults([]); loadSubfolders(m); saveRecentMatter(m); }}
                      className="hidden group-hover:inline-flex px-2 py-1 bg-teal hover:bg-teal text-white text-[10px] rounded font-semibold flex-shrink-0"
                      data-testid="quick-file-matter"
                    >File</span>
                  </div>
                </button>
              ))}
            </div>
          ) : null;
        })()}
        {selectedMatter && (
          <div className="space-y-2.5">
            {/* Selected Destination - Clear confirmation box like add-in */}
            {emailTargetFolder && (
              <div className="bg-teal-dim/40 border-2 border-teal rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Folder className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] text-teal uppercase tracking-wider font-bold">Filing to</p>
                    <p className="text-sm text-white font-bold break-all leading-snug">
                      {selectedMatter.reference || selectedMatter.title} → {emailTargetFolder.name}
                    </p>
                  </div>
                  <button onClick={() => setEmailTargetFolder(null)} className="text-gray-400 hover:text-status-red p-1" title="Clear">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <div className={`flex-1 text-[11px] ${t.textAccent} ${t.bgActive} p-2 rounded-lg border ${t.border} font-medium break-words`}>
                {selectedMatter.reference || selectedMatter.title}
              </div>
              <button onClick={() => { setSelectedMatter(null); setMatterSearch(""); setSubfolders([]); setEmailTargetFolder(null); }}
                className={`${t.textDim} p-1 rounded ${t.bgHover}`}><ChevronLeft className="w-3.5 h-3.5" /></button>
            </div>
            {loadingSubfolders && <div className={`flex items-center gap-2 text-[11px] ${t.textDim}`}><Loader2 className="w-3 h-3 animate-spin" /> Loading subfolders...</div>}
            {subfolders.length > 0 && (
              <>
                <div>
                  <label className={`text-[10px] ${t.textDim} uppercase tracking-wider font-medium block mb-1.5`}>Select subfolder (or drag emails/attachments here)</label>
                  <div className="flex flex-wrap gap-1.5">
                    {subfolders.map(sf => {
                      const isDefault = emailTargetFolder?.id === sf.id && sf.name.toLowerCase().includes("attendan");
                      return (
                      <button 
                        key={sf.id} 
                        onClick={() => setEmailTargetFolder(sf)}
                        onDrop={(e) => handleSubfolderDrop(e, sf)}
                        onDragOver={(e) => handleSubfolderDragOver(e, sf)}
                        onDragLeave={handleSubfolderDragLeave}
                        className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all border-2
                          ${dropTarget === sf.id 
                            ? `bg-teal text-white border-teal scale-105 shadow-xl shadow-teal/50`
                            : emailTargetFolder?.id === sf.id 
                              ? `bg-teal text-white border-teal shadow-lg shadow-teal/50` 
                              : `${t.bgInput} ${t.textMuted} border-transparent hover:border-teal/50 hover:bg-teal-dim/20`}`}
                        data-testid={`email-subfolder-${sf.name.toLowerCase().replace(/\s/g, "-")}`}>
                        {sf.name}{isDefault ? " (default)" : ""}
                      </button>
                      );
                    })}
                  </div>
                </div>
                {email?.attachments?.length > 0 && (
                  <div className={`space-y-1.5 ${t.bgInput} rounded-lg p-2.5 border ${t.border}`}>
                    <div className="flex items-center gap-2">
                      <Paperclip className="w-3.5 h-3.5 text-status-amber" />
                      <span className={`text-[11px] font-medium ${t.text}`}>{email.attachments.length} attachment{email.attachments.length > 1 ? "s" : ""}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox checked={includeAttachments} onCheckedChange={setIncludeAttachments} data-testid="include-attachments-check" />
                      <label className={`text-[11px] ${t.textSec}`}>Save attachments separately</label>
                    </div>
                    {includeAttachments && (
                      <div>
                        <label className={`text-[10px] ${t.textDim} uppercase tracking-wider font-medium block mb-1.5`}>Save attachments to</label>
                        {/* Breadcrumb */}
                        {attachBrowsePath.length > 0 && (
                          <div className="flex items-center gap-1 mb-1.5 text-[10px]">
                            <button onClick={() => { setAttachBrowsePath([]); setAttachBrowseChildren([]); const doc = subfolders.find(s => s.name.toLowerCase().includes("document")); if (doc) setAttachTargetFolder(doc); }}
                              className="text-status-amber hover:text-status-amber">Root</button>
                            {attachBrowsePath.map((p, i) => (
                              <span key={p.id} className="flex items-center gap-1">
                                <ChevronRight className="w-3 h-3 opacity-40" />
                                <span className={i === attachBrowsePath.length - 1 ? "text-status-amber font-medium" : `${t.textDim}`}>{p.name}</span>
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-1.5">
                          {attachBrowsePath.length > 0 && (
                            <button onClick={attachBrowseBack}
                              className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all border-2 ${t.bgInput} ${t.textMuted} border-transparent hover:border-amber-700/50`}>
                              .. Back
                            </button>
                          )}
                          {(attachBrowsePath.length > 0 ? attachBrowseChildren : subfolders).map(sf => (
                            <button key={sf.id} onClick={() => {
                              setAttachTargetFolder(sf);
                              if (sf.is_folder !== false && sf.child_count !== 0) browseAttachFolder(sf);
                            }}
                              className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all border-2
                                ${attachTargetFolder?.id === sf.id 
                                  ? "bg-status-amber text-white border-amber-500 shadow-lg shadow-amber-900/50" 
                                  : `${t.bgInput} ${t.textMuted} border-transparent hover:border-amber-700/50 hover:bg-status-amber-dim/20`}`}
                              data-testid={`att-subfolder-${sf.name.toLowerCase().replace(/\s/g, "-")}`}>
                              {sf.name} {sf.child_count > 0 ? `(${sf.child_count})` : ""}
                            </button>
                          ))}
                          {attachBrowseLoading && <Loader2 className="w-4 h-4 animate-spin text-status-amber" />}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {/* Filing progress bar */}
        {filingInProgress && filingProgressMsg && (() => {
          // Parse "Filing N emails — X done, Y failed..." for real percentage
          const match = filingProgressMsg.match(/(\d+) done/);
          const totalMatch = filingProgressMsg.match(/Filing (\d+)/);
          const done = match ? parseInt(match[1]) : 0;
          const total = totalMatch ? parseInt(totalMatch[1]) : 0;
          const pct = total > 0 ? Math.round((done / total) * 100) : null;
          return (
            <div className="space-y-1.5" data-testid="filing-progress-bar">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-teal flex-shrink-0" />
                  <span className="text-[11px] text-teal font-medium">{filingProgressMsg}</span>
                </div>
                {pct !== null && <span className="text-[11px] text-teal font-mono">{pct}%</span>}
              </div>
              <div className="w-full bg-gray-700/50 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-teal h-full rounded-full transition-all duration-500"
                  style={{ width: pct !== null ? `${pct}%` : "100%", animation: pct === null ? "pulse 1.5s infinite" : "none" }}
                />
              </div>
            </div>
          );
        })()}

        <Button onClick={handleFile} disabled={!selectedMatter || !emailTargetFolder || filingInProgress}
          className="w-full bg-teal hover:bg-teal text-white text-xs h-9 font-medium" data-testid="file-to-sharepoint-btn">
          {filingInProgress ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <FolderUp className="w-3.5 h-3.5 mr-1.5" />}
          {checkedCount > 1 ? `File ${checkedCount} emails` : "File to SharePoint"}
        </Button>
        {/* File entire conversation button - only show when single email selected with conversationId */}
        {email?.conversationId && checkedCount <= 1 && (
          <Button onClick={handleFileConversation} disabled={!selectedMatter || !emailTargetFolder || filingInProgress || filingConversation}
            variant="outline" className={`w-full text-xs h-8 font-medium border-teal/50 ${t.text} hover:bg-teal-dim/30`} data-testid="file-conversation-btn">
            {filingConversation ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <MessageSquare className="w-3.5 h-3.5 mr-1.5" />}
            File entire conversation
          </Button>
        )}
        {/* Delete after filing toggle */}
        <div className="flex items-center gap-2 px-1">
          <Checkbox checked={deleteAfterFiling} onCheckedChange={toggleDeleteAfterFiling} id="delete-after-filing" data-testid="delete-after-filing-check" />
          <label htmlFor="delete-after-filing" className={`text-[11px] ${t.textSec} cursor-pointer select-none`}>Delete email after filing</label>
        </div>
        {/* Filing diagnostic test */}
        {selectedMatter && emailTargetFolder && (
          <button onClick={runFilingDiagnostic} disabled={runningDiagnostic}
            className={`w-full text-[10px] ${t.textDim} hover:${t.textSec} py-1 underline`} data-testid="filing-diagnostic-btn">
            {runningDiagnostic ? "Testing..." : "Test filing connection"}
          </button>
        )}
        {diagnosticResult && (
          <div className={`text-[10px] p-2 rounded border ${diagnosticResult.overall === "PASS" ? "border-teal bg-teal-dim/20 text-teal" : "border-red-700 bg-status-red-dim/20 text-status-red"}`} data-testid="diagnostic-result">
            <div className="font-semibold mb-1">{diagnosticResult.overall === "PASS" ? "All checks passed" : `Failed at: ${diagnosticResult.fail_step || "unknown"}`}</div>
            {diagnosticResult.steps?.map((s, i) => (
              <div key={i} className="flex items-center gap-1">
                <span>{s.pass ? "\u2713" : "\u2717"}</span>
                <span>{s.description || s.step}</span>
                {s.error && <span className="text-status-red break-words"> — {typeof s.error === "string" ? s.error.slice(0, 100) : JSON.stringify(s.error).slice(0, 100)}</span>}
              </div>
            ))}
            {diagnosticResult.error && <div className="text-status-red break-words mt-1">{diagnosticResult.error}</div>}
          </div>
        )}

        {/* Attachment confirmation dialog */}
        {showAttachPrompt && email?.attachments?.length > 0 && (
          <div className={`${t.bgInput} rounded-lg border-2 border-amber-600/50 p-3 space-y-2.5`} data-testid="attach-prompt-dialog">
            <div className="flex items-center gap-2">
              <Paperclip className="w-4 h-4 text-status-amber" />
              <span className={`text-xs font-semibold ${t.text}`}>Save attachments?</span>
            </div>
            <p className={`text-[11px] ${t.textSec}`}>
              This email has <span className="font-semibold text-status-amber">{email.attachments.length}</span> attachment{email.attachments.length > 1 ? "s" : ""}. Where would you like to save them?
            </p>
            {/* Breadcrumb */}
            {attachBrowsePath.length > 0 && (
              <div className="flex items-center gap-1 text-[10px]">
                <button onClick={() => { setAttachBrowsePath([]); setAttachBrowseChildren([]); const doc = subfolders.find(s => s.name.toLowerCase().includes("document")); if (doc) setAttachTargetFolder(doc); }}
                  className="text-status-amber hover:text-status-amber">Root</button>
                {attachBrowsePath.map((p, i) => (
                  <span key={p.id} className="flex items-center gap-1">
                    <ChevronRight className="w-3 h-3 opacity-40" />
                    <span className={i === attachBrowsePath.length - 1 ? "text-status-amber font-medium" : `${t.textDim}`}>{p.name}</span>
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-1">
              {attachBrowsePath.length > 0 && (
                <button onClick={attachBrowseBack}
                  className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors border ${t.bgInput} ${t.textMuted} ${t.border}`}>
                  .. Back
                </button>
              )}
              {(attachBrowsePath.length > 0 ? attachBrowseChildren : subfolders).map(sf => (
                <button key={sf.id} onClick={() => {
                  setAttachTargetFolder(sf);
                  if (sf.is_folder !== false && sf.child_count !== 0) browseAttachFolder(sf);
                }}
                  className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors border
                    ${attachTargetFolder?.id === sf.id ? "bg-status-amber-dim/40 text-status-amber border-amber-700/50" : `${t.bgInput} ${t.textMuted} ${t.border} ${t.bgHover}`}`}
                  data-testid={`prompt-att-subfolder-${sf.name.toLowerCase().replace(/\s/g, "-")}`}>
                  {sf.name} {sf.child_count > 0 ? `(${sf.child_count})` : ""}
                </button>
              ))}
              {attachBrowseLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-status-amber" />}
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={() => confirmFileWithAttachments(true, attachTargetFolder)}
                disabled={!attachTargetFolder}
                className="flex-1 bg-status-amber hover:bg-status-amber text-white text-xs h-8"
                data-testid="confirm-save-attachments-btn">
                Save to {attachTargetFolder?.name || "..."}
              </Button>
              <Button size="sm" variant="outline" onClick={() => confirmFileWithAttachments(false, null)}
                className={`flex-1 text-xs h-8 ${t.border} ${t.textSec}`}
                data-testid="skip-attachments-btn">
                Email only
              </Button>
            </div>
          </div>
        )}
        <div className={`h-px ${t.border}`} />
        <div>
          <label className={`text-[10px] ${t.textDim} uppercase tracking-wider font-medium block mb-2`}>Quick Actions</label>
          <div className="space-y-1.5">
            <Button variant="outline" size="sm" onClick={() => onDelete()}
              className={`w-full text-xs border-red-900/50 text-status-red hover:bg-status-red-dim/15 h-8 justify-start`} data-testid="delete-email-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />{checkedCount > 1 ? `Delete ${checkedCount}` : "Delete"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowMoveMenu(!showMoveMenu)}
              className={`w-full text-xs ${t.border} ${t.textSec} ${t.bgHover} h-8 justify-start`} data-testid="move-email-btn">
              <FolderOpen className="w-3.5 h-3.5 mr-1.5" /> Move to folder
            </Button>
            {showMoveMenu && (
              <div className={`max-h-40 overflow-y-auto ${t.bgInput} rounded-lg border ${t.border}`}>
                {folders.map(f => (
                  <button key={f.id} onClick={() => { onMove(f.id); setShowMoveMenu(false); }}
                    className={`w-full text-left px-3 py-1.5 text-xs ${t.textSec} ${t.bgHover} flex items-center gap-2`}>
                    {getFolderIcon(f.name)} {f.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className={`h-px ${t.border}`} />
        <SharePointBrowser
          email={email}
          onFileToSharePoint={onFileToSharePoint}
          filingInProgress={filingInProgress}
          t={t}
          headers={headers}
          draggedMsgIds={draggedMsgIds}
          checkedCount={checkedCount}
        />
        {/* Morning Digest Widget */}
        {digestSummary && (
          <div className={`border-t ${t.border} pt-3`} data-testid="morning-digest-widget">
            <div className="flex items-center gap-2 mb-2 px-1">
              <Sun className="w-3.5 h-3.5 text-status-amber" />
              <span className={`text-[11px] font-semibold ${t.textSec} tracking-wide`}>Morning Digest</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5 mb-2">
              <div className={`${t.bgInput} rounded px-2 py-1.5 text-center border ${t.borderSub}`}>
                <div className="text-sm font-bold text-status-amber">{digestSummary.unfiled_count || 0}</div>
                <div className={`text-[9px] ${t.textDim}`}>Unfiled</div>
              </div>
              <div className={`${t.bgInput} rounded px-2 py-1.5 text-center border ${t.borderSub}`}>
                <div className="text-sm font-bold text-teal">{digestSummary.filing_rate || "0%"}</div>
                <div className={`text-[9px] ${t.textDim}`}>Filing Rate</div>
              </div>
            </div>
            {digestSummary.tasks?.length > 0 && (
              <div className={`${t.bgInput} rounded-lg p-2 border ${t.borderSub}`}>
                <span className={`text-[10px] font-medium ${t.textSec}`}>Tasks from inbox:</span>
                {digestSummary.tasks.map((task, i) => (
                  <div key={i} className={`text-[10px] ${t.textMuted} py-0.5 truncate`}>
                    · {task.description || (typeof task === "string" ? task : "")}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Today's Time Widget */}
        <div className={`border-t ${t.border} pt-3`}>
          <div className={`flex items-center justify-between mb-2 px-1`}>
            <div className="flex items-center gap-2">
              <Clock className={`w-3.5 h-3.5 ${t.textAccent}`} />
              <span className={`text-[11px] font-semibold ${t.textSec} tracking-wide`}>Today's Time</span>
            </div>
            <div className="flex items-center gap-2">
              {loadingTodayTime && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
              <span className={`text-sm font-bold tabular-nums ${todayTime.total > 0 ? "text-teal" : t.textDim}`}
                data-testid="today-time-total">
                {todayTime.total}h
              </span>
            </div>
          </div>
          {todayTime.entries.length > 0 && (
            <div className={`space-y-1 mb-2 max-h-28 overflow-y-auto`}>
              {todayTime.entries.map((e, i) => (
                <div key={i} className={`flex items-center justify-between text-[10px] px-2 py-1 rounded ${t.bgInput} border ${t.borderSub}`}>
                  <span className={`${t.textMuted} truncate mr-2 flex-1`} title={e.narrative || e.description}>
                    {e.matter_reference || e.matter || "—"}
                  </span>
                  <span className={`font-semibold tabular-nums flex-shrink-0 ${t.textAccent}`}>{e.duration || e.hours || 0}h</span>
                </div>
              ))}
            </div>
          )}
          {todayTime.entries.length === 0 && !loadingTodayTime && (
            <p className={`text-[10px] ${t.textDim} px-1 mb-2`}>No time recorded today</p>
          )}

          {/* LawBillity Time Recording Panel */}
          <button
            onClick={() => setShowTimePanel(p => !p)}
            className={`w-full flex items-center gap-2 text-xs font-semibold ${t.textSec} ${t.bgHover} px-2 py-2 rounded-lg transition-colors`}
            data-testid="toggle-time-panel-btn"
          >
            <Timer className={`w-4 h-4 ${showTimePanel ? "text-indigo-400" : t.textDim}`} />
            <span>LawBillity Time Recording</span>
            <ChevronRight className={`w-3.5 h-3.5 ml-auto transition-transform ${showTimePanel ? "rotate-90" : ""} ${t.textDim}`} />
          </button>
          {showTimePanel && (
            <div className={`mt-2 space-y-2.5 ${t.bgInput} rounded-lg p-3 border ${t.border}`} data-testid="time-recording-panel">
              {/* Matter Reference */}
              <div>
                <label className={`text-[10px] ${t.textDim} uppercase tracking-wider font-medium block mb-1`}>Matter</label>
                <Input
                  value={timeMatterRef}
                  onChange={e => setTimeMatterRef(e.target.value)}
                  placeholder="Matter reference..."
                  className={`h-7 text-xs ${t.bgInput} ${t.border}`}
                  data-testid="time-matter-ref-input"
                />
              </div>
              {/* Template buttons */}
              <div>
                <label className={`text-[10px] ${t.textDim} uppercase tracking-wider font-medium block mb-1`}>Work Type</label>
                <div className="flex flex-wrap gap-1">
                  {[
                    { key: "considering", label: "Considering" },
                    { key: "drafting", label: "Drafting" },
                    { key: "reviewing", label: "Reviewing" },
                    { key: "attendance", label: "Attendance" },
                  ].map(tp => (
                    <button
                      key={tp.key}
                      onClick={() => handleSetTemplate(tp.key)}
                      className={`px-2 py-1 rounded text-[10px] font-medium transition-all border
                        ${timeTemplate === tp.key
                          ? "bg-indigo-700 text-white border-indigo-500"
                          : `${t.bgInput} ${t.textMuted} ${t.border} hover:border-indigo-700/50`}`}
                      data-testid={`time-tpl-${tp.key}`}
                    >{tp.label}</button>
                  ))}
                </div>
              </div>
              {/* AI Narrative */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={`text-[10px] ${t.textDim} uppercase tracking-wider font-medium`}>Narrative</label>
                  <button onClick={() => generateAINarrative()} disabled={!email || generatingNarrative}
                    className={`text-[10px] text-indigo-400 hover:text-indigo-300 flex items-center gap-1 ${!email ? "opacity-50" : ""}`}
                    data-testid="generate-ai-narrative-btn">
                    {generatingNarrative ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Sparkles className="w-2.5 h-2.5" />}
                    AI Generate
                  </button>
                </div>
                <textarea
                  value={timeNarrative}
                  onChange={e => setTimeNarrative(e.target.value)}
                  placeholder="Describe the work..."
                  rows={2}
                  className={`w-full text-xs rounded-md px-2.5 py-1.5 ${t.bgInput} ${t.border} border ${t.textSec} placeholder:${t.textDim} focus:outline-none focus:ring-1 focus:ring-indigo-600/50 resize-none`}
                  data-testid="time-narrative-input"
                />
              </div>
              {/* Duration */}
              <div>
                <label className={`text-[10px] ${t.textDim} uppercase tracking-wider font-medium block mb-1`}>Duration (hours)</label>
                <div className="flex items-center gap-1.5">
                  {[0.1, 0.2, 0.3, 0.5, 1.0].map(d => (
                    <button key={d}
                      onClick={() => setTimeDuration(d)}
                      className={`px-2 py-1 rounded text-[10px] font-medium transition-all border
                        ${timeDuration === d
                          ? "bg-indigo-600 text-white border-indigo-500"
                          : `${t.bgInput} ${t.textMuted} ${t.border} hover:border-indigo-700/50`}`}
                      data-testid={`time-dur-${d}`}
                    >{d}h</button>
                  ))}
                  <Input
                    type="number"
                    step="0.1"
                    min="0.1"
                    value={timeDuration}
                    onChange={e => setTimeDuration(parseFloat(e.target.value) || 0.1)}
                    className={`h-7 w-16 text-xs text-center ${t.bgInput} ${t.border}`}
                    data-testid="time-duration-input"
                  />
                </div>
                {aiDurationHint && (
                  <p className="text-[10px] text-indigo-400 mt-0.5" data-testid="ai-duration-hint">AI suggests {aiDurationHint}h</p>
                )}
              </div>
              {/* Date */}
              <div>
                <label className={`text-[10px] ${t.textDim} uppercase tracking-wider font-medium block mb-1`}>Date</label>
                <Input
                  type="date"
                  value={timeDate}
                  onChange={e => setTimeDate(e.target.value)}
                  className={`h-7 text-xs ${t.bgInput} ${t.border}`}
                  data-testid="time-date-input"
                />
              </div>
              {/* Save button */}
              <Button onClick={saveTimeEntry} disabled={savingTime || !timeMatterRef || !timeNarrative.trim()}
                className="w-full bg-indigo-700 hover:bg-indigo-600 text-white text-xs h-8 font-medium"
                data-testid="save-time-entry-btn">
                {savingTime ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Timer className="w-3.5 h-3.5 mr-1.5" />}
                Save to LawBillity
              </Button>
              {/* Result */}
              {timeResult && (
                <div className={`text-xs p-2 rounded border ${timeResult.type === "success"
                  ? "border-teal bg-teal-dim/20 text-teal"
                  : "border-red-700 bg-status-red-dim/20 text-status-red"}`}
                  data-testid="time-result">
                  {timeResult.type === "success" ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : <AlertCircle className="w-3 h-3 inline mr-1" />}
                  {timeResult.message}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════


function TodoDialog({ email, bonsaiApi, getHeaders, t, todoLists, setTodoLists, todoCreating, setTodoCreating, onClose, initialDescription, initialDueDate, initialPriority }) {
  const [selectedList, setSelectedList] = useState("");
  const [taskTitle, setTaskTitle] = useState(initialDescription || `Follow up: ${email?.subject || "(No subject)"}`);
  const [dueDate, setDueDate] = useState(initialDueDate || new Date(Date.now() + 86400000).toISOString().split("T")[0]);
  const [priority, setPriority] = useState(initialPriority || "normal");
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (todoLists.length === 0) {
      fetch(`${bonsaiApi}/bonsai/deadlines/todo-lists`, { headers: getHeaders() })
        .then(r => r.json())
        .then(data => {
          const lists = data.lists || [];
          setTodoLists(lists);
          if (lists.length > 0) setSelectedList(lists[0].id);
        })
        .catch(() => {});
    } else if (todoLists.length > 0 && !selectedList) {
      setSelectedList(todoLists[0].id);
    }
  }, []);

  const handleCreate = async () => {
    setTodoCreating(true);
    setStatus(null);
    try {
      const res = await fetch(`${bonsaiApi}/bonsai/deadlines/quick-todo-task`, {
        method: "POST",
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          description: taskTitle,
          email_subject: email?.subject || "",
          due_date: dueDate,
          priority: priority,
          list_id: selectedList || undefined,
        }),
      });
      const data = await res.json();
      if (data.ok || data.task_id) {
        setStatus({ ok: true, msg: "Task created!" });
        setTimeout(onClose, 1200);
      } else {
        setStatus({ ok: false, msg: data.error || data.detail || "Failed to create task" });
      }
    } catch (e) {
      setStatus({ ok: false, msg: e.message || "Network error" });
    }
    setTodoCreating(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70" onClick={onClose} data-testid="todo-dialog-overlay">
      <div className="bg-navy border border-navy-border rounded-xl shadow-2xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()} data-testid="todo-dialog">
        <div className="px-5 py-4 border-b border-navy-border flex items-center gap-2">
          <ListTodo className="w-5 h-5 text-status-green" />
          <span className="text-sm font-semibold text-white">Create Task in Microsoft To Do</span>
          <button onClick={onClose} className="ml-auto text-slate-400 hover:text-white p-1" data-testid="todo-close-x">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs text-slate-400 font-medium block mb-1">Task</label>
            <input
              type="text"
              value={taskTitle}
              onChange={e => setTaskTitle(e.target.value)}
              className="w-full bg-navy text-white text-sm rounded-md px-3 py-2.5 border border-navy-border focus:border-green-500/50 focus:outline-none"
              data-testid="todo-title-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 font-medium block mb-1">List</label>
            <select value={selectedList} onChange={e => setSelectedList(e.target.value)}
              className="w-full bg-navy text-white text-sm rounded-md px-3 py-2.5 border border-navy-border focus:border-green-500/50 focus:outline-none appearance-none"
              data-testid="todo-list-select">
              {todoLists.length === 0 && <option value="">Loading lists...</option>}
              {todoLists.map(l => <option key={l.id} value={l.id}>{l.displayName || l.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-400 font-medium block mb-1">Due Date</label>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
              className="w-full bg-navy text-white text-sm rounded-md px-3 py-2.5 border border-navy-border focus:border-green-500/50 focus:outline-none"
              data-testid="todo-date-input" />
          </div>
          <div>
            <label className="text-xs text-slate-400 font-medium block mb-2">Priority</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: "high", label: "High", active: "bg-status-red text-white border-red-500", idle: "bg-navy text-status-red border-navy-border hover:border-red-500/50" },
                { key: "normal", label: "Normal", active: "bg-blue-600 text-white border-blue-500", idle: "bg-navy text-blue-400 border-navy-border hover:border-blue-500/50" },
                { key: "low", label: "Low", active: "bg-slate-600 text-white border-slate-500", idle: "bg-navy text-slate-400 border-navy-border hover:border-slate-500/50" },
              ].map(p => (
                <button key={p.key} onClick={() => setPriority(p.key)} data-testid={`todo-pri-${p.key}`}
                  className={`text-sm py-2 rounded-md border font-medium transition-colors ${priority === p.key ? p.active : p.idle}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-navy-border flex gap-3">
          <button onClick={handleCreate} disabled={todoCreating || !taskTitle.trim()}
            className="flex-1 py-2.5 bg-status-green hover:bg-status-green disabled:bg-status-green-dim/50 disabled:text-status-green/50 text-white text-sm rounded-md font-semibold transition-colors"
            data-testid="todo-create-btn">
            {todoCreating ? "Creating..." : "Create Task"}
          </button>
          <button onClick={onClose} className="px-5 py-2.5 bg-navy text-slate-300 text-sm rounded-md border border-navy-border hover:border-slate-500/50 font-medium transition-colors"
            data-testid="todo-cancel-btn">Cancel</button>
        </div>
        {status && (
          <div className={`mx-5 mb-4 text-xs text-center rounded-md py-2 ${status.ok ? "text-status-green bg-status-green-dim/30 border border-green-500/20" : "text-status-red bg-status-red-dim/30 border border-red-500/20"}`}
            data-testid="todo-status">{status.msg}</div>
        )}
      </div>
    </div>
  );
}

function CalendarEventDialog({ email, bonsaiApi, getHeaders, t, onClose, initialDescription, initialDate, initialStartTime, initialEndTime }) {
  const [title, setTitle] = useState(initialDescription || `Event: ${email?.subject || "(No subject)"}`);
  const [eventDate, setEventDate] = useState(initialDate || new Date(Date.now() + 86400000).toISOString().split("T")[0]);
  const [startTime, setStartTime] = useState(initialStartTime || "09:00");
  const [endTime, setEndTime] = useState(initialEndTime || "09:30");
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState(null);

  // Auto-adjust end time when start time changes
  useEffect(() => {
    if (startTime && (!endTime || endTime <= startTime)) {
      const [h, m] = startTime.split(":").map(Number);
      const newEnd = `${String(Math.min(h + 1, 23)).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      setEndTime(newEnd);
    }
  }, [startTime]);

  const handleCreate = async () => {
    setCreating(true);
    setStatus(null);
    try {
      const res = await fetch(`${bonsaiApi}/bonsai/deadlines/quick-calendar-event`, {
        method: "POST",
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          description: title,
          email_subject: email?.subject || "",
          due_date: eventDate,
          start_time: startTime,
          end_time: endTime,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setStatus({ ok: true, msg: "Calendar event created!" });
        setTimeout(onClose, 1200);
      } else {
        setStatus({ ok: false, msg: data.detail || data.error || "Failed to create event" });
      }
    } catch (e) {
      setStatus({ ok: false, msg: e.message || "Network error" });
    }
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70" onClick={onClose} data-testid="calendar-dialog-overlay">
      <div className="bg-navy border border-navy-border rounded-xl shadow-2xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()} data-testid="calendar-dialog">
        <div className="px-5 py-4 border-b border-navy-border flex items-center gap-2">
          <CalendarPlus className="w-5 h-5 text-purple-400" />
          <span className="text-sm font-semibold text-white">Create Calendar Event</span>
          <button onClick={onClose} className="ml-auto text-slate-400 hover:text-white p-1" data-testid="calendar-close-x">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs text-slate-400 font-medium block mb-1">Event Title</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)}
              className="w-full bg-navy text-white text-sm rounded-md px-3 py-2.5 border border-navy-border focus:border-purple-500/50 focus:outline-none"
              data-testid="calendar-title-input" />
          </div>
          <div>
            <label className="text-xs text-slate-400 font-medium block mb-1">Date</label>
            <input type="date" value={eventDate} onChange={e => setEventDate(e.target.value)}
              className="w-full bg-navy text-white text-sm rounded-md px-3 py-2.5 border border-navy-border focus:border-purple-500/50 focus:outline-none"
              data-testid="calendar-date-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 font-medium block mb-1">Start Time</label>
              <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
                className="w-full bg-navy text-white text-sm rounded-md px-3 py-2.5 border border-navy-border focus:border-purple-500/50 focus:outline-none"
                data-testid="calendar-start-time" />
            </div>
            <div>
              <label className="text-xs text-slate-400 font-medium block mb-1">End Time</label>
              <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
                className="w-full bg-navy text-white text-sm rounded-md px-3 py-2.5 border border-navy-border focus:border-purple-500/50 focus:outline-none"
                data-testid="calendar-end-time" />
            </div>
          </div>
          {email?.subject && (
            <p className="text-[10px] text-slate-500 mt-1">From email: {email.subject}</p>
          )}
        </div>
        <div className="px-5 py-4 border-t border-navy-border flex gap-3">
          <button onClick={handleCreate} disabled={creating || !title.trim() || !eventDate}
            className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-500 disabled:bg-purple-800/50 disabled:text-purple-300/50 text-white text-sm rounded-md font-semibold transition-colors"
            data-testid="calendar-create-btn">
            {creating ? "Creating..." : "Create Event"}
          </button>
          <button onClick={onClose} className="px-5 py-2.5 bg-navy text-slate-300 text-sm rounded-md border border-navy-border hover:border-slate-500/50 font-medium transition-colors"
            data-testid="calendar-cancel-btn">Cancel</button>
        </div>
        {status && (
          <div className={`mx-5 mb-4 text-xs text-center rounded-md py-2 ${status.ok ? "text-status-green bg-status-green-dim/30 border border-green-500/20" : "text-status-red bg-status-red-dim/30 border border-red-500/20"}`}
            data-testid="calendar-status">{status.msg}</div>
        )}
      </div>
    </div>
  );
}


export default function BonsaiMailOnline() {
  const { authenticated, bonsaiApi, loading: bonsaiLoading, auth_error, login, user_id: authUserId } = useBonsai();
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [activeFolderName, setActiveFolderName] = useState("");
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [selectedMsgId, setSelectedMsgId] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [filingInProgress, setFilingInProgress] = useState(false);
  const [filingProgressMsg, setFilingProgressMsg] = useState("");
  const [composeMode, setComposeMode] = useState(null);
  const [composeReplyTo, setComposeReplyTo] = useState(null);
  const [showSigSettings, setShowSigSettings] = useState(false);
  const [ribbonTab, setRibbonTab] = useState("Home");
  const [readingPane, setReadingPane] = useState("right");
  const [showFiling, setShowFiling] = useState(true);
  const [theme, setTheme] = useState(getStoredTheme);
  const [pinnedIds, setPinnedIds] = useState(getPinnedFolders);
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = no search active, [] = no results
  const [searching, setSearching] = useState(false);
  const [searchLabel, setSearchLabel] = useState(""); // label shown above message list when viewing results
  const [conversationPrompt, setConversationPrompt] = useState(null); // { show, conversationId, opts }
  const [showTodoDialog, setShowTodoDialog] = useState(false);
  const [pendingTodoItem, setPendingTodoItem] = useState(null);
  const [todoLists, setTodoLists] = useState([]);
  const [todoCreating, setTodoCreating] = useState(false);
  const [showCalendarDialog, setShowCalendarDialog] = useState(false);
  const [pendingCalEvent, setPendingCalEvent] = useState(null);
  const [aiAssistData, setAiAssistData] = useState(null);
  const [aiAssistLoading, setAiAssistLoading] = useState(false);
  const [aiAssistError, setAiAssistError] = useState(null);
  const [showAiChat, setShowAiChat] = useState(false);
  // P1-7: AI Rules state
  const [showRulesPanel, setShowRulesPanel] = useState(false);
  const [aiRules, setAiRules] = useState([]);
  // P1-8: Lift selectedMatter from FilingSidebar to parent so ComposePanel can access it
  const [selectedMatter, setSelectedMatter] = useState(null);
  // Filing status: show "Filed by X" banner
  const [filingStatus, setFilingStatus] = useState(null);
  // Filed emails filter
  const [filedFilter, setFiledFilter] = useState(false);
  const [filedEmailIds, setFiledEmailIds] = useState(new Set());
  const [filedMessages, setFiledMessages] = useState([]);
  const [filedLoading, setFiledLoading] = useState(false);
  const [deepScanLoading, setDeepScanLoading] = useState(false);
  // One-click cleanup with streaming progress
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupProgress, setCleanupProgress] = useState(null);
  const [cleanupConfirm, setCleanupConfirm] = useState(null);
  // Triage: signal to AI Chat to auto-send "clear my inbox"
  const triageChatRef = useRef(false);

  const t = theme === "dark" ? DARK : LIGHT;

  // ── Stable header helper using ref (avoids stale closure + useEffect churn) ──
  const authUserIdRef = useRef(authUserId);
  useEffect(() => { authUserIdRef.current = authUserId; }, [authUserId]);

  const getHeaders = useCallback(() => {
    const uid = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || authUserIdRef.current || "";
    return uid ? { "X-Bonsai-User-Id": uid } : {};
  }, []);

  const toggleTheme = () => { const next = theme === "dark" ? "light" : "dark"; setTheme(next); setStoredTheme(next); };
  const togglePin = (fId) => {
    setPinnedIds(prev => {
      const next = prev.includes(fId) ? prev.filter(id => id !== fId) : [...prev, fId];
      setPinnedFolders(next);
      return next;
    });
  };

  // ── Filed emails filter toggle ────────────────────────────────
  const handleLoadFiled = useCallback(async () => {
    if (filedFilter) {
      // Toggle OFF — clear filter, go back to normal messages
      setFiledFilter(false);
      setFiledMessages([]);
      setDeepScanLoading(false);
      setCheckedIds(new Set());
      return;
    }
    // Toggle ON — dual-pass: quick tag scan, then deep SharePoint cross-reference
    setFiledLoading(true);
    setFiledFilter(true);
    setCheckedIds(new Set());

    // ── Pass 1: Category tag scan (fast) ──
    let pass1 = [];
    try {
      const folderId = activeFolderId || "inbox";
      const res = await axios.get(`${API}/bonsai/mail/filed-emails`, {
        params: { folder_id: folderId, top: 250 },
        headers: getHeaders()
      });
      pass1 = (res.data.messages || []).map(m => ({ ...m, _source: "tagged" }));
      // Fallback: if category-based detection found nothing, check backend DB
      if (pass1.length === 0 && filedEmailIds.size > 0) {
        pass1 = messages.filter(m => filedEmailIds.has(m.id)).map(m => ({ ...m, _source: "tagged" }));
      }
      setFiledMessages(pass1);
      if (pass1.length > 0) {
        toast.success(`Found ${pass1.length} tagged filed email${pass1.length !== 1 ? "s" : ""} — deep scan running…`);
      }
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load filed emails"));
    } finally {
      setFiledLoading(false);
    }

    // ── Pass 2: SharePoint cross-reference (slow, runs in background) ──
    setDeepScanLoading(true);
    try {
      const res2 = await axios.get(`${API}/bonsai/filing/cleanup/sharepoint-filed-inbox`, {
        params: { top: 1000 },
        headers: getHeaders()
      });
      const pass2Raw = res2.data.filed_emails || [];
      // Normalise Pass 2 results to same shape as Pass 1
      const pass2 = pass2Raw.map(e => ({
        id: e.id,
        subject: e.subject || "(No subject)",
        from_name: e.from_name || "",
        from_email: e.from_email || "",
        received: e.received || "",
        preview: "",
        is_read: true,
        has_attachments: false,
        importance: "normal",
        flagged: false,
        conversation_id: e.conversation_id || "",
        filed_to_sp: true,
        matter_reference: e.matter_reference || "",
        sp_filename: e.sp_filename || "",
        sp_path: e.sp_path || "",
        _source: "deep_scan",
      }));
      // Merge: deduplicate by id, Pass 1 (tagged) takes priority
      setFiledMessages(prev => {
        const seenIds = new Set(prev.map(m => m.id));
        const newFromDeep = pass2.filter(m => !seenIds.has(m.id));
        const merged = [...prev, ...newFromDeep];
        if (newFromDeep.length > 0) {
          toast.success(`Deep scan found ${newFromDeep.length} more untagged filed email${newFromDeep.length !== 1 ? "s" : ""}`);
        } else if (prev.length === 0 && pass2.length === 0) {
          toast.info("No filed emails found in inbox");
          setFiledFilter(false);
        } else if (newFromDeep.length === 0 && prev.length > 0) {
          toast.info("Deep scan complete — no additional emails found");
        }
        return merged;
      });
    } catch (e) {
      // Pass 2 failure is non-fatal — Pass 1 results still usable
      console.warn("Deep scan failed:", e);
      const msg = errorMessage(e, "Deep scan failed — showing tagged emails only");
      toast.warn?.(msg) || toast.info(msg);
    } finally {
      setDeepScanLoading(false);
    }
  }, [filedFilter, activeFolderId, getHeaders, filedEmailIds, messages]);

  // ── One-click cleanup (streaming, READ-ONLY) ─────────────────
  // Fix 1 (Vanessa, 20 May 2026): the sweep is strictly read-only.
  // Backend POST /me/messages/{id}/move loop has been removed; this
  // helper only ever issues a scan now. ``dryRun`` parameter
  // retained at call sites for diff-readability but is no longer
  // sent to the server — passing ``dry_run=false`` is rejected with
  // 422.
  const _streamCleanup = useCallback(async () => {
    const hdrs = getHeaders();
    const url = `${API}/bonsai/filing/cleanup/stream?top=1000`;
    const response = await fetch(url, { headers: hdrs });
    if (!response.ok) {
      // Surface the backend's specific message (structured `detail` dict or
      // plain string) instead of a bare "HTTP 500" — see errorMessage helper.
      let detail = "";
      try {
        const body = await response.json();
        const d = body?.detail;
        detail = (d && typeof d === "object" ? (d.message || d.error) : d) || body?.message || "";
      } catch { /* non-JSON body */ }
      throw new Error(detail || `Cleanup scan failed (HTTP ${response.status})`);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let lastEvt = null;
    // BM-H20 — track the error phase separately from lastEvt so the caller
    // can distinguish clean scan_done from a partial scan that hit an
    // error mid-stream. The backend now yields an ``error`` event with
    // ``{error, message, failed_phase}`` after a ``scan_done_partial``
    // when the generator dies.
    let errorEvt = null;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const parsed = JSON.parse(line.slice(6));
            if (parsed?.phase === 'error') {
              errorEvt = parsed;
              // Do NOT overwrite the last real progress event with the
              // error event — the caller wants the scan_done_partial that
              // preceded it.
            } else {
              lastEvt = parsed;
              setCleanupProgress(parsed);
            }
          } catch { /* ignore malformed */ }
        }
      }
    }
    // Attach the error, if any, to the returned event so the caller can
    // decide whether to render partial results + a warning or a full toast.
    if (errorEvt && lastEvt) {
      lastEvt._streamError = errorEvt;
    } else if (errorEvt && !lastEvt) {
      lastEvt = { phase: 'error', ...errorEvt, _streamError: errorEvt };
    }
    return lastEvt;
  }, [getHeaders]);

  const handleCleanup = useCallback(async () => {
    if (cleanupRunning) return;
    console.log("[Cleanup] Starting cleanup scan");
    const hdrs = getHeaders();
    console.log("[Cleanup] Headers:", JSON.stringify(hdrs));
    if (!hdrs["X-Bonsai-User-Id"]) {
      toast.error("Not signed in — cannot run cleanup");
      return;
    }
    setCleanupRunning(true);
    setCleanupProgress({ phase: "tag_scan", pct: 0, step: "Starting scan…", found: 0 });
    setCleanupConfirm(null);
    setFiledFilter(true);
    setFiledMessages([]);
    setCheckedIds(new Set());
    try {
      const finalEvt = await _streamCleanup();
      // BM-H20 — accept scan_done_partial as a rendering phase so Vanessa
      // still gets results (with a warning) when Graph dies mid-scan,
      // instead of a blank screen + generic error toast. Restores the
      // pre-regression behaviour: "give me results even if suspicious they
      // were not the full results".
      const isDone = finalEvt?.phase === "scan_done" || finalEvt?.phase === "scan_done_partial";
      const isPartial = finalEvt?.phase === "scan_done_partial" || finalEvt?.partial === true;
      const streamErr = finalEvt?._streamError;

      if (isDone && finalEvt.found > 0) {
        const allEmails = finalEvt.emails || [];
        // Fix 1 (20 May 2026): sweep is read-only. No bulk-delete
        // confirmation banner is shown; the candidate list renders
        // and the user removes items one-by-one with the existing
        // per-row delete control. We still flip cleanupRunning off
        // and clear the progress bar so the UI exits the "scanning"
        // state cleanly.
        setCleanupConfirm(null);
        setCleanupRunning(false);
        setCleanupProgress(null);
        // Populate filedMessages so user can see them in the list
        setFiledMessages(allEmails.map(e => ({
          id: e.id, subject: e.subject || "(No subject)",
          from_name: e.from_name || "", from_email: "", received: "",
          preview: "", is_read: true, has_attachments: false,
          importance: "normal", flagged: e.preserved && e.preserve_reason === "Flagged",
          conversation_id: "",
          filed_to_sp: true, matter_reference: e.matter_reference || "",
          _source: e.source || "tagged",
          _preserved: e.preserved || false,
          _preserve_reason: e.preserve_reason || "",
        })));
        if (isPartial) {
          const reason = streamErr?.message || streamErr?.error || `scan stopped in ${finalEvt.step || "an unknown phase"}`;
          toast.warning(`Partial results shown — ${allEmails.length} filed emails found before the scan stopped (${reason}). Re-run to try for a complete sweep.`, { duration: 10000 });
        }
      } else if (isDone && finalEvt.found === 0) {
        if (isPartial) {
          const reason = streamErr?.message || streamErr?.error || "scan stopped early";
          toast.error(`Cleanup scan stopped before finding anything — ${reason}`);
        } else {
          toast.info("No filed emails found in inbox");
        }
        setFiledFilter(false);
        setCleanupRunning(false);
        setCleanupProgress(null);
      } else if (finalEvt?.phase === "error" || streamErr) {
        // Stream opened OK, gave no scan_done at all, then died.
        const err = streamErr || finalEvt;
        toast.error(`Cleanup scan failed — ${err?.message || err?.error || "unknown error"}`);
        setCleanupRunning(false);
        setCleanupProgress(null);
      } else {
        setCleanupRunning(false);
        setCleanupProgress(null);
      }
    } catch (e) {
      // _streamCleanup throws an Error carrying the backend's specific message;
      // prefer it over the generic fallback so Vanessa sees what actually broke.
      toast.error(e?.message || errorMessage(e, "Cleanup scan failed"));
      setCleanupRunning(false);
      setCleanupProgress(null);
      setFiledFilter(false);
    }
  }, [cleanupRunning, _streamCleanup]);

  // Fix 1 (20 May 2026): the cleanup sweep no longer offers a bulk
  // "Delete N from inbox" action — the candidate list is read-only
  // and the user removes items one-by-one with the existing per-row
  // delete control. This callback is retained so the prop contract
  // stays stable for any cached FE bundle still calling it; it now
  // just dismisses whatever banner state remains.
  const handleCleanupConfirm = useCallback(async () => {
    setCleanupConfirm(null);
    setCleanupRunning(false);
    setCleanupProgress(null);
  }, []);

  const handleCleanupCancel = useCallback(() => {
    setCleanupConfirm(null);
    setCleanupRunning(false);
    setCleanupProgress(null);
    setFiledFilter(false);
    setFiledMessages([]);
  }, []);

  // ── Search handlers ──────────────────────────────────────────
  const doGlobalSearch = async (query) => {
    if (!query.trim()) { clearSearch(); return; }
    setSearching(true); setSearchLabel(`Search: "${query}"`);
    try {
      const res = await axios.get(`${API}/bonsai/mail/search`, { params: { q: query, top: 50 }, headers: getHeaders() });
      setSearchResults(res.data.messages || []);
      setSelectedMsgId(null); setSelectedEmail(null); setCheckedIds(new Set());
    } catch (e) { toast.error("Search failed"); setSearchResults([]); }
    finally { setSearching(false); }
  };

  const findConversation = async (msg) => {
    if (!msg?.id) return;
    setSearching(true); setSearchLabel(`Conversation: "${msg.subject}"`);
    try {
      const res = await axios.get(`${API}/bonsai/mail/messages/${encodeURIComponent(msg.id)}/conversation`, { params: { top: 50 }, headers: getHeaders() });
      setSearchResults(res.data.messages || []);
      setSelectedMsgId(null); setSelectedEmail(null); setCheckedIds(new Set());
      toast.success(`Found ${(res.data.messages || []).length} messages in conversation`);
    } catch { toast.error("Failed to find conversation"); setSearchResults([]); }
    finally { setSearching(false); }
  };

  const findFromSender = async (msg) => {
    const email = msg?.from_email;
    if (!email) return;
    setSearching(true); setSearchLabel(`From: ${msg.from_name || email}`);
    try {
      const res = await axios.get(`${API}/bonsai/mail/messages/by-sender`, { params: { email, top: 50 }, headers: getHeaders() });
      setSearchResults(res.data.messages || []);
      setSelectedMsgId(null); setSelectedEmail(null); setCheckedIds(new Set());
      toast.success(`Found ${(res.data.messages || []).length} messages from ${msg.from_name || email}`);
    } catch { toast.error("Failed to find messages from sender"); setSearchResults([]); }
    finally { setSearching(false); }
  };

  const clearSearch = () => {
    setSearchResults(null); setSearchQuery(""); setSearchLabel("");
    setFiledFilter(false); setFiledMessages([]);
  };

  // ── Load folders (single stable effect, no dependency churn) ────────
  const foldersLoadedRef = useRef(false);

  useEffect(() => {
    if (!authenticated || foldersLoadedRef.current) return;
    let cancelled = false;

    const loadFolders = async (attempt = 0) => {
      if (cancelled) return;
      const hdrs = getHeaders();
      if (!hdrs["X-Bonsai-User-Id"]) {
        if (attempt < 5) {
          console.warn(`[BonsaiMail] No user ID — retry ${attempt + 1}/5 in 600ms`);
          setTimeout(() => loadFolders(attempt + 1), 600);
        } else {
          if (!cancelled) {
            toast.error("Could not establish mail session — please refresh");
            setFoldersLoading(false);
          }
        }
        return;
      }
      if (!cancelled) setFoldersLoading(true);
      try {
        const res = await axios.get(`${API}/bonsai/mail/folders`, { headers: hdrs });
        if (cancelled) return;
        const flds = res.data.folders || [];
        setFolders(flds);
        foldersLoadedRef.current = true;
        const inbox = flds.find(f => f.name.toLowerCase() === "inbox");
        if (inbox) { setActiveFolderId(inbox.id); setActiveFolderName(inbox.name); }
      } catch (e) {
        if (cancelled) return;
        const detail = e.response?.data?.detail || "";
        if (detail.includes("Not authenticated") || detail.includes("expired")) {
          toast.error("Microsoft session expired — please sign in again");
        } else if (attempt < 2) {
          console.warn(`[BonsaiMail] Folder load error — retry ${attempt + 1}/2`);
          setTimeout(() => loadFolders(attempt + 1), 1500);
          return;
        } else {
          toast.error("Failed to load mail folders");
        }
        console.error("[BonsaiMail] Folder load error:", detail || e.message);
      } finally {
        if (!cancelled) setFoldersLoading(false);
      }
    };

    loadFolders(0);
    return () => { cancelled = true; };
  }, [authenticated]); // minimal deps — getHeaders is stable, no bonsaiApi

  const MAX_EMAILS = 1000;
  const PAGE_SIZE = 50;
  const [loadProgress, setLoadProgress] = useState(null); // { loaded, total } or null
  const totalEstimateRef = useRef(null);

  const loadMessages = useCallback(async (folderId) => {
    if (!folderId) return;
    setMessagesLoading(true);
    setLoadProgress(null);
    totalEstimateRef.current = null;
    setCheckedIds(new Set()); setSelectedMsgId(null); setSelectedEmail(null);
    try {
      // First page — show immediately
      const res = await axios.get(`${API}/bonsai/mail/folders/${encodeURIComponent(folderId)}/messages`, { params: { skip: 0, top: PAGE_SIZE }, headers: getHeaders() });
      let allMsgs = res.data.messages || [];
      const totalEstimate = res.data.total_count || null;
      totalEstimateRef.current = totalEstimate;
      let nextLink = res.data.next_link;

      setMessages(allMsgs);
      if (nextLink) setLoadProgress({ loaded: allMsgs.length, total: totalEstimate });

      // Auto-fetch remaining pages in background
      while (nextLink && allMsgs.length < MAX_EMAILS) {
        const pageRes = await axios.get(`${API}/bonsai/mail/folders/${encodeURIComponent(folderId)}/messages`, { params: { skip: allMsgs.length, top: PAGE_SIZE }, headers: getHeaders() });
        const pageMsgs = pageRes.data.messages || [];
        if (pageMsgs.length === 0) break;
        allMsgs = [...allMsgs, ...pageMsgs];
        nextLink = pageRes.data.next_link;
        setMessages(allMsgs);
        setLoadProgress({ loaded: allMsgs.length, total: totalEstimate || allMsgs.length });
      }

      setSkip(allMsgs.length);
      const stillMore = nextLink && allMsgs.length >= MAX_EMAILS;
      setHasMore(stillMore);
      setLoadProgress(stillMore ? { loaded: allMsgs.length, total: totalEstimate, capped: true } : null);

    } catch { toast.error("Failed to load messages"); }
    finally { setMessagesLoading(false); }
  }, [getHeaders]);

  // Load more beyond the initial cap
  const loadMoreMessages = useCallback(async () => {
    if (!activeFolderId || messagesLoading) return;
    setMessagesLoading(true);
    try {
      const currentSkip = messages.length;
      const pageRes = await axios.get(`${API}/bonsai/mail/folders/${encodeURIComponent(activeFolderId)}/messages`, { params: { skip: currentSkip, top: PAGE_SIZE }, headers: getHeaders() });
      const pageMsgs = pageRes.data.messages || [];
      if (pageMsgs.length === 0) { setHasMore(false); return; }
      const updated = [...messages, ...pageMsgs];
      setMessages(updated);
      setSkip(updated.length);
      const nextLink = pageRes.data.next_link;
      setHasMore(!!nextLink && pageMsgs.length === PAGE_SIZE);
      setLoadProgress(nextLink ? { loaded: updated.length, total: totalEstimateRef.current, capped: true } : null);
    } catch { toast.error("Failed to load more messages"); }
    finally { setMessagesLoading(false); }
  }, [activeFolderId, messages, messagesLoading, getHeaders]);

  useEffect(() => { if (activeFolderId) loadMessages(activeFolderId); }, [activeFolderId]);

  const loadEmail = useCallback(async (msgId) => {
    if (!msgId) return;
    setEmailLoading(true); setSelectedMsgId(msgId);
    setAiAssistData(null); setAiAssistError(null);  // Reset AI assist when switching emails
    setFilingStatus(null); // Reset filing status
    try {
      const res = await axios.get(`${API}/bonsai/mail/messages/${encodeURIComponent(msgId)}`, { headers: getHeaders() });
      const emailData = res.data;
      setSelectedEmail(emailData);
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, is_read: true } : m));

      // Fetch attachments in background if email has any
      if (emailData.has_attachments) {
        axios.get(`${API}/bonsai/emails/${encodeURIComponent(msgId)}/attachments`, { headers: getHeaders() })
          .then(r => {
            const atts = (r.data.attachments || []).filter(a => !a.isInline);
            setSelectedEmail(prev => prev && prev.id === msgId ? { ...prev, attachments: atts } : prev);
          })
          .catch(() => {});
      }

      // Check filing status in background
      const imid = emailData.internetMessageId ? `&internet_message_id=${encodeURIComponent(emailData.internetMessageId)}` : "";
      axios.get(`${API}/bonsai/filing/check-filed?email_id=${encodeURIComponent(msgId)}${imid}`, { headers: getHeaders() })
        .then(r => {
          if (r.data.filed) {
            setFilingStatus(r.data.filing);
            // Mark as filed in message list so the green icon shows for colleagues too
            setMessages(prev => prev.map(m => m.id === msgId ? { ...m, filed_to_sp: true } : m));
          }
        })
        .catch(() => {});
    } catch { toast.error("Failed to load email"); }
    finally { setEmailLoading(false); }
  }, [getHeaders]);

  const runAiAssist = useCallback(async () => {
    if (!selectedMsgId) { toast.error("Select an email first"); return; }
    setAiAssistLoading(true); setAiAssistError(null);
    try {
      const res = await axios.post(`${API}/bonsai/mail/messages/${encodeURIComponent(selectedMsgId)}/ai-assist`, {}, { headers: getHeaders() });
      setAiAssistData(res.data);
      if (!res.data.ai_available) toast.info("AI service unavailable — showing fallback");
    } catch (e) {
      setAiAssistError(errorMessage(e, "AI analysis failed"));
      toast.error("AI analysis failed");
    } finally { setAiAssistLoading(false); }
  }, [selectedMsgId, getHeaders]);

  // Auto-run AI assist when Assist tab is active and email changes
  // Use selectedMsgId directly (not runAiAssist) to avoid infinite loop
  useEffect(() => {
    if (ribbonTab === "Assist" && selectedMsgId && !aiAssistLoading) {
      runAiAssist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ribbonTab, selectedMsgId]); // intentionally omit runAiAssist to prevent loop

  // Clear "Filed to SharePoint" category from the current email
  const handleClearFiledTag = useCallback(async () => {
    if (!selectedMsgId) return;
    try {
      await axios.post(`${API}/bonsai/filing/clear-category`, { email_id: selectedMsgId }, { headers: getHeaders() });
      setFilingStatus(null);
      toast.success("Filed tag removed");
    } catch (e) { toast.error(errorMessage(e, "Failed to clear tag")); }
  }, [selectedMsgId, getHeaders]);

  // Create To Do from extracted task — opens the modal dialog pre-populated
  const handleCreateTodoFromTask = useCallback((description, dueDate, priority) => {
    setPendingTodoItem({
      description: description || "",
      dueDate: dueDate || new Date(Date.now() + 86400000).toISOString().split("T")[0],
      priority: priority === "high" ? "high" : priority === "low" ? "low" : "normal",
    });
    setShowTodoDialog(true);
  }, []);

  // Create Calendar event from extracted task/deadline
  // Create Calendar event — open the dialog pre-populated
  const handleCreateCalendarEvent = useCallback(async (description, dueDate, time) => {
    setPendingCalEvent({
      description: description || `Event: ${selectedEmail?.subject || "(No subject)"}`,
      dueDate: dueDate || new Date(Date.now() + 86400000).toISOString().split("T")[0],
      startTime: time || "09:00",
      endTime: time ? undefined : "09:30",
    });
    setShowCalendarDialog(true);
  }, [selectedEmail]);

  // File entire conversation from AI Assist panel
  const handleAssistFileConversation = useCallback(async () => {
    if (!selectedEmail?.conversationId) {
      toast.error("No conversation thread found");
      return;
    }
    if (!selectedMatter?.driveId || !selectedMatter?.itemId) {
      setShowFiling(true);
      setRibbonTab("Home");
      toast.info("Select a matter in the filing sidebar first");
      return;
    }
    setFilingInProgress(true);
    try {
      const subRes = await axios.get(`${API}/bonsai/mail/matters/${selectedMatter.driveId}/${selectedMatter.itemId}/subfolders`, { headers: getHeaders() });
      const subs = subRes.data.subfolders || [];
      // Always file to Attendances — Correspondence folder is legacy, do not use
      const att = subs.find(s => s.name.toLowerCase().includes("attendan"));
      const targetFolder = att || subs[0];
      if (!targetFolder) {
        toast.error("No subfolders found in matter folder");
        return;
      }
      const res = await axios.post(`${API}/bonsai/filing/conversation`, {
        conversation_id: selectedEmail.conversationId,
        // PR #466 — Assist-tab "File conversation" button click.
        // assist_suggest is the right vocab term — Assist routinely
        // surfaces this action after analysing the email; the user
        // confirmed by clicking.
        user_intent: 'assist_suggest',
        subject: selectedEmail.subject || "",
        drive_id: selectedMatter.driveId,
        folder_id: targetFolder.id,
        include_attachments: true,
      }, { headers: getHeaders() });
      if (res.data?.batch_id) {
        toast.success(`Filing ${res.data.total || "all"} messages in conversation to ${selectedMatter.reference || selectedMatter.title || "matter"}...`);
      }
    } catch (e) {
      toast.error(errorMessage(e, "Failed to file conversation"));
    } finally {
      setFilingInProgress(false);
    }
  }, [selectedEmail, selectedMatter, getHeaders]);

  // File checked emails — switch to filing sidebar
  const handleAssistFileChecked = useCallback(() => {
    setShowFiling(true);
    setRibbonTab("Home");
    toast.info("Select a matter and click 'File to SP' to file the selected emails");
  }, []);

  const getTargetIds = (singleId) => {
    if (singleId) return [singleId];
    if (checkedIds.size > 0) return [...checkedIds];
    if (selectedMsgId) return [selectedMsgId];
    if (selectedEmail?.id) return [selectedEmail.id];
    return [];
  };

  const handleFileToSharePoint = async (opts) => {
    // Ensure deleteAfterFiling is resolved even when callers (e.g. SharePointBrowser)
    // don't explicitly pass it — fall back to the persisted user preference.
    if (opts.deleteAfterFiling === undefined) {
      try { opts.deleteAfterFiling = localStorage.getItem("bonsai_delete_after_filing") === "true"; } catch { /* ignore */ }
    }

    const ids = getTargetIds();
    if (ids.length === 0) { toast.error("No emails selected"); return; }
    setFilingInProgress(true);
    setFilingProgressMsg(`Filing ${ids.length} email${ids.length !== 1 ? "s" : ""}...`);

    // For batches > 5 emails, use the async batch endpoint with progress polling
    if (ids.length > 5) {
      try {
        const res = await axios.post(`${API}/bonsai/filing/batch`, {
          email_ids: ids,
          // PR #466 — handleFileToSharePoint is wired to "File to SP"
          // buttons throughout the UI (EmailPanel, ribbon, etc.). Each
          // is a click that user-confirms the destination.
          user_intent: 'click_file',
          drive_id: opts.driveId,
          folder_id: opts.folderId,
          include_attachments: opts.includeAttachments || false,
          matter_reference: selectedMatter?.reference || "",
          matter_id: selectedMatter?.itemId || "",
        }, { headers: getHeaders() });

        const batchId = res.data.batch_id;
        const total = res.data.total || ids.length;
        if (!batchId) throw new Error("No batch ID returned");

        // Poll for progress every 3 seconds
        let completed = 0, failed = 0, filedIds = [], failedItems = [];
        while (completed + failed < total) {
          await new Promise(r => setTimeout(r, 3000));
          const statusRes = await axios.get(`${API}/bonsai/filing/batch/${batchId}`, { headers: getHeaders() });
          const batch = statusRes.data.batch || {};
          const items = statusRes.data.items || [];
          completed = batch.completed || 0;
          failed = batch.failed || 0;
          // Enhancement 2: Mark each email as filed in real-time for green check overlay
          const completedItems = items.filter(i => i.status === "completed").map(i => i.email_id);
          if (completedItems.length > 0) {
            setFiledEmailIds(prev => { const next = new Set(prev); completedItems.forEach(id => next.add(id)); return next; });
            setMessages(prev => prev.map(m => completedItems.includes(m.id) ? { ...m, filed_to_sp: true } : m));
          }
          setFilingProgressMsg(`Filing ${ids.length} emails — ${completed} done, ${failed} failed...`);
          if (batch.status === "completed" || batch.status === "failed") break;
        }

        // Final status
        const statusRes = await axios.get(`${API}/bonsai/filing/batch/${batchId}`, { headers: getHeaders() });
        const items = statusRes.data.items || [];
        filedIds = items.filter(i => i.status === "completed").map(i => i.email_id);
        failedItems = items.filter(i => i.status === "failed").map(i => ({
          subject: messages.find(m => m.id === i.email_id)?.subject || i.email_id?.slice(0, 30) + "...",
          error: i.error || "Unknown error"
        }));
        completed = filedIds.length;
        failed = failedItems.length;

        if (completed > 0 && failed === 0) {
          toast.success(`Filed ${completed} of ${total} emails to SharePoint`);
        } else if (completed > 0 && failed > 0) {
          toast.warning(`Filed ${completed} of ${total} — ${failed} failed`, {
            description: failedItems.map(f => `${f.subject}: ${f.error}`).join("; "),
            duration: 10000,
          });
        } else {
          toast.error(`All ${total} emails failed to file`, {
            description: failedItems.slice(0, 3).map(f => `${f.subject}: ${f.error}`).join("; "),
            duration: 10000,
          });
        }

        // Delete after filing
        if (opts.deleteAfterFiling && filedIds.length > 0) {
          setFilingProgressMsg(`Deleting ${filedIds.length} filed email${filedIds.length !== 1 ? "s" : ""}...`);
          try {
            const delRes = await axios.post(`${API}/bonsai/mail/messages/delete`, { message_ids: filedIds }, { headers: getHeaders() });
            const deleted = delRes.data.deleted || 0;
            if (deleted > 0) toast.success(`Deleted ${deleted} filed email${deleted !== 1 ? "s" : ""} from inbox`);
            setMessages(prev => prev.filter(m => !filedIds.includes(m.id)));
            setCheckedIds(prev => { const n = new Set(prev); filedIds.forEach(id => n.delete(id)); return n; });
            if (filedIds.includes(selectedMsgId)) { setSelectedMsgId(null); setSelectedEmail(null); }
          } catch { toast.error("Filed successfully but failed to delete from inbox"); }
        } else if (completed > 0) {
          setMessages(prev => prev.map(m => filedIds.includes(m.id) ? { ...m, filed_to_sp: true } : m));
          setCheckedIds(prev => { const n = new Set(prev); filedIds.forEach(id => n.delete(id)); return n; });
        }
      } catch (e) {
        const msg = errorMessage(e, "Unknown error");
        toast.error(`Batch filing failed: ${msg}`);
      } finally { setFilingInProgress(false); setFilingProgressMsg(""); }
      return;
    }

    // For 1-5 emails use the synchronous endpoint (faster, no polling overhead)
    try {
      const res = await axios.post(`${API}/bonsai/mail/messages/file-to-sharepoint`, {
        message_ids: ids,
        // PR #466 — same gesture path as the >5 branch above
        // (handleFileToSharePoint is the "File to SP" button click).
        user_intent: 'click_file',
        drive_id: opts.driveId, folder_id: opts.folderId,
        include_attachments: opts.includeAttachments,
        attachment_drive_id: opts.attachmentDriveId, attachment_folder_id: opts.attachmentFolderId,
      }, { headers: getHeaders() });
      // Enhancement 6: Call onSuccess callback if filing succeeds (for File & Reply)
      if (res.data?.filed > 0 && opts._onSuccess) { try { opts._onSuccess(); } catch {} }
      const results = res.data.results || [];
      const filed = res.data.filed || 0;
      const atts = res.data.total_attachments || 0;
      const failed = ids.length - filed;

      const filedIds = [];
      const failedItems = [];
      for (const r of results) {
        if (r.success) {
          filedIds.push(r.id);
        } else {
          const subj = r.subject || messages.find(m => m.id === r.id)?.subject || r.id?.slice(0, 20) + "...";
          failedItems.push({ subject: subj, error: r.error || r.message || "Unknown error" });
        }
      }

      if (filed > 0 && failed === 0) {
        toast.success(`Filed ${filed} email${filed !== 1 ? "s" : ""}${atts > 0 ? ` + ${atts} attachments` : ""} to SharePoint`);
        if (ids.length === 1 && selectedEmail?.conversationId) {
          setConversationPrompt({ show: true, conversationId: selectedEmail.conversationId,
            opts: { driveId: opts.driveId, folderId: opts.folderId, includeAttachments: opts.includeAttachments } });
        }
      } else if (filed > 0 && failed > 0) {
        toast.warning(`Filed ${filed} of ${ids.length} — ${failed} failed`, {
          description: failedItems.map(f => `${f.subject}: ${f.error}`).join("; "),
          duration: 8000,
        });
      } else {
        const failDetail = failedItems.length > 0
          ? failedItems.map(f => `${f.subject}: ${f.error}`).join("; ")
          : results?.[0]?.error || "Upload may have failed — check SharePoint";
        toast.error(`Filing failed`, { description: failDetail, duration: 8000 });
      }

      if (opts.deleteAfterFiling && filedIds.length > 0) {
        setFilingProgressMsg(`Deleting ${filedIds.length} filed email${filedIds.length !== 1 ? "s" : ""}...`);
        try {
          const delRes = await axios.post(`${API}/bonsai/mail/messages/delete`, { message_ids: filedIds }, { headers: getHeaders() });
          const deleted = delRes.data.deleted || 0;
          if (deleted > 0) toast.success(`Deleted ${deleted} email${deleted !== 1 ? "s" : ""} from inbox`);
          setMessages(prev => prev.filter(m => !filedIds.includes(m.id)));
          setCheckedIds(prev => { const n = new Set(prev); filedIds.forEach(id => n.delete(id)); return n; });
          if (filedIds.includes(selectedMsgId)) { setSelectedMsgId(null); setSelectedEmail(null); }
        } catch { toast.error("Filed successfully but failed to delete from inbox"); }
      }
      if (filed > 0 && !opts.deleteAfterFiling) {
        setMessages(prev => prev.map(m => filedIds.includes(m.id) ? { ...m, filed_to_sp: true } : m));
        setCheckedIds(prev => { const n = new Set(prev); filedIds.forEach(id => n.delete(id)); return n; });
      }
    } catch (e) {
      toast.error(`Filing failed: ${errorMessage(e, "Unknown error")}`);
    }
    finally { setFilingInProgress(false); setFilingProgressMsg(""); }
  };

  const handleFileConversation = (batchData) => {
    // The FilingSidebar already handles the conversation filing
    // This callback is for any follow-up actions like refreshing the list
    if (batchData?.batch_id) {
      // Could poll for progress here if needed
      toast.info(`Conversation filing started (batch ${batchData.batch_id})`);
    }
  };

  const handleConversationPromptFile = async () => {
    if (!conversationPrompt?.opts || !selectedEmail?.conversationId) return;
    const { driveId, folderId, includeAttachments } = conversationPrompt.opts;
    setConversationPrompt(null);
    setFilingInProgress(true);
    try {
      const res = await axios.post(`${API}/bonsai/filing/conversation`, {
        conversation_id: selectedEmail.conversationId,
        // PR #466 — handleConversationPromptFile fires when the user
        // clicks "Yes, file the conversation" on the post-file
        // confirmation prompt. Click = click_file.
        user_intent: 'click_file',
        subject: selectedEmail.subject || "",
        drive_id: driveId,
        folder_id: folderId,
        include_attachments: includeAttachments || false,
      }, { headers: getHeaders() });
      if (res.data?.batch_id) {
        toast.success(`Filing ${res.data.total || "all"} messages in conversation...`);
      } else {
        toast.error("Failed to start conversation filing");
      }
    } catch (e) {
      toast.error(errorMessage(e, "Failed to file conversation"));
    } finally { setFilingInProgress(false); }
  };

  const handleDelete = async (singleId) => {
    const ids = getTargetIds(singleId);
    if (ids.length === 0) { toast.error("No emails selected"); return; }
    try {
      const res = await axios.post(`${API}/bonsai/mail/messages/delete`, { message_ids: ids }, { headers: getHeaders() });
      toast.success(`Deleted ${res.data.deleted || 0} email(s)`);
      setMessages(prev => prev.filter(m => !ids.includes(m.id)));
      if (filedFilter) setFiledMessages(prev => prev.filter(m => !ids.includes(m.id)));
      setCheckedIds(prev => { const n = new Set(prev); ids.forEach(id => n.delete(id)); return n; });
      if (ids.includes(selectedMsgId)) { setSelectedMsgId(null); setSelectedEmail(null); }
    } catch { toast.error("Delete failed"); }
  };

  // Select only emails with filed indicator
  const handleSelectFiled = useCallback(() => {
    const msgs = filedFilter ? filedMessages : (searchResults !== null ? searchResults : messages);
    const filedIds = msgs.filter(m => m.filed_to_sp || filedEmailIds?.has(m.id)).map(m => m.id);
    setCheckedIds(new Set(filedIds));
  }, [filedFilter, filedMessages, searchResults, messages, filedEmailIds]);

  // Bulk delete all filed emails in view
  const handleClearFiled = useCallback(async () => {
    const msgs = filedFilter ? filedMessages : (searchResults !== null ? searchResults : messages);
    const filedIds = msgs.filter(m => m.filed_to_sp || filedEmailIds?.has(m.id)).map(m => m.id);
    if (filedIds.length === 0) { toast.error("No filed emails found"); return; }
    try {
      const res = await axios.post(`${API}/bonsai/mail/messages/delete`, { message_ids: filedIds }, { headers: getHeaders() });
      const count = res.data.deleted || filedIds.length;
      toast.success(`${count} filed email${count !== 1 ? "s" : ""} removed from inbox`);
      setMessages(prev => prev.filter(m => !filedIds.includes(m.id)));
      if (filedFilter) setFiledMessages(prev => prev.filter(m => !filedIds.includes(m.id)));
      setCheckedIds(prev => { const n = new Set(prev); filedIds.forEach(id => n.delete(id)); return n; });
      if (filedIds.includes(selectedMsgId)) { setSelectedMsgId(null); setSelectedEmail(null); }
    } catch { toast.error("Failed to delete filed emails"); }
  }, [filedFilter, filedMessages, searchResults, messages, filedEmailIds, getHeaders, selectedMsgId]);

  const handleMove = async (destFolderId, singleId) => {
    const ids = getTargetIds(singleId);
    if (ids.length === 0) { toast.error("No emails selected"); return; }
    try {
      const res = await axios.post(`${API}/bonsai/mail/messages/move`, { message_ids: ids, destination_folder_id: destFolderId }, { headers: getHeaders() });
      toast.success(`Moved ${res.data.moved || 0} email(s)`);
      setMessages(prev => prev.filter(m => !ids.includes(m.id)));
      setCheckedIds(prev => { const n = new Set(prev); ids.forEach(id => n.delete(id)); return n; });
      if (ids.includes(selectedMsgId)) { setSelectedMsgId(null); setSelectedEmail(null); }
    } catch { toast.error("Move failed"); }
  };

  const handleArchive = async (singleId) => {
    const archiveFolder = folders.find(f => f.name.toLowerCase() === "archive");
    if (!archiveFolder) { toast.error("Archive folder not found"); return; }
    handleMove(archiveFolder.id, singleId);
  };

  const handleFlag = async (msgId) => {
    // Toggle flag visual locally (Graph API flag would need PATCH /messages/{id})
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, flagged: !m.flagged } : m));
    toast.success("Flag toggled");
  };

  // Enhancement 1+5: Mark as Read/Unread with API call and local state update
  const handleMarkRead = async (msgId, markRead) => {
    try {
      await axios.patch(`${API}/bonsai/mail/messages/${encodeURIComponent(msgId)}`, { is_read: markRead }, { headers: getHeaders() });
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, is_read: markRead } : m));
      // Update folder unread counts
      setFolders(prev => prev.map(f => {
        if (f.id === activeFolderId) {
          return { ...f, unread: Math.max(0, (f.unread || 0) + (markRead ? -1 : 1)) };
        }
        return f;
      }));
      toast.success(markRead ? "Marked as read" : "Marked as unread");
    } catch { toast.error("Failed to update read status"); }
  };

  const handleDropToFolder = (destFolderId) => {
    // Get IDs from the drag data — we use checked or selected
    const ids = checkedIds.size > 0 ? [...checkedIds] : selectedMsgId ? [selectedMsgId] : [];
    if (ids.length === 0) return;
    handleMove(destFolderId);
  };

  const openCompose = (mode, replyTo = null) => { setComposeMode(mode); setComposeReplyTo(replyTo); };

  // Enhancement 6: Wire File & Reply callback from FilingSidebar
  useEffect(() => {
    window.__bonsaiOpenReply = (emailData) => { openCompose("reply", emailData); };
    return () => { delete window.__bonsaiOpenReply; };
  }, []);

  // Auth gate
  if (bonsaiLoading) return <div className={`min-h-screen ${t.bg} flex items-center justify-center`}><Loader2 className={`w-6 h-6 animate-spin ${t.textAccentDim}`} /></div>;
  if (!authenticated) return (
    <div className={`min-h-screen ${t.bg} flex items-center justify-center p-8`}>
      <div className="text-center max-w-md">
        <Mail className={`w-12 h-12 mx-auto mb-4 ${t.textAccentDim} opacity-60`} />
        <h2 className={`text-xl font-semibold ${t.text} mb-2`}>Bonsai Mail Online</h2>
        <p className={`${t.textMuted} text-sm mb-6`}>Browse your inbox, file emails to SharePoint matters, and manage your email — all from CaseMinder.</p>
        {auth_error && <p className="text-status-red text-xs mb-4">{auth_error}</p>}
        <Button onClick={() => login && login()} className="bg-blue hover:bg-blue text-white px-6" data-testid="ms-sign-in-btn">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#f25022"/><rect x="11" y="1" width="9" height="9" fill="#7fba00"/><rect x="1" y="11" width="9" height="9" fill="#00a4ef"/><rect x="11" y="11" width="9" height="9" fill="#ffb900"/></svg>
          Sign in with Microsoft
        </Button>
      </div>
    </div>
  );

  const hasSelection = checkedIds.size > 0 || !!selectedMsgId;
  const hasEmail = !!selectedEmail;

  const folderPanelProps = {
    folders, activeFolderId, loading: foldersLoading, pinnedIds, onTogglePin: togglePin,
    onSelect: (id, name) => { clearSearch(); setActiveFolderId(id); setActiveFolderName(name); },
    onDropToFolder: handleDropToFolder, t,
  };

  // When search results are active, show those instead of folder messages
  // When filed filter is active, show only filed emails
  const displayMessages = filedFilter ? filedMessages : (searchResults !== null ? searchResults : messages);
  const displayLoading = filedLoading || searching || messagesLoading;
  const displayFolderName = filedFilter ? `Filed emails in ${activeFolderName || "Inbox"}` : (searchLabel || activeFolderName);

  const msgListProps = {
    messages: displayMessages, loading: displayLoading, selectedId: selectedMsgId, onSelect: loadEmail,
    checkedIds, onToggleCheck: id => setCheckedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }),
    onSelectAll: () => setCheckedIds(new Set(displayMessages.map(m => m.id))), onDeselectAll: () => setCheckedIds(new Set()),
    folderName: displayFolderName, onRefresh: filedFilter ? handleLoadFiled : (searchResults !== null ? () => {} : () => loadMessages(activeFolderId)),
    onLoadMore: (filedFilter || searchResults !== null) ? () => {} : loadMoreMessages,
    hasMore: (filedFilter || searchResults !== null) ? false : hasMore,
    onDelete: handleDelete, onArchive: handleArchive, onFlag: handleFlag,
    onFindConversation: findConversation, onFindFromSender: findFromSender,
    onFileSelected: (msg) => { setSelectedMsgId(msg.id); loadEmail(msg.id); setShowFiling(true); },
    isSearchActive: searchResults !== null, onClearSearch: clearSearch,
    onLoadFiled: handleLoadFiled, filedFilter, filedLoading, deepScanLoading,
    onMove: handleMove, onMarkRead: handleMarkRead, folders, filedEmailIds,
    onTriageNow: () => { setRibbonTab("Assist"); setShowAiChat(true); triageChatRef.current = true; },
    loadProgress,
    onSelectFiled: handleSelectFiled, onClearFiled: handleClearFiled,
    onCleanup: handleCleanup, cleanupRunning, cleanupProgress, cleanupConfirm,
    onCleanupConfirm: handleCleanupConfirm, onCleanupCancel: handleCleanupCancel,
    t,
  };

  const aiAssistPanel = ribbonTab === "Assist" ? (
    <div className={`w-[320px] border-l ${t.border} ${t.bgPanel} flex-shrink-0 overflow-hidden flex flex-col`} data-testid="ai-assist-sidebar">
      {showAiChat ? (
        <>
          <div className={`px-4 py-2 border-b ${t.border} flex items-center gap-2`}>
            <MessageSquare className="w-4 h-4 text-blue-400" />
            <span className="text-xs font-bold uppercase tracking-widest text-blue-400">AI Chat</span>
            <button onClick={() => setShowAiChat(false)} className={`ml-auto text-[10px] ${t.textMuted} hover:${t.text}`} data-testid="ai-chat-back-btn">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <AiChatPanel t={t} getHeaders={getHeaders} checkedIds={checkedIds} onOpenFileDialog={handleAssistFileChecked} checkedCount={checkedIds.size} setCheckedIds={setCheckedIds} messages={displayMessages} filedEmailIds={filedEmailIds} folders={folders} onFileEmails={handleFileToSharePoint} onArchiveEmails={handleArchive} onFlagEmails={handleFlag} triageChatRef={triageChatRef} />
        </>
      ) : (
        <>
          <div className={`px-4 py-2 border-b ${t.border} flex items-center gap-2`}>
            <Sparkles className="w-4 h-4 text-purple-400" />
            <span className="text-xs font-bold uppercase tracking-widest text-purple-400">AI Assist</span>
            <span className={`ml-auto text-[9px] ${t.textDim}`}>Email Analysis</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            <AiAssistPanel data={aiAssistData} loading={aiAssistLoading} error={aiAssistError} onRun={runAiAssist} hasEmail={!!selectedEmail} emailSubject={selectedEmail?.subject} onCreateTodo={handleCreateTodoFromTask} onCreateCalendarEvent={handleCreateCalendarEvent} onFileConversation={handleAssistFileConversation} conversationId={selectedEmail?.conversationId} checkedCount={checkedIds.size} onFileChecked={handleAssistFileChecked} filingInProgress={filingInProgress} t={t} getHeaders={getHeaders} checkedIds={checkedIds} onOpenFileDialog={handleAssistFileChecked} selectedEmail={selectedEmail} selectedMatter={selectedMatter} />
          </div>
        </>
      )}
    </div>
  ) : null;

  const renderContent = () => {
    const filingOrAssist = aiAssistPanel || (showFiling && <FilingSidebar email={selectedEmail} onFileToSharePoint={handleFileToSharePoint} onFileConversation={handleFileConversation} onDelete={handleDelete} onMove={handleMove} filingInProgress={filingInProgress} filingProgressMsg={filingProgressMsg} checkedCount={checkedIds.size} folders={folders} t={t} draggedMsgIds={checkedIds.size > 0 ? [...checkedIds] : selectedMsgId ? [selectedMsgId] : []} onDropFiled={() => {}} selectedMatter={selectedMatter} setSelectedMatter={setSelectedMatter} onCreateTodo={handleCreateTodoFromTask} onCreateCalendarEvent={handleCreateCalendarEvent} />);
    if (readingPane === "right") {
      return (
        <div className="flex-1 flex overflow-hidden">
          <FolderPanel {...folderPanelProps} />
          <MessageList {...msgListProps} layout="right" />
          <EmailPreview email={selectedEmail} loading={emailLoading} t={t} theme={theme} filingStatus={filingStatus} onClearFiledTag={handleClearFiledTag} />
          {filingOrAssist}
        </div>
      );
    }
    if (readingPane === "bottom") {
      return (
        <div className="flex-1 flex overflow-hidden">
          <FolderPanel {...folderPanelProps} />
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className={`h-[45%] border-b ${t.border} overflow-hidden`}>
              <MessageList {...msgListProps} layout="bottom" />
            </div>
            <div className="flex-1 overflow-hidden">
              <EmailPreview email={selectedEmail} loading={emailLoading} t={t} theme={theme} filingStatus={filingStatus} onClearFiledTag={handleClearFiledTag} />
            </div>
          </div>
          {filingOrAssist}
        </div>
      );
    }
    return (
      <div className="flex-1 flex overflow-hidden">
        <FolderPanel {...folderPanelProps} />
        <MessageList {...msgListProps} layout="off" />
        {filingOrAssist}
      </div>
    );
  };

  return (
    <div className={`h-[calc(100vh-64px)] flex flex-col ${t.bg}`} data-testid="bonsai-mail-online">
      <RibbonBar
        activeTab={ribbonTab} onTabChange={setRibbonTab}
        onNewEmail={() => openCompose("new")}
        onReply={() => openCompose("reply", selectedEmail)}
        onReplyAll={() => openCompose("replyAll", selectedEmail)}
        onForward={() => openCompose("forward", selectedEmail)}
        onDelete={() => handleDelete()} onArchive={() => handleArchive()}
        onSignature={() => setShowSigSettings(true)}
        readingPane={readingPane} onReadingPaneChange={setReadingPane}
        hasSelection={hasSelection} hasEmail={hasEmail}
        showFiling={showFiling} onToggleFiling={() => setShowFiling(p => !p)}
        theme={theme} onToggleTheme={toggleTheme} t={t}
        searchQuery={searchQuery} onSearchChange={q => { setSearchQuery(q); if (!q) clearSearch(); }}
        onSearchSubmit={doGlobalSearch} searching={searching}
        onCreateTask={() => setShowTodoDialog(true)}
        onAiAssist={() => { setShowAiChat(false); runAiAssist(); }} aiAssistLoading={aiAssistLoading}
        onAiChat={() => setShowAiChat(p => !p)} aiChatActive={showAiChat}
        onAIRules={() => setShowRulesPanel(p => !p)}
      />
      {renderContent()}
      {composeMode && (
        <ComposePanel bonsaiApi={bonsaiApi} mode={composeMode} replyTo={composeReplyTo}
          defaultFileMatter={selectedMatter}
          onClose={() => { setComposeMode(null); setComposeReplyTo(null); }}
          onSent={() => { if (activeFolderId) setTimeout(() => loadMessages(activeFolderId), 1500); }} />
      )}
      {showSigSettings && <SignatureSettings bonsaiApi={bonsaiApi} onClose={() => setShowSigSettings(false)} />}
      {/* Conversation Prompt — appears after filing a single email that has a conversationId */}
      {conversationPrompt?.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="conversation-prompt-overlay">
          <div className={`${t.bgCard} border ${t.border} rounded-xl shadow-2xl p-5 max-w-sm w-full mx-4`} data-testid="conversation-prompt-dialog">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-teal/20 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-teal" />
              </div>
              <div>
                <p className={`text-sm font-semibold ${t.text}`}>Email Filed!</p>
                <p className={`text-xs ${t.textMuted}`}>Would you like to file the entire conversation?</p>
              </div>
            </div>
            <p className={`text-xs ${t.textDim} mb-4`}>
              This will file all other emails in this thread to the same SharePoint folder.
            </p>
            <div className="flex gap-2">
              <Button onClick={handleConversationPromptFile}
                className="flex-1 bg-teal hover:bg-teal text-white text-xs h-9"
                disabled={filingInProgress}
                data-testid="conversation-prompt-yes-btn">
                {filingInProgress ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <MessageSquare className="w-3.5 h-3.5 mr-1.5" />}
                File Conversation
              </Button>
              <Button variant="outline" onClick={() => setConversationPrompt(null)}
                className={`flex-1 text-xs h-9 ${t.border} ${t.textSec}`}
                data-testid="conversation-prompt-no-btn">
                Just This Email
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* ToDo Task Creator Dialog */}
      {showTodoDialog && (
        <TodoDialog
          email={selectedEmail}
          bonsaiApi={bonsaiApi}
          getHeaders={getHeaders}
          t={t}
          todoLists={todoLists}
          setTodoLists={setTodoLists}
          todoCreating={todoCreating}
          setTodoCreating={setTodoCreating}
          onClose={() => { setShowTodoDialog(false); setPendingTodoItem(null); }}
          initialDescription={pendingTodoItem?.description}
          initialDueDate={pendingTodoItem?.dueDate}
          initialPriority={pendingTodoItem?.priority}
        />
      )}
      {/* Calendar Event Creator Dialog */}
      {showCalendarDialog && (
        <CalendarEventDialog
          email={selectedEmail}
          bonsaiApi={bonsaiApi}
          getHeaders={getHeaders}
          t={t}
          onClose={() => { setShowCalendarDialog(false); setPendingCalEvent(null); }}
          initialDescription={pendingCalEvent?.description}
          initialDate={pendingCalEvent?.dueDate}
          initialStartTime={pendingCalEvent?.startTime}
          initialEndTime={pendingCalEvent?.endTime}
        />
      )}
    </div>
  );
}
